/* eslint-disable */
import { chartAxisTicks } from "../utils/chartAxisTicks";
import CsvExportButton from "../components/CsvExportButton";
import * as d3 from "d3";
import Papa from "papaparse";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SteamVideoPreview from "../components/SteamVideoPreview";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DateSlider from "../components/DateSlider";
import TextInputField from "../components/Fields/TextInputField";
import PriceSlider from "../components/PriceSlider";
import SavedViewsManager from "../components/SavedViewsManager";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import VirtualizedGameGrid from "../components/VirtualizedGameGrid";
import { useGlobalData } from "../contexts/DataContext";
import "./mindSHARE/styles/mindshare.css";


// ---- Steam price tiers (covers all games, no gaps) ----
const PRICE_TIERS = [
  { id: "free",    label: "Free",          min: 0.0,  max: 0.0 },
  { id: "under5",  label: "Under $5",      min: 0.01, max: 4.99 },
  { id: "5_9",     label: "$5 – $9.99",    min: 5.0,  max: 9.99 },
  { id: "10_14",   label: "$10 – $14.99",  min: 10.0, max: 14.99 },
  { id: "15_24",   label: "$15 – $24.99",  min: 15.0, max: 24.99 },
  { id: "25_39",   label: "$25 – $39.99",  min: 25.0, max: 39.99 },
  { id: "40_59",   label: "$40 – $59.99",  min: 40.0, max: 59.99 },
  { id: "60_plus", label: "$60+",          min: 60.0, max: 999.99 },
];

const PRICE_TIER_MAP = PRICE_TIERS.reduce((acc, t) => {
  acc[t.id] = t;
  return acc;
}, {});

// Interpret Steam metadata full_price + f2p flag
function derivePrice(meta) {
  const raw = meta?.full_price;
  const f2pFlag = meta?.f2p;

  // f2p can be TRUE/FALSE, true/false, 1/0, etc.
  const isF2P =
    String(f2pFlag).toLowerCase() === "true" ||
    f2pFlag === 1 ||
    f2pFlag === "1";

  const n = Number(raw);

  if (isF2P) {
    // True free-to-play
    return { priceValue: 0, isF2P: true };
  }

  if (Number.isFinite(n) && n > 0) {
    // Normal paid game
    return { priceValue: n, isF2P: false };
  }

  // No usable price (0 with f2p = FALSE, or junk)
  return { priceValue: null, isF2P: false };
}

// --- Steam-only packed bubble skeleton (Market Mind) ---
const SteamBubbleSkeleton = ({ width = 800, height = 800, count = 220 }) => {
  // Make sure we get a square area so it looks like a disk
  const size = Math.max(200, Math.min(width, height));
  const SAFE_INSET = size * 0.06;
  const S = size - SAFE_INSET * 2;
  const offsetX = (width - S) / 2;
  const offsetY = (height - S) / 2;

  // Build nodes once for this width/height/count
  const nodes = useMemo(() => {
    // 1) Generate “weights” with a wide distribution so radii vary a lot
    const children = d3.range(count).map(() => {
      const r = Math.random();
      let exp;

      // a few giants in the middle
      if (r < 0.04) {
        exp = 8 + Math.random() * 2; // biggest
      }
      // some big mid-tier bubbles
      else if (r < 0.18) {
        exp = 5 + Math.random() * 2;
      }
      // chunky medium group
      else if (r < 0.45) {
        exp = 3 + Math.random() * 2;
      }
      // lots of small ones
      else {
        exp = 0 + Math.random() * 2;
      }

      return { value: Math.pow(1.5, exp) };
    });

    const root = d3.hierarchy({ children }).sum((d) => d.value);
    d3.pack().size([S, S]).padding(2.5)(root);

    // 2) Turn leaves into renderable nodes with per-circle animation
    return root.leaves().map((leaf, i) => {
      // tiny random radius jitter so the disk edge isn’t perfectly smooth
      const jitter = 0.92 + Math.random() * 0.22;
      const cx = leaf.x + offsetX;
      const cy = leaf.y + offsetY;

      // random pulse timing per bubble
      const delay = Math.random() * 1.8;               // 0–1.8s
      const duration = 1.2 + Math.random() * 0.9;      // 1.2–2.1s
      const opacity = 0.30 + Math.random() * 0.28;     // 0.30–0.58

      return {
        id: i,
        cx,
        cy,
        r: leaf.r * jitter,
        opacity,
        animation: `ms-skeleton-pulse ${duration.toFixed(
          2
        )}s ease-in-out ${delay.toFixed(2)}s infinite`,
      };
    });
  }, [S, offsetX, offsetY, count]);

  return (
    <svg
      className="ms-bubble-skeleton"
      width={width}
      height={height}
      aria-label="Loading"
    >
      {nodes.map((n) => (
        <circle
          key={n.id}
          cx={n.cx}
          cy={n.cy}
          r={n.r}
          fill="#1d2730"
          opacity={n.opacity}
          style={{
            // per-bubble pulse: same keyframes, different timing
            animation: n.animation,
            transformOrigin: `${n.cx}px ${n.cy}px`,
          }}
        />
      ))}
    </svg>
  );
};


/* ============================
   .csv.gz helpers for Followers
   ============================ */
const rIC =
  typeof window !== "undefined" && "requestIdleCallback" in window
    ? window.requestIdleCallback
    : (cb) => setTimeout(() => cb({ timeRemaining: () => 17 }), 0);

const LATEST_FOLLOWERS_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_followers.csv.gz";
const FOLLOWERS_GROWTH_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/followers_growth.csv.gz";
const LATEST_CCU_URL =
  "https://steam-scrape-production.s3.us-east-1.amazonaws.com/latest_ccu.csv.gz";

/* -------- CCU history backend (same as GameDetail) -------- */
import { API_BASE } from "../config/api";
const DEFAULT_BASES = [API_BASE];

async function fetchJSON(pathOrUrl) {
  const isFull = /^https?:\/\//i.test(pathOrUrl);
  const makeUrl = (base) =>
    isFull
      ? pathOrUrl
      : `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;

  let lastErr = null;
  for (const base of DEFAULT_BASES) {
    try {
      const url = makeUrl(base);
      const r = await fetch(url, { cache: "no-store" });
      if (r.ok) return await r.json();
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) console.warn("fetchJSON fallbacks exhausted:", lastErr);
  return null;
}

// North America prime time:
// 11:00 PM UTC  →  3:00 PM PT
// 05:00 AM UTC* →  9:00 PM PT (*next UTC day, same local day)
// i.e. 23:00 → 05:00 UTC, wraps midnight
const NA_UTC_START_HOUR = 24;
const NA_UTC_END_HOUR   = 6;   // wrap to next day
const NA_BAND_FILL      = "rgba(63, 169, 245, 0.16)"; // blue

// APAC prime time:
// 10:00 AM UTC →  2:00 AM PT
// 04:00 PM UTC →  8:00 AM PT
// i.e. 10:00 → 16:00 UTC
const ASIA_UTC_START_HOUR = 10;
const ASIA_UTC_END_HOUR   = 16;
const ASIA_BAND_FILL      = "rgba(255, 235, 59, 0.1)"; // yellow

// Europe prime time:
// 5:00 PM UTC →  9:00 AM PT
// 11:00 PM UTC →  3:00 PM PT
// i.e. 11:00 → 17:00 UTC
const EU_UTC_START_HOUR = 17;
const EU_UTC_END_HOUR   = 23;
const EU_BAND_FILL      = "rgba(76, 175, 80, 0.14)";   // (currently green)

const fmtDate = (dt) =>
  new Date(dt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
const fmtDateTimeUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);

  // Example: 12/13 02:01AM (UTC)
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
};

const fmtDateShortUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);

  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    timeZone: "UTC",
  });
};

// ✅ Parse MySQL DATETIME (no tz) as UTC so daily bucketing is stable
function parseMysqlDatetimeAsUTC(dt) {
  if (!dt) return null;
  let raw = String(dt).trim();

  // If it already has timezone info, respect it
  const hasTZ = /[zZ]$|[+\-]\d\d:?\d\d$/.test(raw);

  // "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DDTHH:MM:SS"
  if (!raw.includes("T")) raw = raw.replace(" ", "T");

  const d = hasTZ ? new Date(raw) : new Date(raw + "Z"); // force UTC
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ===== live + daily CCU helpers (copied from GameDetail) ===== */
const dayKeyUTC = (dt) => {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear(),
    m = d.getUTCMonth(),
    dd = d.getUTCDate();
  return new Date(Date.UTC(y, m, dd)).toISOString().slice(0, 10);
};
const startOfDayUTCISO = (dayStr) => `${dayStr}T00:00:00.000Z`;

function normalizeArchive(rows) {
  return (rows || [])
    .map((r) => ({
      dt: startOfDayUTCISO(dayKeyUTC(r.dt)),
      ccu: Number(r.ccu) || 0,
    }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}
function dailyFromLiveSnapshots(rows) {
  const byDay = new Map();
  for (const r of rows || []) {
    const k = dayKeyUTC(r.dt);
    const v = Number(r.ccu);
    if (!k || !Number.isFinite(v) || v < 0) continue;
    const prev = byDay.get(k);
    if (prev === undefined || v > prev) byDay.set(k, v);
  }
  return Array.from(byDay.entries())
    .map(([day, max]) => ({ dt: startOfDayUTCISO(day), ccu: max }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}

function mergeDailyWithLiveDaily(dailySeries, liveRows) {
  const base = Array.isArray(dailySeries) ? [...dailySeries] : [];
  const liveDaily = dailyFromLiveSnapshots(liveRows);

  const byDt = new Map(base.map((p) => [p.dt, { ...p }]));
  for (const p of liveDaily) {
    byDt.set(p.dt, { ...p }); // overwrite/add (includes partial UTC day)
  }

  return Array.from(byDt.values()).sort(
    (a, b) => new Date(a.dt) - new Date(b.dt)
  );
}

function forwardFill(series) {
  let last = null;
  for (let i = 0; i < series.length; i++) {
    const v = Number(series[i].ccu);
    if (Number.isFinite(v) && v > 0) {
      last = v;
    } else if (last != null) {
      series[i].ccu = last;
    }
  }
  return series;
}

function normalizeLive(rows) {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const floor = now - sevenDaysMs;

  const out = [];
  for (const r of rows || []) {
    // Always interpret dt as UTC if it has no explicit timezone
let raw = String(r.dt).trim();

// If it already has a timezone (Z or +hh:mm / -hh:mm), just use it
const hasTZ = /[zZ]$|[+\-]\d\d:?\d\d$/.test(raw);

// Normalize "YYYY-MM-DD HH:MM:SS" → "YYYY-MM-DDTHH:MM:SS"
if (!raw.includes("T")) {
  raw = raw.replace(" ", "T");
}

let d;
if (hasTZ) {
  d = new Date(raw);
} else {
  // Treat as UTC by appending Z
  d = new Date(raw + "Z");
}

const t = d.getTime();
if (!Number.isFinite(t) || t < floor) continue;


    let v = Number(r.ccu);
    if (!(Number.isFinite(v) && v > 0)) v = NaN;

    out.push({ dt: d.toISOString(), ccu: v });
  }
  out.sort((a, b) => new Date(a.dt) - new Date(b.dt));
  return forwardFill(out);
}

async function loadLiveSeries(appId, hours = 24 * 7) {
  const live = await fetchJSON(`/live_ccu/${appId}?hours=${hours}`);
  if (Array.isArray(live) && live.length) return normalizeLive(live);
  return [];
}

async function loadDailySeries(appId) {
  const daily = await fetchJSON(`/ccu_daily/${appId}`);
  let base = Array.isArray(daily) && daily.length ? normalizeArchive(daily) : null;

  if (!base) {
    const arch = await fetchJSON(`/ccu_history/${appId}`);
    if (Array.isArray(arch) && arch.length) base = normalizeArchive(arch);
  }

  // ✅ Pull live snapshots and derive “daily max so far” including partial UTC day
  const live = await fetchJSON(`/live_ccu/${appId}?hours=${24 * 10}`); // 10 days is safe + cheap
  const liveNorm = Array.isArray(live) && live.length ? normalizeLive(live) : [];

  if (!base) {
    // If we have no archive at all, at least return live-derived daily
    return dailyFromLiveSnapshots(liveNorm);
  }

  // ✅ Merge: ensures today’s UTC day exists in daily series
  return mergeDailyWithLiveDaily(base, liveNorm);
}

/* ===== Followers RAW series (all points, sorted by timestamp, UTC) ===== */
async function loadFollowersSeries(appId, days = 365) {
  const rows = await fetchJSON(`/followers_history/${appId}?days=${days}`);
  if (!Array.isArray(rows) || !rows.length) return [];

  const out = [];

  for (const r of rows) {
    if (!r?.dt) continue;

    const d = parseMysqlDatetimeAsUTC(r.dt);
    if (!d) continue;

    const val = Number(r.followers ?? r.Followers);
    if (!Number.isFinite(val)) continue;

    // keep raw timestamp (UTC ISO)
    out.push({
      dt: d.toISOString(),
      ccu: val, // chart uses ccu key, but it's followers
    });
  }

  // sort by timestamp ascending
  out.sort((a, b) => new Date(a.dt) - new Date(b.dt));
  return out;
}

/* Live/Daily rows → chart points (same as GameDetail) */
function toChartRows(game, mode, viewByDaysSinceLaunch) {
  if (!game?.metadata) return [];
  if (mode === "live") {
    if (!Array.isArray(game.live) || game.live.length === 0) return [];
    return game.live.map((r) => ({ x: r.dt, [game.metadata.app_id]: r.ccu }));
  }
  if (!Array.isArray(game.daily) || game.daily.length === 0) return [];
  const release = new Date(game.metadata.release_date);
  const relValid = !Number.isNaN(release.getTime());
  return game.daily.map((r) => {
    const d = new Date(r.dt);
    const key =
      viewByDaysSinceLaunch && relValid
        ? Math.max(0, Math.floor((d - release) / (1000 * 60 * 60 * 24)))
        : r.dt; // ✅ keep UTC ISO, e.g. "2025-12-13T00:00:00.000Z"
    return { x: key, [game.metadata.app_id]: r.ccu };
  });
}

// Decide average daily playtime per user (hours) based on
// 1) a global baseline and
// 2) CCU shape (spikiness)
// 3) Days since release
// Decide average daily playtime per user (hours) based on
// 1) a global baseline
// 2) CCU shape (spikiness)
// 3) Days since release
// 4) Uncertainty band width (spread) that varies by game
function getDynamicSessionHoursForGame(game) {
  // --- 0) Baseline for Steam (same as before) ---
  let low = 3.0;   // conservative
  let high = 3.5;  // aggressive

  const live = Array.isArray(game?.live) ? game.live : [];

  // We'll reuse these for uncertainty later
  let ratio = null;      // max / mean spikiness
  let daysSince = null;  // days since release

  // --- 1) Shape / spikiness from last 24h of live CCU ---
  if (live.length >= 8) {
    const ccus = live
      .map((r) => Number(r.ccu) || 0)
      .filter((v) => v > 0);

    if (ccus.length >= 8) {
      const sum = ccus.reduce((a, b) => a + b, 0);
      const mean = sum / ccus.length;
      const max = Math.max(...ccus);

      if (mean > 0 && max > 0) {
        ratio = max / mean; // 1.0 = flat, >1 = peaky

        // Map ratio into a factor for the *center*:
        // - very flat (ratio <= 1.3)  → +10% hours
        // - very spiky (ratio >= 1.9) → -20% hours
        // - linear in between
        let shapeFactor;
        if (ratio <= 1.3) {
          shapeFactor = 1.10;
        } else if (ratio >= 1.9) {
          shapeFactor = 0.80;
        } else {
          // interpolate between 1.10 and 0.80 as ratio goes 1.3 → 1.9
          const t = (ratio - 1.3) / (1.9 - 1.3);
          shapeFactor = 1.10 + t * (0.80 - 1.10);
        }

        low *= shapeFactor;
        high *= shapeFactor;
      }
    }
  }

  // --- 2) Days since release (launch vs long tail) ---
  const rdStr = game?.metadata?.release_date;
  if (rdStr) {
    const rd = new Date(rdStr);
    const now = new Date();
    if (!Number.isNaN(rd.getTime())) {
      daysSince = (now - rd) / (1000 * 60 * 60 * 24);

      // First 30 days: people binge more → longer sessions
      if (daysSince >= 0 && daysSince <= 30) {
        low *= 1.10;   // +10% hours
        high *= 1.10;
      }

      // Very long tail (1+ years): more drop-in behavior → slightly shorter
      if (daysSince > 365) {
        low *= 0.90;   // -10% hours
        high *= 0.90;
      }
    }
  }

  // --- 3) Convert to center + spread, then vary spread by uncertainty ---
  let center = (low + high) / 2;
  let spread = (high - low) / 2; // base half-width (~0.25 scaled by factors)

  // Bounds for how wide the band can get
  const MIN_SPREAD = 0.20;
  const MAX_SPREAD = 0.80;

  // 3a) Spikiness: peakier games → wider uncertainty band
  if (ratio != null) {
    // Flat (≤1.3) → narrow band; spiky (≥1.9) → wide band
    if (ratio <= 1.3) {
      spread *= 0.8; // tighten by 20%
    } else if (ratio >= 1.9) {
      spread *= 1.8; // almost 2× wider
    } else {
      const t = (ratio - 1.3) / (1.9 - 1.3);
      const factor = 0.8 + t * (1.8 - 0.8); // 0.8 → 1.8
      spread *= factor;
    }
  }

  // 3b) Recency: launch window → more uncertainty; long tail → less
  if (daysSince != null) {
    if (daysSince >= 0 && daysSince <= 30) {
      // first month: widen band a bit more
      spread *= 1.25;
    } else if (daysSince > 365) {
      // long tail: slightly narrower
      spread *= 0.9;
    }
  }

  // Clamp spread to sane bounds, then recompute low/high around center
  spread = Math.max(MIN_SPREAD, Math.min(spread, MAX_SPREAD));

  low = center - spread;
  high = center + spread;

  // --- 4) Final safety clamps on absolute hours ---
  low = Math.max(1.5, Math.min(low, 6.0));
  high = Math.max(low, Math.min(high, 7.0));

  return { lowHours: low, highHours: high };
}

// Estimate Daily Users from live CCU snapshots over the last 24h
function estimateDailyUsersFromLive(
  liveRows,
  hoursLow = 2.25,  // lower bound of average hours per user
  hoursHigh = 2.5   // upper bound of average hours per user
) {
  if (!Array.isArray(liveRows) || liveRows.length < 2) return null;

  const MS_PER_HOUR = 60 * 60 * 1000;
  const WINDOW_HOURS = 24;
  const WINDOW_MS = WINDOW_HOURS * MS_PER_HOUR;

  // Ensure we’re sorted by time
  const sorted = [...liveRows]
    .map((r) => {
      const t = new Date(r.dt).getTime();
      return {
        t,
        ccu: Number(r.ccu) || 0,
      };
    })
    .filter((r) => Number.isFinite(r.t))
    .sort((a, b) => a.t - b.t);

  if (sorted.length < 2) return null;

  const maxTs = sorted[sorted.length - 1].t;
  const windowStart = maxTs - WINDOW_MS;

  let totalPlayerHours = 0;

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (!Number.isFinite(prev.t) || !Number.isFinite(curr.t)) continue;
    if (curr.t <= prev.t) continue;

    // overlap of this segment with the [windowStart, maxTs] window
    const segStart = Math.max(prev.t, windowStart);
    const segEnd   = Math.min(curr.t, maxTs);
    const segMs    = segEnd - segStart;
    if (segMs <= 0) continue;

    const avgCCU   = (prev.ccu + curr.ccu) / 2;
    const segHours = segMs / MS_PER_HOUR;
    totalPlayerHours += avgCCU * segHours;
  }

  if (totalPlayerHours <= 0) return null;

  // --- Base (unbiased) estimates from your dynamic hours ---
  const baseLowUsers  = totalPlayerHours / hoursHigh; // fewer users (more hours per user)
  const baseHighUsers = totalPlayerHours / hoursLow;  // more users (fewer hours per user)

  if (!Number.isFinite(baseLowUsers) || !Number.isFinite(baseHighUsers)) {
    return null;
  }

  // If somehow inverted, just collapse to one point
  if (baseHighUsers <= 0 || baseLowUsers <= 0) return null;

  const center = (baseLowUsers + baseHighUsers) / 2;
  const width  = baseHighUsers - baseLowUsers;

  // --- 1) Tighter band ---
  // e.g. 0.6 → 60% of original width (you can tweak 0.6 → 0.5 if you want even tighter)
  const SHRINK = 0.6;

  let half = (width * SHRINK) / 2;
  if (!Number.isFinite(half) || half < 0) half = 0;

  let low  = center - half;
  let high = center + half;

  // --- 2) Bias both ends downward so we under-estimate a bit ---
  // e.g. 0.9 → shift ~10% lower
  const BIAS = 0.9;
  low  *= BIAS;
  high *= BIAS;

  low  = Math.round(low);
  high = Math.round(high);

  if (!Number.isFinite(low) || !Number.isFinite(high) || low <= 0 || high <= 0) {
    return null;
  }

  // Make sure ordering is correct
  const finalLow  = Math.min(low, high);
  const finalHigh = Math.max(low, high);

  return {
    low: finalLow,
    high: finalHigh,
  };
}


// Nicely round the Y-axis max
function roundYAxisMax(rawMax) {
  const max = Number(rawMax);
  if (!Number.isFinite(max)) return rawMax;

  // Keep your current 10% headroom
  const padded = max * 1.1;

  // For small games, keep it precise-ish
  if (padded <= 100) {
    return Math.ceil(padded); // 67.042 -> 68, etc.
  }

  // For anything bigger, round UP to the next hundred
  // e.g., 1,694,621 * 1.1 = 1,864,083.1 -> 1,864,100
  return Math.ceil(padded / 100) * 100;
}

/** Fetch a .csv.gz and return CSV text (native DecompressionStream if available). */
async function fetchGzipCsvText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
  if ("DecompressionStream" in window && res.body) {
    try {
      const ds = new DecompressionStream("gzip");
      const stream = res.body.pipeThrough(ds);
      const reader = stream.getReader();
      const chunks = [];
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      const blob = new Blob(chunks, { type: "text/plain;charset=utf-8" });
      return await blob.text();
    } catch (e) {
      try {
        return await res.text();
      } catch {
        throw e;
      }
    }
  }
  return await res.text();
}

/** Parse CSV text to array of objects (Papa header mode). */
function parseCsvText(csvText) {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      worker: true, 
      complete: ({ data }) => resolve(data),
      error: (err) => reject(err),
    });
  });
}

/** fetch + gunzip (if needed) + parse */
async function loadCsvGz(url) {
  const text = await fetchGzipCsvText(url);
  return await parseCsvText(text);
}

/* ========= NEW date-only helpers (local time) ========= */
function toLocalISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function safeDate(dLike) {
  if (!dLike) return null;
  const d = new Date(dLike);
  return Number.isNaN(d.getTime()) ? null : d;
}
// "Contains" search, but only starting at a word boundary — plain .includes()
// lets "resident" match inside "president", "evil" match inside "medieval", etc.
function nameMatchesSearch(name, term) {
  const q = (term || "").trim().toLowerCase();
  if (!q) return true;
  const n = (name || "").toLowerCase();
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z0-9])${escaped}`).test(n);
}
function withinLastNDays(dateLike, n = 7) {
  const d = safeDate(dateLike);
  if (!d) return false;
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - n);
  const dISO = toLocalISODate(d);
  const startISO = toLocalISODate(start);
  const endISO = toLocalISODate(today);
  return dISO >= startISO && dISO <= endISO;
}
function bucketFirstLastPerUtcDay(rawRows) {
  // rawRows is already sorted ascending by dt in loadFollowersSeries()
  const byDay = new Map(); // day -> { firstX, firstV, lastX, lastV }

  for (const r of rawRows || []) {
    const day = dayKeyUTC(r.dt);
    const v = Number(r.ccu);
    if (!day || !Number.isFinite(v)) continue;

    const prev = byDay.get(day);
    if (!prev) {
      byDay.set(day, { firstX: r.dt, firstV: v, lastX: r.dt, lastV: v });
    } else {
      // overwrite as we move forward -> last-of-day
      prev.lastX = r.dt;
      prev.lastV = v;
    }
  }

  // sorted by day
  const days = Array.from(byDay.keys()).sort();
  return { byDay, days };
}

/* ================================================ */

function releasedWithin7Days(releaseDate) {
  // NEW: delegate to local date-only window
  return withinLastNDays(releaseDate, 7);
}

/** =========================================================
 *  Effervescent (Login-style) bubbles for Steam games (CCU)
 *  ========================================================= */
function EffervescentBubbles({ games, onHoverChange }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const bubblesRef = useRef([]);
  const pausedRef = useRef(false);

  // Round-robin source
  const sorted = useMemo(
    () => [...games].sort((a, b) => (b.ccu || 0) - (a.ccu || 0)),
    [games]
  );
  const idxRef = useRef(0);
  const nextGame = () => {
    if (!sorted.length) return null;
    const g = sorted[idxRef.current];
    idxRef.current = (idxRef.current + 1) % sorted.length;
    return g;
  };

  // Spawn accumulator
  const spawnRateRef = useRef(48);
  const spawnAccRef = useRef(0);
  const warmUntilRef = useRef(0);

  // Helpers
  const lerp = (a, b, t) => a + (b - a) * t;
  const hexToRgb = (h) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
    return m
      ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
      : { r: 255, g: 255, b: 255 };
  };
  const piece = (colors, t) => {
    const n = colors.length - 1;
    const x = Math.max(0, Math.min(1, t)) * n;
    const i = Math.floor(x);
    const f = x - i;
    const c1 = hexToRgb(colors[i]);
    const c2 = hexToRgb(colors[Math.min(n, i + 1)]);
    const r = Math.round(lerp(c1.r, c2.r, f));
    const g = Math.round(lerp(c1.g, c2.g, f));
    const b = Math.round(lerp(c1.b, c2.b, f));
    return `rgb(${r},${g},${b})`;
  };
  const mixWithWhite = (rgbStr, amt = 0.25) => {
    const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgbStr);
    if (!m) return rgbStr;
    const r = Math.round(lerp(parseInt(m[1]), 255, amt));
    const g = Math.round(lerp(parseInt(m[2]), 255, amt));
    const b = Math.round(lerp(parseInt(m[3]), 255, amt));
    return `rgb(${r},${g},${b})`;
  };
  const toRgba = (rgbStr, a) => {
    const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgbStr);
    if (!m) return `rgba(255,255,255,${a})`;
    return `rgba(${m[1]},${m[2]},${m[3]},${a})`;
  };

  const ccMax = Math.max(1000, ...games.map((g) => g.ccu || 0));
  const colorForCCU = (ccu) => {
    const v = Math.max(0, ccu || 0);
    if (v < 100) return piece(["#ff3b2f", "#ff6a4f"], v / 100);
    if (v < 1000) return piece(["#fe6d4e", "#ffc28b"], (v - 100) / 900);
    const t = Math.log10(v) - Math.log10(1000);
    const d = Math.max(1e-6, Math.log10(ccMax) - Math.log10(1000));
    return piece(
      ["#819898", "#4c7d8e", "#174d7c", "#2794c3"],
      Math.min(1, t / d)
    );
  };

  const getRadius = (ccu, w) => {
    const maxR = Math.max(90, Math.min(170, (w || window.innerWidth) * 0.07));
    const minR = Math.max(11, Math.min(24, (w || window.innerWidth) * 0.012));
    const t = Math.sqrt((ccu || 0) / ccMax);
    return lerp(minR, maxR, t);
  };

  const hitTest = (x, y) => {
    const arr = bubblesRef.current;
    for (let i = arr.length - 1; i >= 0; i--) {
      const b = arr[i];
      const dx = x - b.x;
      const dy = y - b.y;
      if (dx * dx + dy * dy <= b.r * b.r) return b;
    }
    return null;
  };

  // Hover control
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const hit = hitTest(x, y);
      pausedRef.current = !!hit;
      onHoverChange?.(
        hit ? { x: hit.x, y: hit.y, r: hit.r, data: hit.data } : null
      );
    };
    const handleLeave = () => {
      pausedRef.current = false;
      onHoverChange?.(null);
    };

    canvas.addEventListener("mousemove", handleMove);
    canvas.addEventListener("mouseleave", handleLeave);
    return () => {
      canvas.removeEventListener("mousemove", handleMove);
      canvas.removeEventListener("mouseleave", handleLeave);
    };
  }, [onHoverChange]);

  // Draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true });

    const fit = () => {
      const parent = canvas.parentElement;
      const w = parent?.clientWidth || window.innerWidth;
      const h = parent?.clientHeight || window.innerHeight;
      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(canvas.parentElement || document.body);

    const rand = (a, b) => a + Math.random() * (b - a);
    const maxBubbles = 240;

    const spawnOne = (opts = {}) => {
      if (bubblesRef.current.length >= maxBubbles) return;
      const g = nextGame();
      if (!g) return;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const r = getRadius(g.ccu, w);
      const seeded = !!opts.seeded;

      const x = rand(r, Math.max(r, w - r));
      let y;
      if (seeded) {
        const bandTop = h * 0.85;
        const bandBottom = Math.min(h - r * 0.4, h * 0.96);
        y = rand(bandTop, bandBottom);
      } else {
        y = h + r + rand(0, h * 0.18);
      }

      const base = ((cc) => {
        const refR = getRadius(cc, w);
        return lerp(30, 88, r / Math.max(1, refR));
      })(Math.max(...games.map((g) => g.ccu || 0), 1));

      const speed = base * lerp(0.96, 1.08, Math.random());
      const vx = rand(-10, 10);
      const stopY = rand(h * 0.06, h * 0.12);

      bubblesRef.current.push({
        x,
        y,
        r,
        vx,
        vy: -speed,
        life: seeded ? rand(0.12, 0.6) : 0,
        lifeMax: 60,
        color: colorForCCU(g.ccu),
        name: g.name || "",
        data: g,
        wobblePhase: rand(0, Math.PI * 2),
        wobbleAmp: rand(0.25, 1.0),
        stopY,
      });
    };

    const seedInitial = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const area = w * h || 1;
      const target = Math.min(
        maxBubbles * 0.4,
        Math.max(20, Math.floor(area / 42000))
      );
      for (let i = 0; i < target; i++) spawnOne({ seeded: true });
    };

    warmUntilRef.current = performance.now() + 1200;
    seedInitial();

    let last = performance.now();
    const tick = () => {
      const t = performance.now();
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      ctx.clearRect(0, 0, w, h);

      if (!pausedRef.current) {
        const warmBoost = t < warmUntilRef.current ? 2.0 : 1;
        spawnAccRef.current += dt * spawnRateRef.current * warmBoost;
        while (spawnAccRef.current >= 1) {
          spawnOne();
          spawnAccRef.current -= 1;
        }
      }

      const arr = bubblesRef.current;
      arr.sort((a, b) => a.r - b.r);

      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.imageSmoothingEnabled = true;

      for (let i = arr.length - 1; i >= 0; i--) {
        const b = arr[i];

        if (!pausedRef.current) {
          b.life += dt;
          const wob = Math.sin(b.wobblePhase + b.life * 1.6) * b.wobbleAmp;
          b.x += b.vx * dt + wob;
          b.y += b.vy * dt;

          if (b.x < b.r) {
            b.x = b.r;
            b.vx = Math.abs(b.vx) * 0.9;
          }
          if (b.x > w - b.r) {
            b.x = w - b.r;
            b.vx = -Math.abs(b.vx) * 0.9;
          }
        }

        const atTopBand = b.y - b.r <= b.stopY;
        const aIn = Math.min(1, b.life / 0.5);
        const aOutByLife = Math.min(1, (b.lifeMax - b.life) / 2.8);
        const aOutByTop = atTopBand
          ? Math.max(0, (b.y - b.stopY) / (h * 0.06))
          : 1;
        const alpha =
          Math.max(
            0,
            Math.min(1, Math.min(aIn, Math.max(aOutByLife, aOutByTop)))
          ) * 0.9;

        if (b.y + b.r < -20 || (atTopBand && alpha < 0.06)) {
          arr.splice(i, 1);
          continue;
        }

        const r = b.r;

        const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, r);
        grad.addColorStop(0.0, toRgba(b.color, 0.12));
        grad.addColorStop(0.82, toRgba(b.color, 0.4));
        grad.addColorStop(0.96, toRgba(mixWithWhite(b.color, 0.15), 0.85));
        grad.addColorStop(1.0, "rgba(0,0,0,0)");
        ctx.globalAlpha = alpha;
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = alpha * 0.65;
        ctx.lineWidth = Math.max(1, r * 0.035);
        ctx.strokeStyle = toRgba(mixWithWhite(b.color, 0.2), 0.6);
        ctx.beginPath();
        ctx.arc(b.x, b.y, r * 0.988, 0, Math.PI * 2);
        ctx.stroke();

        let label = b.name || "";
        const maxLen = r > 64 ? 28 : r > 44 ? 22 : 12;
        if (label.length > maxLen) label = label.slice(0, maxLen) + "…";

        const fontSize = Math.max(6, Math.min(12, r * 0.34));

        ctx.save();
        ctx.globalAlpha = 1;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;
        ctx.font = `500 ${fontSize}px "Segoe UI", system-ui, -apple-system, sans-serif`;
        ctx.strokeStyle = "rgba(0,0,0,0.50)";
        ctx.lineWidth = Math.max(0.75, fontSize * 0.09);
        ctx.strokeText(label, b.x, b.y);
        ctx.fillStyle = "#fff";
        ctx.fillText(label, b.x, b.y);
        ctx.restore();
      }

      ctx.restore();
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      bubblesRef.current = [];
      onHoverChange?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted]);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "auto",
          background:
            "radial-gradient(1200px 600px at 50% -10%, rgba(40,144,195,0.10), transparent 55%)",
        }}
        aria-hidden
      />
    </div>
  );
}

/** Utility: place a 320px popover near (x,y) within a container rect. */
function computePopoverPos({
  x,
  y,
  r,
  containerWidth,
  containerHeight,
  preferBelow,
}) {
  const CARD_W = 320;
  const CARD_H = 360; // approximate card height (image + text)
  const GAP = 16;

  // --- Horizontal placement (unchanged) ---
  let preferRight = x < containerWidth * 0.5;
  let left = preferRight ? x + r + GAP : x - r - CARD_W - GAP;

  // If initial side would push us off-screen, flip
  if (left < 8) {
    preferRight = true;
    left = x + r + GAP;
  } else if (left + CARD_W > containerWidth - 8) {
    preferRight = false;
    left = x - r - CARD_W - GAP;
  }

  // Final clamp within viewport
  left = Math.max(8, Math.min(left, containerWidth - CARD_W - 8));

  // --- Vertical placement (tiered by height) ---
  // Top third  -> card mostly below bubble
  // Middle     -> card centered on bubble
  // Bottom third -> card mostly above bubble
  const minTop = 8;
  const maxTop = containerHeight - CARD_H - 8;
  const yFrac = containerHeight > 0 ? y / containerHeight : 0.5;

  let rawTop;
  if (yFrac < 0.33) {
    // upper third: 3/4 of card below center
    rawTop = y - CARD_H * 0.25;
  } else if (yFrac < 0.66) {
    // middle third: center card on bubble
    rawTop = y - CARD_H * 0.5;
  } else {
    // bottom third: 3/4 of card above center (keeps it from sitting on bottom edge)
    rawTop = y - CARD_H * 0.9;
  }

  const top = Math.max(minTop, Math.min(rawTop, maxTop));

  return { left, top };
}



/* ===== GoldGlow ring (outer) ===== */
function GoldGlow({ active, subtle = false }) {
  if (!active) return null;

  // Strong vs subtle glow settings
  const borderColor = subtle
    ? "rgba(255,215,0,0.7)"
    : "rgba(255,215,0,0.95)";

  const boxShadow = subtle
    ? "0 0 0 1px rgba(255,215,0,0.7), 0 0 16px 3px rgba(255,200,0,0.30)"
    : "0 0 0 1px rgba(255,215,0,0.9), 0 0 28px 6px rgba(255,200,0,0.45)";

  const background = subtle
    ? "radial-gradient(120% 120% at 50% 50%, rgba(255,215,0,0.08), rgba(255,215,0,0) 70%)"
    : "radial-gradient(130% 130% at 50% 50%, rgba(255,215,0,0.14), rgba(255,215,0,0) 70%)";

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "14px",           // matches the tilt card
        pointerEvents: "none",
        zIndex: 5,
        border: `2px solid ${borderColor}`,
        boxShadow,
        background,
        mixBlendMode: "screen",
      }}
    />
  );
}

function RedMissingGlow({ active }) {
  const ref = useRef(null);
  const phaseRef = useRef(Math.random() * Math.PI * 2); // desync per card
  const rafRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;

    const BORDER_W = 6;
    const OUTER_GAP = 2;

    // Base style (set once)
    el.style.position = "absolute";
    el.style.inset = `${-OUTER_GAP}px`;
    el.style.borderRadius = `${14 + OUTER_GAP}px`;
    el.style.pointerEvents = "none";
    el.style.zIndex = "50";
    el.style.border = `${BORDER_W}px solid rgba(255, 60, 60, 0.95)`;
    el.style.background =
      "radial-gradient(120% 120% at 50% 50%, rgba(255,60,60,0.08), rgba(255,60,60,0) 72%)";
    el.style.transformOrigin = "center center";
    el.style.willChange = "transform, box-shadow";
    el.style.mixBlendMode = "normal";

    const period = 1.8; // seconds per pulse
    const start = performance.now();
    const phase = phaseRef.current;

    const tick = (now) => {
      const t = (now - start) / 1000;

      // Smooth 0..1..0 pulse (cosine)
      const wave = 0.5 - 0.5 * Math.cos((t * 2 * Math.PI) / period + phase);

      // Scale: subtle but obvious (tweak 0.04 -> 0.05 if you want louder)
      const scale = 1 + 0.04 * wave;

      // Glow intensity ramps with the wave
      const glowA = 0.25 + 0.45 * wave;       // alpha for red glow
      const glowBlur = 14 + 16 * wave;        // blur radius
      const glowSpread = 4 + 8 * wave;        // spread radius

      el.style.transform = `scale(${scale.toFixed(4)})`;
      el.style.boxShadow = `
        0 0 0 3px rgba(0,0,0,0.85),
        0 0 0 1px rgba(255, 60, 60, 0.95),
        0 0 ${glowBlur.toFixed(0)}px ${glowSpread.toFixed(0)}px rgba(255, 40, 40, ${glowA.toFixed(3)})
      `;

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active]);

  if (!active) return null;

  return <div aria-hidden ref={ref} />;
}


function PlayersHeader({
  steamTotalOnline,
  totalCcu,
  playersShareOfTotal,
  filteredPlayers,
  statNumberStyle,
  showOnline,
  onRefresh,
  refreshReady,
  mgMissingById,
  mgMissingByName,
  excludeAdultPrimary,
  mgMetaLoaded,
  minPlayers,
  chartMode = false,   // 👈 NEW
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}
    >
      {/* LEFT: stats block(s) */}
      {/* In chart mode: only Playing Now + % of Players */}
      {chartMode ? (
        <>
          <div style={{ fontSize: "1rem", color: "#aaa" }}>
            <span style={statNumberStyle}>{totalCcu.toLocaleString()}</span>
            <span style={{ marginLeft: 6 }}>Playing Now</span>
          </div>

          <div style={{ fontSize: "1rem", color: "#aaa" }}>
            <span style={statNumberStyle}>{playersShareOfTotal}%</span>
            <span style={{ marginLeft: 6 }}>of Players</span>
          </div>
        </>
      ) : (
        <>
          {showOnline && (
            <div style={{ fontSize: "1rem", color: "#aaa" }}>
              <span style={statNumberStyle}>
                {steamTotalOnline.toLocaleString()}
              </span>
              <span style={{ marginLeft: 6 }}>Online</span>
            </div>
          )}

          <div style={{ fontSize: "1rem", color: "#aaa" }}>
            <span style={statNumberStyle}>{totalCcu.toLocaleString()}</span>
            <span style={{ marginLeft: 6 }}>Playing Now</span>
          </div>

          <div style={{ fontSize: "1rem", color: "#aaa" }}>
            <span style={statNumberStyle}>{playersShareOfTotal}%</span>
            <span style={{ marginLeft: 6 }}>of Players</span>
          </div>
        </>
      )}

      {/* RIGHT: Active Games (only when NOT chart mode) + refresh button (always) */}
      <div
        style={{
          fontSize: "1rem",
          color: "#aaa",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {!chartMode && (
          <div>
            <span style={statNumberStyle}>
              {filteredPlayers.length.toLocaleString()}
            </span>
            <span style={{ marginLeft: 6 }}>Active Games</span>
          </div>
        )}

        <button
          type="button"
          onClick={onRefresh}
          title="Refresh Steam data"
          style={{
            width: 28,
            height: 28,
            borderRadius: "999px",
            border: "1px solid #555",
            background: "#222",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: refreshReady ? "pointer" : "default",
            padding: 0,
            lineHeight: 1,
            opacity: refreshReady ? 1 : 0,
            pointerEvents: refreshReady ? "auto" : "none",
            transition: "opacity 300ms ease",
          }}
        >
          ↻
        </button>
      </div>
    </div>
  );
}

function FollowersHeader({
  filteredFollowers,
  followersTotal,
  gamesFilter,
  setGamesFilter,
  followersSort,
  setFollowersSort,
  statNumberStyle,
  chartMode = false, 
  mgMissingById,
  mgMissingByName,
  excludeAdultPrimary,
}) {
  const pillBase = {
    border: "none",
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
    transition: "background 0.15s ease",
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        width: "100%",
        flexWrap: "wrap",
      }}
    >
      {/* Left: stats */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
        {chartMode ? (
          // Chart mode: just Total Follows, based on chart selection
          <div style={{ fontSize: "1rem", color: "#aaa" }}>
            <span style={statNumberStyle}>
              {followersTotal.toLocaleString()}
            </span>
            <span style={{ marginLeft: 6 }}>Total Follows</span>
          </div>
        ) : (
          // Normal mode: Games + Total Follows
          <>
            <div style={{ fontSize: "1rem", color: "#aaa" }}>
              <span style={statNumberStyle}>
                {filteredFollowers.length.toLocaleString()}
              </span>
              <span style={{ marginLeft: 6 }}>
                {gamesFilter === "unreleased" ? "Upcoming Games" : "Games"}
              </span>
            </div>
            <div style={{ fontSize: "1rem", color: "#aaa" }}>
              <span style={statNumberStyle}>
                {followersTotal.toLocaleString()}
              </span>
              <span style={{ marginLeft: 6 }}>Total Follows</span>
            </div>
          </>
        )}
      </div>

      {/* Right: filter + sort pills */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {/* All | Unreleased */}
        <div
          role="tablist"
          aria-label="Games filter"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            background: "transparent",
            border: "1px solid #333",
            borderRadius: 999,
            padding: 4,
            minWidth: 260,
          }}
        >
          <button
            role="tab"
            aria-selected={gamesFilter === "all"}
            onClick={() => setGamesFilter("all")}
            style={{
              ...pillBase,
              background: gamesFilter === "all" ? "#3fa9f5" : "transparent",
              color: gamesFilter === "all" ? "#000" : "#ddd",
            }}
          >
            All
          </button>
          <button
            role="tab"
            aria-selected={gamesFilter === "unreleased"}
            onClick={() => setGamesFilter("unreleased")}
            style={{
              ...pillBase,
              background: gamesFilter === "unreleased" ? "#3fa9f5" : "transparent",
              color: gamesFilter === "unreleased" ? "#000" : "#ddd",
            }}
          >
            Unreleased
          </button>
        </div>

        {/* Divider */}
        <span aria-hidden="true" style={{ color: "#444", fontWeight: 700 }}>|</span>

        {/* Sort By: Total Followers | 7 Day Growth */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "#ccc", fontWeight: 700 }}>Sort By:</span>
          <div
            role="tablist"
            aria-label="Sort followers by"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
              background: "transparent",
              border: "1px solid #333",
              borderRadius: 999,
              padding: 4,
            }}
          >
            <button
              role="tab"
              aria-selected={followersSort === "followers"}
              onClick={() => setFollowersSort("followers")}
              style={{
                ...pillBase,
                background: followersSort === "followers" ? "#3fa9f5" : "transparent",
                color: followersSort === "followers" ? "#000" : "#ddd",
                whiteSpace: "nowrap",
              }}
            >
              Total Followers
            </button>
            <button
              role="tab"
              aria-selected={followersSort === "growth_7d"}
              onClick={() => setFollowersSort("growth_7d")}
              style={{
                ...pillBase,
                background: followersSort === "growth_7d" ? "#3fa9f5" : "transparent",
                color: followersSort === "growth_7d" ? "#000" : "#ddd",
                whiteSpace: "nowrap",
              }}
            >
              7 Day Growth
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Steam_debug() {
  const {
    metadata,
    latestCCU,
    loading: globalLoading,
    catalogLoading,
    catalogReady,
    ensureSteamCatalog,
    getAllTags,
    getAllPublishers,
    getAllDevelopers,
    getAllReleaseDates,
    refreshData,
  } = useGlobalData();

const [pendingChartRestore, setPendingChartRestore] = useState(null);

const restoringRef = useRef(false);
const restoreTokenRef = useRef(0);

  /** -----------------------
   *  Players (existing Steam)
   *  ----------------------- */
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameQuery, setGameQuery] = useState("");
  const [tagQuery, setTagQuery] = useState("");
  // Primary Tags (first three tags only)
  const [selectedPrimaryTag, setSelectedPrimaryTag] = useState(null);
  const [primaryTagQuery, setPrimaryTagQuery] = useState("");
  const [displayedGames, setDisplayedGames] = useState([]);
  const [hiddenGames, setHiddenGames] = useState([]);
  const [steamTotalOnline, setSteamTotalOnline] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [loadingPlayers, setLoadingPlayers] = useState(true);

// ============================
// mindGAME metadata (MG) — for "Missing from MG" filters
// ============================
const MG_METADATA_URL = "https://mindgamedataapi.s3.us-east-1.amazonaws.com/metadata.csv";

// independent toggles (can be on together)
const [mgMissingById, setMgMissingById] = useState(false);
const [mgMissingByName, setMgMissingByName] = useState(false);
// Adult-content filter toggle (primary tag only)
const [excludeAdultPrimary, setExcludeAdultPrimary] = useState(false);

// loading state
const [mgMetaLoading, setMgMetaLoading] = useState(false);
const [mgMetaLoaded, setMgMetaLoaded] = useState(false);

// lookup sets
const mgSteamIdSetRef = useRef(new Set());   // Steam_ID values as strings
const mgGameNameSetRef = useRef(new Set());  // Game values lowercased

useEffect(() => {
  let cancelled = false;

  const loadMg = async () => {
    setMgMetaLoading(true);
    try {
      const res = await fetch(`${MG_METADATA_URL}?t=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) throw new Error(`MG metadata fetch failed: ${res.status}`);
      const text = await res.text();

      const rows = await new Promise((resolve, reject) => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
          worker: true,
          complete: ({ data }) => resolve(data || []),
          error: (err) => reject(err),
        });
      });

      if (cancelled) return;

      const idSet = new Set();
      const nameSet = new Set();

      for (const r of rows) {
        // 1) Steam_ID set (string compare against app_id)
        const sid = r?.Steam_ID ?? r?.steam_id ?? r?.steamId;

// Normalize Steam_ID to match your g.app_id format (integer string)
const sidRaw = sid == null ? "" : String(sid).trim();

if (sidRaw) {
  // Handles "1234567", "1234567.0", 1234567, etc.
  const sidInt = parseInt(sidRaw, 10);

  if (Number.isFinite(sidInt) && sidInt > 0) {
    idSet.add(String(sidInt));
  } else {
    // fallback: strip trailing ".0" if parseInt failed for some reason
    const cleaned = sidRaw.replace(/\.0+$/, "");
    if (cleaned) idSet.add(cleaned);
  }
}


        // 2) Game name set (case-insensitive)
        const gname = r?.Game ?? r?.game ?? r?.name;
        if (gname != null && String(gname).trim() !== "") {
          nameSet.add(String(gname).trim().toLowerCase());
        }
      }

      mgSteamIdSetRef.current = idSet;
      mgGameNameSetRef.current = nameSet;

      setMgMetaLoaded(true);
    } catch (e) {
      console.warn("MG metadata load failed:", e);
      // leave mgMetaLoaded=false, but don’t crash the page
      mgSteamIdSetRef.current = new Set();
      mgGameNameSetRef.current = new Set();
    } finally {
      if (!cancelled) setMgMetaLoading(false);
    }
  };

  loadMg();
  return () => {
    cancelled = true;
  };
}, []);


  // stream reset for effervescent
  const [streamNonce, setStreamNonce] = useState(0);

  /** -----------------------
   *  Followers (moved here)
   *  ----------------------- */
  const [followersLoaded, setFollowersLoaded] = useState(false);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followersGames, setFollowersGames] = useState([]);
  const [followersDisplayed, setFollowersDisplayed] = useState([]);
  const [hiddenFollowers, setHiddenFollowers] = useState([]);
  const [followersSort, setFollowersSort] = useState("growth_7d"); // followers | growth_7d
  const [gamesFilter, setGamesFilter] = useState("all");
  const [followersValueMode, setFollowersValueMode] = useState("total"); // "total" | "daily"

  /* ============================
     Player History compare (live+daily)
     ============================ */
  const [chartGames, setChartGames] = useState([]); // [{ metadata, live, daily }]
  const [dataMode, setDataMode] = useState("live"); // "live" | "daily"
  const [viewByDaysSinceLaunch, setViewByDaysSinceLaunch] = useState(false);
  const [showRegionBands, setShowRegionBands] = useState(false);
  const [highlightedSeries, setHighlightedSeries] = useState(null); // app_id or null


  const [winStartIdx, setWinStartIdx] = useState(0);
  const [winEndIdx, setWinEndIdx] = useState(0);
  const hasInitWindow = useRef(false);

  /* =========================
     Restore helpers (AFTER states)
     ========================= */

  const findGameStubForChart = (appId, targetMode) => {
    const idStr = String(appId);
    const list = targetMode === "followers" ? (followersGames || []) : (games || []);
    const hit = (list || []).find((g) => String(g.app_id) === idStr);

    if (hit) {
      return {
        app_id: idStr,
        name: String(hit.name || hit?.metadata?.name || idStr),
        box_art: String(hit.box_art || hit?.metadata?.box_art || ""),
        release_date: String(hit.release_date || hit?.metadata?.release_date || ""),
        full_price: String(hit.full_price || hit?.metadata?.full_price || ""),
      };
    }

    return { app_id: idStr, name: idStr, box_art: "", release_date: "", full_price: "" };
  };

  const loadSeriesForRestore = async (appId, targetMode) => {
    const idStr = String(appId);

    if (targetMode === "followers") {
      const daily = await loadFollowersSeries(idStr, 90);
      return { appId: idStr, live: [], daily };
    }

    const [live, daily] = await Promise.all([
      loadLiveSeries(idStr),
      loadDailySeries(idStr),
    ]);

    return { appId: idStr, live, daily };
  };

// ---- Market Mind precompute cache ----
const marketCacheRef = useRef({ key: "", nodes: [] }); // { key, nodes }
const [precomputedReady, setPrecomputedReady] = useState(false);

  /* =============================
     NEW: Players vs Followers mode
     ============================= */
  const [mode, setMode] = useState("players"); // "players" | "followers"
  useEffect(() => {
    if (mode === "followers") ensureSteamCatalog();
  }, [mode, ensureSteamCatalog]);
  const [isReleasedThisWeek, setIsReleasedThisWeek] = useState(false);
  const [savedViewsKey, setSavedViewsKey] = useState(0); 

  /** Players view-type (unchanged) */
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "market" | "effervescent"

  /** Shared filters */
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);

// Min thresholds (debug)
const [minPlayersRaw, setMinPlayersRaw] = useState("");     // text input
const [minFollowersRaw, setMinFollowersRaw] = useState(""); // text input

const minPlayers = useMemo(() => {
  const digits = String(minPlayersRaw || "").replace(/[^\d]/g, "");
  if (digits.length < 2) return 0;               // ✅ wait for 2+ digits
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
}, [minPlayersRaw]);

const minFollowers = useMemo(() => {
  const digits = String(minFollowersRaw || "").replace(/[^\d]/g, "");
  if (digits.length < 2) return 0;               // ✅ wait for 2+ digits
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
}, [minFollowersRaw]);

  // ---- Publishers UI (lightweight) ----
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [pubQuery, setPubQuery] = useState(""); // user typing into publisher Select

  const [allTags, setAllTags] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);

  const [allDevelopers, setAllDevelopers] = useState([]);
  const [selectedDevelopers, setSelectedDevelopers] = useState(null);
  const [devQuery, setDevQuery] = useState("");

  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [releaseDateRange, setReleaseDateRange] = useState([null, null]);

  // Multi-select price tiers: array of tier ids (start with ALL selected)
  const [selectedPriceRange, setSelectedPriceRange] = useState(
    PRICE_TIERS.map((t) => t.id)
  );

  // ---------- Custom tooltip: sorted by CCU, UTC + PT time, comma formatting ----------
  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // For live mode, label is an ISO datetime (UTC)
    let headerLines = [];
    if (dataMode === "live") {
      const d = new Date(label);
      if (!Number.isNaN(d.getTime())) {
        const utcStr = d.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "UTC",
        });
        const ptStr = d.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "America/Los_Angeles",
        });
        headerLines = [
          `${utcStr} UTC`,
          `${ptStr} PT`,
        ];
      } else {
        headerLines = [String(label)];
      }
    } else {
  // Daily / Days-since-launch
  headerLines = viewByDaysSinceLaunch
  ? [String(label)]
  : [fmtDateShortUTC(label)];

}


    // Sort series by value DESC for that time point
    const sorted = [...payload]
      .filter((p) => p && Number.isFinite(Number(p.value)))
      .sort((a, b) => Number(b.value) - Number(a.value));

    const nf = new Intl.NumberFormat("en-US");

    return (
      <div
        style={{
          background: "#050608",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "6px 10px 8px",
          fontSize: 12,
          color: "#f9fafb",
          minWidth: 180,
        }}
      >
        {/* Time header */}
        {headerLines.map((line, idx) => (
          <div
            key={idx}
            style={{
              fontWeight: idx === 0 ? 700 : 500,
              color: idx === 0 ? "#e5e7eb" : "#9ca3af",
            }}
          >
            {line}
          </div>
        ))}

        <div style={{ marginTop: 6 }}>
          {sorted.map((entry) => {
            const appId = entry.dataKey;
            // Find consistent series color
            const seriesIndex = chartGames.findIndex(
              (g) => String(g.metadata.app_id) === String(appId)
            );
            const color = seriesColor(seriesIndex >= 0 ? seriesIndex : 0);
            const name = entry.name || appId;
            const value = nf.format(Number(entry.value) || 0);

            return (
              <div
                key={appId}
                style={{
                  color,
                  fontWeight: 600,
                  marginBottom: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {name}: <span style={{ color: "#f9fafb" }}>{value}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

 
/** =========================
 * Hover preview like mindSHARE
 * ========================= */
const [preview, setPreview] = useState(null); // { type, value } or null

const norm = (s = "") => s.toLowerCase().trim();
const ADULT_PRIMARY_TAGS = new Set(["sexual content", "nsfw", "nudity"]);
const EXCLUDED_PRIMARY_TAGS = new Set(["utilities"]);

/** Reuse your normalized helpers for pubs/devs */
function matchesPreviewSteam(node, prev) {
  if (!prev || !prev.type || !prev.value) return true;
  const v = norm(prev.value);

  switch (prev.type) {
    case "tag": {
      const tags = Array.isArray(node.tags) ? node.tags.map(norm) : [];
      return tags.includes(v);
    }
        case "primaryTag": {
      const t = Array.isArray(node?.tags) ? node.tags.map(norm).slice(0, 5) : [];
      return t.includes(v);
    }
    case "publisher": {
      const pubs = getPublishersNorm(node); // already lowercased
      return pubs.includes(v);
    }
    case "developer": {
      const devs = getDevelopersNorm(node); // already lowercased
      return devs.includes(v);
    }
    case "game": {
      return node.app_id === String(prev.value) || norm(node.name) === v;
    }
    default:
      return true;
  }
}

  /** -------- Click-to-filter helpers from hover metadata -------- */

  // Price → pick the matching tier (or Free)
  const handleClickPriceFilter = (game) => {
    if (!game) return;

    // Free to Play
    if (game.f2p === true) {
      setSelectedPriceRange(["free"]);
      return;
    }

    const price = game.price_value;
    if (price == null || !Number.isFinite(price)) return;

    const tier = PRICE_TIERS.find(
      (t) => price >= t.min && price <= t.max
    );
    if (!tier) return;

    // Single-tier selection; slider will reflect it
    setSelectedPriceRange([tier.id]);
  };

  // Add a publisher into the multi-select filter
  const handleClickPublisherFilter = (name) => {
    if (!name) return;
    const value = name.trim();
    if (!value) return;

    setSelectedPublisher((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (arr.some((p) => (p?.value || "") === value)) return arr;
      return [...arr, { label: value, value }];
    });
  };

  // Add a developer into the multi-select filter
  const handleClickDeveloperFilter = (name) => {
    if (!name) return;
    const value = name.trim();
    if (!value) return;

    setSelectedDevelopers((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (arr.some((d) => (d?.value || "") === value)) return arr;
      return [...arr, { label: value, value }];
    });
  };

  // Add a tag into the Tags filter
  const handleClickTagFilter = (tag) => {
    if (!tag) return;
    const value = tag.trim();
    if (!value) return;

    setSelectedTag((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];
      if (arr.some((t) => (t?.value || "") === value)) return arr;
      return [...arr, { label: value, value }];
    });
  };

/** Imperatively apply preview by toggling a CSS class on nodes */
const previewRef = useRef(null);
const previewRaf = useRef(0);
const clearTimerRef = useRef(0);


function applyPreview(prev) {
  // memoize latest request
  previewRef.current = prev;

  // if we’re asked to clear, do it with a tiny grace period to avoid flicker
  const wantsClear =
    !prev || !prev.type || !prev.value || (prev.type === "nameContains" && !String(prev.value).trim());

  if (wantsClear) {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      // only clear if nothing new arrived during the grace window
      if (!previewRef.current || !previewRef.current.value) {
        if (bubbleSvgRef.current) {
          d3.select(bubbleSvgRef.current).selectAll(".mm-node").classed("mm-bubble-dim", false);
        }
      }
    }, 120); // small delay prevents “flash to all” when moving between items
    return;
  }

  // we have a real preview request: cancel pending clears and apply immediately
  if (clearTimerRef.current) {
    clearTimeout(clearTimerRef.current);
    clearTimerRef.current = 0;
  }

  if (previewRaf.current) return; // throttle to one per frame
  previewRaf.current = requestAnimationFrame(() => {
    previewRaf.current = 0;
    if (!bubbleSvgRef.current) return;

    const sel = d3.select(bubbleSvgRef.current).selectAll(".mm-node");

    sel.each(function () {
      const appId = this.getAttribute("data-app");
      let data = null;
      if (appId) data = nodeIndexRef.current.get(String(appId));
      const hit = matchesPreviewSteam(data || {}, previewRef.current);
      d3.select(this).classed("mm-bubble-dim", !hit);
    });
  });
}



// identical to Followers' pills
const pillBase = {
  border: "none",
  borderRadius: 999,
  padding: "8px 12px",
  fontWeight: 800,
  cursor: "pointer",
  transition: "background 0.15s ease",
};

  /** 3D tilt handlers (shared) */
  const handleCardMove = (e) => {
    const card = e.currentTarget.querySelector(".mg-tilt");
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = (x / rect.width) * 2 - 1;
    const py = (y / rect.height) * 2 - 1;
    const rotateY = px * 4;
    const rotateX = -py * 4;
    card.style.setProperty("--rx", `${rotateX}deg`);
    card.style.setProperty("--ry", `${rotateY}deg`);
    card.style.setProperty("--sx", `${(x / rect.width) * 100}%`);
    card.style.setProperty("--sy", `${(y / rect.height) * 100}%`);
  };
  const handleCardLeave = (e) => {
    const card = e.currentTarget.querySelector(".mg-tilt");
    if (!card) return;
    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
    card.style.setProperty("--sx", "50%");
    card.style.setProperty("--sy", "50%");
  };

  /** Build lists */
  useEffect(() => {
    if (globalLoading || !metadata.length) return;
    setAllTags(getAllTags());
    setAllPublishers(getAllPublishers());
    setAllDevelopers(getAllDevelopers());
    const r = getAllReleaseDates(); // expected [min, max] Dates (or strings)
    const hardMaxLocal = new Date(2030, 11, 31, 23, 59, 59, 999); // Dec=11
    if (Array.isArray(r) && r.length === 2) {
      const min = r[0] ? new Date(r[0]) : null;
      const max = r[1] ? new Date(r[1]) : null;
      setReleaseDateRange([min, (max && +max > +hardMaxLocal) ? max : hardMaxLocal]);
    } else {
      setReleaseDateRange([null, hardMaxLocal]);
    }

  }, [metadata, globalLoading, getAllTags, getAllPublishers, getAllDevelopers, getAllReleaseDates]);


useEffect(() => {
  const c = pendingChartRestore;
  if (!c) return;

  restoringRef.current = true;
  const myToken = ++restoreTokenRef.current;

  const ids = Array.isArray(c.ids) ? c.ids.map(String).filter(Boolean) : [];
  if (ids.length === 0) {
    setChartGames([]);
    setPendingChartRestore(null);

    if (restoreTokenRef.current === myToken) {
      restoringRef.current = false;
    }
    return;
  }

if (viewMode !== "cards") {
  if (restoreTokenRef.current === myToken) restoringRef.current = false;
  return;
}

  const targetMode = mode;

  // Wait for lists to exist (stay locked while waiting)
  if (targetMode === "followers" && (!followersGames || followersGames.length === 0)) return;
  if (targetMode !== "followers" && (!games || games.length === 0)) return;

  // apply chart toggles first
  if (targetMode === "followers") {
    setFollowersValueMode(c.followersValueMode ?? "total");
    setDataMode("daily");
    setViewByDaysSinceLaunch(false);
    setShowRegionBands(false);
  } else {
    setDataMode(c.dataMode ?? "live");
    setViewByDaysSinceLaunch(!!c.viewByDaysSinceLaunch);
    setShowRegionBands(!!c.showRegionBands);
  }
  setHighlightedSeries(c.highlightedSeries ?? null);

  // seed placeholders
  const stubs = ids.map((id) => findGameStubForChart(id, targetMode));
  setChartGames(
    stubs.map((m) => ({ metadata: m, live: [], daily: [], loading: true }))
  );
  hasInitWindow.current = false;

  let cancelled = false;

  (async () => {
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          return await loadSeriesForRestore(id, targetMode);
        } catch (e) {
          console.warn("restore series failed:", id, e);
          return { appId: String(id), live: [], daily: [] };
        }
      })
    );

    if (cancelled) {
      if (restoreTokenRef.current === myToken) {
        restoringRef.current = false;
      }
      return;
    }

    setChartGames((prev) => {
      const byId = new Map(results.map((r) => [String(r.appId), r]));
      return prev.map((row) => {
        const id = String(row?.metadata?.app_id);
        const r = byId.get(id);
        if (!r) return { ...row, loading: false };
        return { ...row, live: r.live, daily: r.daily, loading: false };
      });
    });

    if (Number.isFinite(c.winStartIdx)) setWinStartIdx(c.winStartIdx);
    if (Number.isFinite(c.winEndIdx)) setWinEndIdx(c.winEndIdx);

    setPendingChartRestore(null);

    if (restoreTokenRef.current === myToken) {
      restoringRef.current = false;
    }
  })();

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [pendingChartRestore, mode, viewMode, games, followersGames]);


  /** Players: process data */
  useEffect(() => {
    if (globalLoading || !metadata.length || !latestCCU.length) return;

    try {
      setLoadingPlayers(true);
      const metaMap = new Map();
      metadata.forEach((meta) => {
        const cleanAppId = String(parseInt(meta.app_id)).trim();
        if (cleanAppId && cleanAppId !== "0") metaMap.set(cleanAppId, meta);
      });

            const combined = latestCCU
        .map((ccu) => {
          const cleanAppId = String(parseInt(ccu.app_id)).trim();
          if (cleanAppId === "0") {
            setSteamTotalOnline(parseInt(ccu.ccu?.trim() || "0", 10));
            return null;
          }
          const meta = metaMap.get(cleanAppId);
          if (!meta) return null;

          const { priceValue, isF2P } = derivePrice(meta);

          return {
            app_id: cleanAppId,
            name: meta.name?.trim() || "",
            box_art: meta.box_art?.trim() || "",
            ccu: parseInt(ccu.ccu?.trim() || "0", 10),
            release_date: meta.release_date?.trim() || "",
            full_price: meta.full_price?.trim() || "",
            price_value: priceValue,     // numeric for filters
            f2p: isF2P,                  // boolean
            short_description: meta.short_description?.trim() || "",
            developers: meta.developers?.trim() || "",
            publishers: meta.publishers?.trim() || "",
            video: pickBestVideoUrl(meta),
            tags:
              meta.tags
                ?.split(",")
                .map((t) => t.trim())
                .filter(Boolean) || [],
            primary_tags:
              (meta.tags
                ?.split(",")
                .map((t) => t.trim())
                .filter(Boolean) || []
              ).slice(0, 5),
          };
        })

        .filter(Boolean);

      combined.sort((a, b) => b.ccu - a.ccu);
      setGames(combined);
      setDisplayedGames(combined);
    } catch (err) {
      console.error("❌ Failed to process game data:", err);
    } finally {
      setLoadingPlayers(false);
    }
  }, [metadata, latestCCU, globalLoading]);

// Prefetch Followers in background while user is on Players -> Cards
useEffect(() => {
  if (mode !== "players" || viewMode !== "cards") return;
  if (followersLoaded || followersLoading) return;
  if (!metadata.length || !catalogReady || catalogLoading) return;

  let cancelled = false;

  const run = async () => {
    try {
      // Give rendering time first; run when the browser is idle
      rIC(async () => {
        try {
          const [latestRows, growthRows] = await Promise.all([
            loadCsvGz(LATEST_FOLLOWERS_URL),
            loadCsvGz(FOLLOWERS_GROWTH_URL),
          ]);

          if (cancelled) return;

          const latestMap = new Map();
          for (const r of latestRows) {
            if (!r?.app_id) continue;
            const appId = Number(r.app_id);
            latestMap.set(appId, { followers: Number(r.followers) || 0, dt: r.dt || null });
          }

          const toNumOrNull = (v) =>
            v === null || v === undefined || v === "" ? null : Number(v);

          const growthMap = new Map();
          for (const r of growthRows) {
            if (!r?.app_id) continue;
            const appId = Number(r.app_id);
            growthMap.set(appId, {
              current_followers: Number(r.current_followers) || 0,
              growth_7d: toNumOrNull(r["7_day_growth"]),
            });
          }

          const metadataMap = {};
          metadata.forEach((m) => {
            if (m?.app_id) metadataMap[String(m.app_id).trim()] = m;
          });

          const excludedTypes = new Set([
            "episode","hardware","music","series","video","unlisted","not found",
          ]);

          const allAppIds = new Set([
            ...Object.keys(metadataMap),
            ...Array.from(latestMap.keys()),
            ...Array.from(growthMap.keys()),
          ]);

          const unified = [];
          for (const appId of allAppIds) {
  const meta = metadataMap[String(appId)];
  if (!meta) continue;

  const contentType = (meta.type || "").toLowerCase();
  if (excludedTypes.has(contentType)) continue;

  const latest = latestMap.get(appId);
  const growth = growthMap.get(appId);

  const followers =
    latest?.followers ?? null ?? growth?.current_followers ?? 0;
  if (followers == null) continue;      // keep 0, only drop null/undefined

  const g7raw = growth?.growth_7d;
  const computed7 =
    g7raw == null ? growth?.current_followers ?? followers ?? 0 : g7raw;

  // NEW: derive price + f2p for Followers as well
  const { priceValue, isF2P } = derivePrice(meta);

  unified.push({
    ...meta,
    app_id: String(appId),
    name: (meta.name || "").trim(),
    box_art: (meta.box_art || "").trim(),
    followers,
    growth_7d: computed7,
    tags: meta?.tags?.split(",").map((t) => t.trim()) || [],
    primary_tags:
      (meta?.tags?.split(",").map((t) => t.trim()) || []).slice(0, 5),
    video: pickBestVideoUrl(meta),
    publishers: meta.publishers || "",
    developers: meta.developers || "",
    short_description: meta.short_description || "",
    full_price: meta.full_price || "",
    price_value: priceValue,
    f2p: isF2P,
    release_date: meta.release_date || "",
  });
}


          if (cancelled) return;

          unified.sort((a, b) => (b.followers || 0) - (a.followers || 0));

          // Prime Followers state so toggle is instant
          setFollowersGames(unified);
          setFollowersDisplayed(unified);
          setFollowersLoaded(true);
        } catch (e) {
          // ignore background prefetch errors; normal on-toggle loader will still work
          // console.warn("Followers prefetch failed:", e);
        }
      });
    } catch {}
  };

  run();
  return () => { cancelled = true; };
}, [mode, viewMode, metadata, followersLoaded, followersLoading, catalogLoading, catalogReady]);

  /** Followers: load once upon entering Followers (when metadata ready) */
  useEffect(() => {
    if (mode !== "followers") return;
    if (followersLoaded || followersLoading) return;
    if (!metadata.length || !catalogReady || catalogLoading) return;

    const metadataMap = {};
    metadata.forEach((m) => {
      if (m?.app_id) metadataMap[String(m.app_id).trim()] = m;
    });

    const loadFollowers = async () => {
      setFollowersLoading(true);
      try {
        const [latestRows, growthRows] = await Promise.all([
          loadCsvGz(LATEST_FOLLOWERS_URL),
          loadCsvGz(FOLLOWERS_GROWTH_URL),
        ]);

        const latestMap = new Map();
        for (const r of latestRows) {
          if (!r?.app_id) continue;
          const appId = Number(r.app_id);
          latestMap.set(appId, {
            followers: Number(r.followers) || 0,
            dt: r.dt || null,
          });
        }

        const toNumOrNull = (v) =>
          v === null || v === undefined || v === "" ? null : Number(v);

        const growthMap = new Map();
        for (const r of growthRows) {
          if (!r?.app_id) continue;
          const appId = Number(r.app_id);
          growthMap.set(appId, {
            current_followers: Number(r.current_followers) || 0,
            growth_7d: toNumOrNull(r["7_day_growth"]),
          });
        }

        const allAppIds = new Set([
          ...Object.keys(metadataMap),
          ...Array.from(latestMap.keys()),
          ...Array.from(growthMap.keys()),
        ]);

        const excludedTypes = new Set([
          "episode",
          "hardware",
          "music",
          "series",
          "video",
          "unlisted",
          "not found",
        ]);

        const unified = [];
for (const appId of allAppIds) {
  const meta = metadataMap[String(appId)];
  if (!meta) continue;

  const contentType = (meta.type || "").toLowerCase();
  if (excludedTypes.has(contentType)) continue;

  const latest = latestMap.get(appId);
  const growth = growthMap.get(appId);

  const followers =
    latest?.followers ?? null ?? growth?.current_followers ?? 0;
  if (followers == null) continue; // keep 0, only drop null/undefined

  const g7raw = growth?.growth_7d;
  const computed7 =
    g7raw == null ? growth?.current_followers ?? followers ?? 0 : g7raw;

  // 🔹 FIX: actually derive price + f2p for Followers
  const { priceValue, isF2P } = derivePrice(meta);

  unified.push({
    ...meta,
    app_id: String(appId),
    name: (meta.name || "").trim(),
    box_art: (meta.box_art || "").trim(),
    followers,
    growth_7d: computed7,
    tags: meta?.tags?.split(",").map((t) => t.trim()) || [],
    primary_tags:
      (meta?.tags?.split(",").map((t) => t.trim()) || []).slice(0, 5),
    video: pickBestVideoUrl(meta),
    publishers: meta.publishers || "",
    developers: meta.developers || "",
    short_description: meta.short_description || "",
    full_price: meta.full_price || "",
    price_value: priceValue, // numeric for filters
    f2p: isF2P,              // boolean for "Free to Play"
    release_date: meta.release_date || "",
  });
}


        unified.sort((a, b) => (b.followers || 0) - (a.followers || 0));
        setFollowersGames(unified);
        setFollowersDisplayed(unified);
        setFollowersLoaded(true);
      } catch (e) {
        console.error("❌ Failed to load followers CSV.GZ:", e);
      } finally {
        setFollowersLoading(false);
      }
    };

    loadFollowers();
  }, [mode, metadata, followersLoaded, followersLoading, catalogLoading, catalogReady]);

  /** Shared export helper */
  const exportCSV = (rows, filename) => {
    const csv = Papa.unparse(rows);
    const utf8Bom = "\uFEFF";
    const blob = new Blob([utf8Bom + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Players handlers */
  const handleRemove = (app_id) => setHiddenGames((prev) => [...prev, app_id]);
  const handleResetPlayers = () => {
  setSavedViewsKey((k) => k + 1);
  setMgMissingById(false);
  setMgMissingByName(false);
  setExcludeAdultPrimary(false);
  setHiddenGames([]);
  setSelectedGame(null);
  setSelectedTag(null);
  setSelectedPublisher(null);
  setSelectedDevelopers(null);
  setDisplayedGames(games);
  setStreamNonce((n) => n + 1);
  setHoveredCards({});
  setIsReleasedThisWeek(false);
  setSelectedPriceRange(PRICE_TIERS.map((t) => t.id)); // reset price tiers to ALL
  setSelectedDateRange([null, null]);
  setChartGames([]);             // no series -> chart disappears
  setHighlightedSeries(null);    // clear any highlight
  setWinStartIdx(0);
  setWinEndIdx(0);
  setMinPlayersRaw("");
  setMinFollowersRaw("");
  hasInitWindow.current = false; // so the window re-inits next time
};

  const handleExportPlayers = () => {
    const visibleGames = filteredPlayers.filter(
      (g) => !hiddenGames.includes(g.app_id)
    );
    exportCSV(visibleGames, "Steam_Live_Player_Counts.csv");
  };
  const handleReleasedThisWeekPlayers = () => {
    const filtered = games.filter((g) => withinLastNDays(g.release_date, 7));
    setDisplayedGames(filtered);
    setStreamNonce((n) => n + 1);
  };

 // Quick refresh button: re-download latest_ccu.csv.gz and recompute games in-place
const refreshSteamData = useCallback(async () => {
  try {
    // 1) Fetch latest_ccu.csv.gz with a cache-buster
    const rows = await loadCsvGz(`${LATEST_CCU_URL}?t=${Date.now()}`);

    // 2) Build metadata map (same as in Players useEffect)
    const metaMap = new Map();
    metadata.forEach((meta) => {
      const cleanAppId = String(parseInt(meta.app_id)).trim();
      if (cleanAppId && cleanAppId !== "0") metaMap.set(cleanAppId, meta);
    });

    // 3) Rebuild combined games + total online
    let newOnline = 0;

    const combined = rows
      .map((ccu) => {
        const cleanAppId = String(parseInt(ccu.app_id)).trim();
        if (!cleanAppId) return null;

        if (cleanAppId === "0") {
          const val =
            typeof ccu.ccu === "number"
              ? ccu.ccu
              : parseInt(String(ccu.ccu || "0").trim(), 10);
          newOnline = Number.isFinite(val) ? val : 0;
          return null;
        }

        const meta = metaMap.get(cleanAppId);
        if (!meta) return null;

        const ccuVal =
          typeof ccu.ccu === "number"
            ? ccu.ccu
            : parseInt(String(ccu.ccu || "0").trim(), 10);

        const { priceValue, isF2P } = derivePrice(meta);

        return {
          app_id: cleanAppId,
          name: meta.name?.trim() || "",
          box_art: meta.box_art?.trim() || "",
          ccu: Number.isFinite(ccuVal) ? ccuVal : 0,
          release_date: meta.release_date?.trim() || "",
          full_price: meta.full_price?.trim() || "",
          price_value: priceValue,
          f2p: isF2P,
          short_description: meta.short_description?.trim() || "",
          developers: meta.developers?.trim() || "",
          publishers: meta.publishers?.trim() || "",
          video: pickBestVideoUrl(meta),
          tags: meta.tags?.split(",").map((t) => t.trim()).filter(Boolean) || [],
          primary_tags:
            (meta.tags?.split(",").map((t) => t.trim()).filter(Boolean) || []).slice(0, 5),
        };
      })
      .filter(Boolean);

    combined.sort((a, b) => b.ccu - a.ccu);

    setSteamTotalOnline(newOnline);
    setGames(combined);
    setDisplayedGames(combined);

    // Refresh any games in chart
    if (chartGames.length) {
      const refreshed = await Promise.all(
        chartGames.map(async (g) => {
          const appId = String(g.metadata.app_id);
          const [live, daily] = await Promise.all([
            loadLiveSeries(appId),
            loadDailySeries(appId),
          ]);
          return { ...g, live, daily };
        })
      );
      hasInitWindow.current = false;
      setChartGames(refreshed);
    }
  } catch (err) {
    console.error("Steam refresh failed:", err);
  }
}, [metadata, chartGames]); // ✅ THIS LINE WAS MISSING IN YOUR VERSION

const handleQuickRefresh = async () => {
  armRefreshCooldown(); // ✅ ONLY manual click triggers cooldown
  await refreshSteamData();
};


// Price formatter: uses derived price + f2p flag
const formatPrice = (game) => {
  if (!game) return null;

  // Only treat **boolean true** as free-to-play
  if (game.f2p === true) return "Free to Play";

  const n = game.price_value;
  if (n == null || !Number.isFinite(n)) {
    // No known price
    return "-";
  }

  const formatted = Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // NO SPACE after $
  return `$${formatted}`;
};

// ---------- Debug copy helpers ----------
const [copiedToast, setCopiedToast] = useState(null); // { text, ts } | null
const copyToastTimerRef = useRef(null);

const showCopyToast = (text) => {
  setCopiedToast({ text, ts: Date.now() });
  if (copyToastTimerRef.current) clearTimeout(copyToastTimerRef.current);
  copyToastTimerRef.current = setTimeout(() => setCopiedToast(null), 900);
};

useEffect(() => {
  return () => {
    if (copyToastTimerRef.current) clearTimeout(copyToastTimerRef.current);
  };
}, []);

async function copyTextToClipboard(text) {
  const value = String(text ?? "");
  if (!value) return;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
    } else {
      // fallback for older browsers / non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      ta.style.top = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    showCopyToast(`Copied: ${value}`);
  } catch (e) {
    console.warn("Copy failed:", e);
    showCopyToast("Copy failed (clipboard blocked)");
  }
}

const copyBtnBase = {
  height: 20,
  lineHeight: "20px",
  borderRadius: 14,
  padding: "0 6px",
  border: "1px solid rgba(255,255,255,0.25)",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};


const MgMissingButtons = (
  <>
    <button
      type="button"
      disabled={!mgMetaLoaded && mgMetaLoading}
      onClick={() => setMgMissingById((v) => !v)}
      style={{
        width: "100%",
        minHeight: 36,
        background: mgMissingById ? "#3fa9f5" : "#333",
        border: "1px solid",
        borderColor: mgMissingById ? "#3fa9f5" : "#555",
        borderRadius: 8,
        padding: "8px 12px",
        color: mgMissingById ? "#000" : "#fff",
        cursor: !mgMetaLoaded && mgMetaLoading ? "not-allowed" : "pointer",
        opacity: !mgMetaLoaded && mgMetaLoading ? 0.65 : 1,
      }}
      title={
        !mgMetaLoaded && mgMetaLoading
          ? "Loading mindGAME metadata…"
          : "Show only Steam games whose app_id does NOT exist in mindGAME metadata Steam_ID"
      }
    >
      ID Missing from MG
    </button>

    <button
      type="button"
      disabled={!mgMetaLoaded && mgMetaLoading}
      onClick={() => setMgMissingByName((v) => !v)}
      style={{
        width: "100%",
        minHeight: 36,
        background: mgMissingByName ? "#3fa9f5" : "#333",
        border: "1px solid",
        borderColor: mgMissingByName ? "#3fa9f5" : "#555",
        borderRadius: 8,
        padding: "8px 12px",
        color: mgMissingByName ? "#000" : "#fff",
        cursor: !mgMetaLoaded && mgMetaLoading ? "not-allowed" : "pointer",
        opacity: !mgMetaLoaded && mgMetaLoading ? 0.65 : 1,
      }}
      title={
        !mgMetaLoaded && mgMetaLoading
          ? "Loading mindGAME metadata…"
          : "Show only Steam games whose name does NOT exist in mindGAME metadata Game"
      }
    >
      Name Missing from MG
    </button>

<button
  type="button"
  onClick={() => setExcludeAdultPrimary((v) => !v)}
  style={{
    width: "100%",
    minHeight: 36,
    background: excludeAdultPrimary ? "#3fa9f5" : "#333",
    border: "1px solid",
    borderColor: excludeAdultPrimary ? "#3fa9f5" : "#555",
    borderRadius: 8,
    padding: "8px 12px",
    color: excludeAdultPrimary ? "#000" : "#fff",
    cursor: "pointer",
  }}
  title="Exclude games whose 1st or 2nd primary tag is Sexual Content, NSFW, or Nudity, or whose 1st, 2nd, or 3rd primary tag is Utilities"
>
  Exclude Adult Only/Utilities
</button>

  </>
);

    /** Followers handlers */
  const handleRemoveFollower = (app_id) =>
    setHiddenFollowers((prev) => [...prev, app_id]);

  const handleResetFollowers = () => {
    setSavedViewsKey((k) => k + 1);
    setMgMissingById(false);
    setMgMissingByName(false);
    setExcludeAdultPrimary(false);
    setHiddenFollowers([]);
    setSelectedGame(null);
    setSelectedTag(null);
    setSelectedPublisher(null);
    setFollowersDisplayed(followersGames);
    setHoveredCards({});
    setIsReleasedThisWeek(false);
    setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
    setSelectedDateRange([null, null]);
    setMinPlayersRaw("");
    setMinFollowersRaw("");


    // 🔹 Reset the compare chart, same as Players
    setChartGames([]);
    setHighlightedSeries(null);
    setAnimSeriesId(null);
    setWinStartIdx(0);
    setWinEndIdx(0);
    hasInitWindow.current = false;
  };


  const handleExportFollowers = () => {
    const visible = filteredFollowers.filter(
      (g) => !hiddenFollowers.includes(g.app_id)
    );
    exportCSV(visible, "Steam_Followers.csv");
  };
  const handleReleasedThisWeekFollowers = () => {
    const filtered = followersGames.filter((g) =>
      withinLastNDays(g.release_date, 7)
    );
    setFollowersDisplayed(filtered);
  };

// Prefer microtrailer if available, otherwise fall back to main trailer/video
function pickBestVideoUrl(meta) {
  if (!meta) return "";
  return (
    (meta.microtrailer ||
      meta.micro_trailer ||
      meta.micro_trailer_url ||
      meta.video ||
      meta.trailer ||
      meta.video_url ||
      "") + ""
  ).trim();
}

  /* ============================
     FAST PUBLISHER FILTER PATH
     ============================ */

  // --- Helpers to safely split publisher strings without creating stray "Inc." entries ---
  function normalizePublisherCommas(raw = "") {
    // remove the comma before common suffixes so "Studio, Inc." -> "Studio Inc."
    return raw.replace(
      /,\s*(inc\.?|ltd\.?|llc\.?|corp\.?|corporation|co\.?(?:,\s*ltd\.?)?|gmbh|plc|s\.a\.|s\.r\.l\.|pte\.?\s*ltd\.?|k\.k\.)\b/gi,
      " $1"
    );
  }
  function splitPublishersSafe(raw = "") {
    const cleaned = normalizePublisherCommas(raw);
    return cleaned
      .split(/[,;|/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Per-game normalized publisher cache: app_id -> string[] (lowercased)
  const pubNormCache = useRef(new Map());
  function getPublishersNorm(g) {
    const key = g.app_id;
    const cached = pubNormCache.current.get(key);
    if (cached) return cached;
    const list = splitPublishersSafe(g.publishers || g.publisher || "").map(
      (s) => s.toLowerCase()
    );
    pubNormCache.current.set(key, list);
    return list;
  }

  // Optional global index (lazy): publisherLower -> Set<app_id>
  const publisherIndexRef = useRef(null);
  function buildPublisherIndexIfNeeded(sourceGames) {
    if (publisherIndexRef.current) return;
    const idx = new Map();
    for (const g of sourceGames) {
      const pubs = getPublishersNorm(g);
      for (const p of pubs) {
        if (!idx.has(p)) idx.set(p, new Set());
        idx.get(p).add(g.app_id);
      }
    }
    publisherIndexRef.current = idx;
  }

  // Build the index when the menu opens, in idle time (no impact to TTI)
  const onPublishersMenuOpen = () => {
    const doBuild = () =>
      buildPublisherIndexIfNeeded([...games, ...followersGames]);
    (window.requestIdleCallback || ((cb) => setTimeout(cb, 0)))(doBuild);
  };

  // Lightweight options list: cap count, only expand when user types
  const publisherOptions = useMemo(() => {
    const MAX_DEFAULT = 200;
    const MAX_FILTERED = 500;
    if (!allPublishers?.length) return [];
    if ((pubQuery || "").length < 2) {
      return allPublishers
        .slice(0, MAX_DEFAULT)
        .map((p) => ({ label: p, value: p }));
    }
    const q = pubQuery.toLowerCase();
    return allPublishers
      .filter((p) => p.toLowerCase().includes(q))
      .slice(0, MAX_FILTERED)
      .map((p) => ({ label: p, value: p }));
  }, [allPublishers, pubQuery]);

  // Top publishers by current CCU (players), built once games are loaded — uses safe split
  const TOP_PUBLISHERS_LIMIT = 500;
  const publisherOptionsByCCU = useMemo(() => {
    if (!games?.length) {
      return allPublishers.map((p) => ({ label: p, value: p }));
    }

    // accumulate using lowercase key, but keep the richest display name variant
    const acc = new Map(); // keyLower -> { name, total, count }
    for (const g of games) {
      const pubs = splitPublishersSafe(g.publishers || "");
      for (const name of pubs) {
        const key = name.toLowerCase();
        const prev = acc.get(key) || { name, total: 0, count: 0 };
        const total = prev.total + (g.ccu || 0);
        const count = prev.count + 1;
        // prefer longer display variant if we see multiple
        const display = name.length > prev.name.length ? name : prev.name;
        acc.set(key, { name: display, total, count });
      }
    }

    return Array.from(acc.values())
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
      .slice(0, TOP_PUBLISHERS_LIMIT)
      .map(({ name, total, count }) => ({
        value: name,
        label: `${name} — ${total.toLocaleString()} CCU • ${count} game${
          count === 1 ? "" : "s"
        }`,
      }));
  }, [games, allPublishers]);

  // Top tags by current CCU (players), same idea as publishers
  const TOP_TAGS_LIMIT = 500;
  const tagsOptionsByCCU = useMemo(() => {
    if (!games?.length) {
      return allTags.map((t) => ({ label: t, value: t }));
    }
    const acc = new Map(); // tag -> { total, count }
    for (const g of games) {
      const tags = Array.isArray(g.tags) ? g.tags : [];
      for (const t of tags) {
        const prev = acc.get(t) || { total: 0, count: 0 };
        acc.set(t, { total: prev.total + (g.ccu || 0), count: prev.count + 1 });
      }
    }
    return Array.from(acc.entries())
      .sort((a, b) => b[1].total - a[1].total || a[0].localeCompare(b[0]))
      .slice(0, TOP_TAGS_LIMIT)
      .map(([tag, { total, count }]) => ({
        value: tag,
        label: `${tag} — ${total.toLocaleString()} CCU • ${count} game${
          count === 1 ? "" : "s"
        }`,
      }));
  }, [games, allTags]);

// Alphabetized Primary Tags (first 5 tags from metadata only)
const primaryTagsAlphaOptions = useMemo(() => {
  if (!metadata?.length) return [];
  const set = new Set();
  for (const m of metadata) {
    const t = (m?.tags || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 5);
    for (const tag of t) set.add(tag);
  }
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b))
    .map((tag) => ({ label: tag, value: tag }));
}, [metadata]);


  const gamesOptionsByCCU = useMemo(() => {
    const totalCcu = games.reduce((sum, g) => sum + (g.ccu || 0), 0);
    return games
      .slice() // avoid mutating original array
      .sort((a, b) => b.ccu - a.ccu)
      .map((g) => {
        const share =
          totalCcu > 0 ? ((g.ccu / totalCcu) * 100).toFixed(2) : "0.00";
        return {
          label: `${g.name} — ${g.ccu?.toLocaleString()} CCU • ${share}%`,
          value: g.app_id,
        };
      });
  }, [games]);

  const getGameOptionName = (option) => String(option?.label || "").split(" — ")[0].trim();

  const followersOptionsByFollowers = useMemo(() => {
    return followersGames
      .slice()
      .sort((a, b) => (b.followers || 0) - (a.followers || 0) || a.name.localeCompare(b.name))
      .map((g) => ({
        label: `${g.name} — ${(g.followers || 0).toLocaleString()} followers`,
        value: g.app_id,
      }));
  }, [followersGames]);

  /** -------- Filtering (Developers) -------- */
  function normalizeDeveloperCommas(raw = "") {
    return raw.replace(
      /,\s*(inc\.?|ltd\.?|llc\.?|corp\.?|corporation|co\.?(?:,\s*ltd\.?)?|gmbh|plc|s\.a\.|s\.r\.l\.|pte\.?\s*ltd\.?|k\.k\.)\b/gi,
      " $1"
    );
  }

  function splitDevelopersSafe(raw = "") {
    const cleaned = normalizeDeveloperCommas(raw);
    return cleaned
      .split(/[,;|/]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const devNormCache = useRef(new Map());
  function getDevelopersNorm(g) {
    const key = g.app_id;
    const cached = devNormCache.current.get(key);
    if (cached) return cached;
    const list = splitDevelopersSafe(g.developers || g.developer || "").map(
      (s) => s.toLowerCase()
    );
    devNormCache.current.set(key, list);
    return list;
  }

  const developerOptions = useMemo(() => {
    if (!allDevelopers?.length) return [];
    const filtered = devQuery
      ? allDevelopers.filter((d) =>
          d.toLowerCase().includes(devQuery.toLowerCase())
        )
      : allDevelopers;
    return filtered.map((d) => ({ label: d, value: d }));
  }, [allDevelopers, devQuery]);

  const developerOptionsByCCU = useMemo(() => {
    if (!games?.length) {
      return allDevelopers.map((d) => ({ label: d, value: d }));
    }

    const acc = new Map();
    for (const g of games) {
      const devs = splitDevelopersSafe(g.developers || "");
      for (const name of devs) {
        const key = name.toLowerCase();
        const prev = acc.get(key) || { name, total: 0, count: 0 };
        const total = prev.total + (g.ccu || 0);
        const count = prev.count + 1;
        const display = name.length > prev.name.length ? name : prev.name;
        acc.set(key, { name: display, total, count });
      }
    }

    return Array.from(acc.values())
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
      .map(({ name, total, count }) => ({
        value: name,
        label: `${name} — ${total.toLocaleString()} CCU • ${count} game${
          count === 1 ? "" : "s"
        }`,
      }));
  }, [games, allDevelopers]);

  /** -------- Filtering (Players) -------- */
  const filteredPlayers = useMemo(() => {
    let result = displayedGames
      .filter((g) => !hiddenGames.includes(g.app_id))
      .filter((g) => nameMatchesSearch(g.name, searchTerm))
      .filter((g) => (minPlayers > 0 ? (Number(g.ccu) || 0) > minPlayers : true))
      .filter((g) => {
  if (!selectedTag) return true;
  const sel = Array.isArray(selectedTag) ? selectedTag : [selectedTag];
  if (sel.length === 0) return true;
  const tags = Array.isArray(g.tags) ? g.tags : [];
  return sel.some((t) => tags.includes(t.value)); // OR across selected tags
})

  // ---- MG "Missing from mindGAME" filter (applies to whatever is currently on-screen)
.filter((g) => {
  if (!mgMetaLoaded) return true;

  // if neither toggle is on, pass everything
  if (!mgMissingById && !mgMissingByName) return true;

  // compute matches
  const appIdStr = String(parseInt(g.app_id ?? "", 10)).trim();
  const idMatches = appIdStr ? mgSteamIdSetRef.current.has(appIdStr) : false;

  const nm = String(g.name ?? "").trim().toLowerCase();
  const nameMatches = nm ? mgGameNameSetRef.current.has(nm) : false;

  // AND logic: if a toggle is on, require "missing" for that dimension
  if (mgMissingById && idMatches) return false;
  if (mgMissingByName && nameMatches) return false;

  return true;
})

// Exclude games where:
// - 1st or 2nd primary tag is Sexual Content / NSFW / Nudity
// - OR any of the first 3 primary tags is Utilities
.filter((g) => {
  if (!excludeAdultPrimary) return true;

  // Prefer primary_tags (already first 5), otherwise fall back to tags
  const list =
    Array.isArray(g.primary_tags) && g.primary_tags.length
      ? g.primary_tags
      : Array.isArray(g.tags) && g.tags.length
      ? g.tags
      : [];

  const t0 = String(list[0] ?? "").trim().toLowerCase();
  const t1 = String(list[1] ?? "").trim().toLowerCase();
  const t2 = String(list[2] ?? "").trim().toLowerCase();

  const hasAdultPrimary = ADULT_PRIMARY_TAGS.has(t0) || ADULT_PRIMARY_TAGS.has(t1);
  const hasExcludedUtility = EXCLUDED_PRIMARY_TAGS.has(t0) || EXCLUDED_PRIMARY_TAGS.has(t1) || EXCLUDED_PRIMARY_TAGS.has(t2);

  return !(hasAdultPrimary || hasExcludedUtility);
})

// Primary Tags (first five only)
.filter((g) => {
  if (!selectedPrimaryTag) return true;
  const sel = Array.isArray(selectedPrimaryTag) ? selectedPrimaryTag : [selectedPrimaryTag];
  if (sel.length === 0) return true;
  const p = Array.isArray(g.primary_tags) ? g.primary_tags : (Array.isArray(g.tags) ? g.tags.slice(0, 5) : []);
  return sel.some((t) => p.includes(t.value));
});

    if (selectedPublisher) {
  const sel = Array.isArray(selectedPublisher) ? selectedPublisher : [selectedPublisher];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    const idx = publisherIndexRef.current;
    if (idx) {
      // fast path via index: OR across the chosen publishers
      result = result.filter((g) => {
        const idStr = g.app_id;
        for (const w of wants) {
          const set = idx.get(w);
          if (set && set.has(idStr)) return true;
        }
        return false;
      });
    } else {
      // fallback: per-card normalized list
      result = result.filter((g) => {
        const pubs = getPublishersNorm(g); // lowercased array
        return wants.some((w) => pubs.includes(w));
      });
    }
  }
}

    if (selectedDevelopers) {
  const sel = Array.isArray(selectedDevelopers) ? selectedDevelopers : [selectedDevelopers];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    result = result.filter((g) => {
      const devs = getDevelopersNorm(g); // lowercased array
      return wants.some((w) => devs.includes(w)); // OR across selected devs
    });
  }
}

    if (selectedDateRange[0] && selectedDateRange[1]) {
      const [from, to] = selectedDateRange;
      result = result.filter((g) => {
        if (!g.release_date) return false;
        const rd = new Date(g.release_date);
        return rd >= from && rd <= to;
      });
    }

            const activeTierIds = Array.isArray(selectedPriceRange)
      ? selectedPriceRange
      : [];

    if (activeTierIds.length > 0 && activeTierIds.length < PRICE_TIERS.length) {
      result = result.filter((g) => {
        const price = g.price_value;
        if (price == null || !Number.isFinite(price)) return false; // unknown price -> excluded when filtering
        return activeTierIds.some((id) => {
          const tier = PRICE_TIER_MAP[id];
          if (!tier) return false;
          return price >= tier.min && price <= tier.max;
        });
      });
    }


    // Only enforce CCU>0 when no explicit publisher is selected
    if (!selectedPublisher) {
      result = result.filter((g) => g.ccu > 0);
    }

    return result;
  }, [
    displayedGames,
    hiddenGames,
    searchTerm,
    selectedTag,
    selectedPublisher,
    selectedDevelopers,
    selectedDateRange,
    selectedPriceRange,
    selectedPrimaryTag,
    mgMissingById,
    mgMissingByName,
    excludeAdultPrimary,
    mgMetaLoaded,
    minPlayers,  
  ]);

  /** -------- Filtering (Followers) -------- */
  const filteredFollowers = useMemo(() => {
    let result = followersDisplayed
      .filter((g) => !hiddenFollowers.includes(g.app_id))
      .filter((g) => nameMatchesSearch(g.name, searchTerm))
      .filter((g) => (minFollowers > 0 ? (Number(g.followers) || 0) > minFollowers : true))
      .filter((g) => {
  if (!selectedTag) return true;
  const sel = Array.isArray(selectedTag) ? selectedTag : [selectedTag];
  if (sel.length === 0) return true;
  const tags = Array.isArray(g.tags) ? g.tags : [];
  return sel.some((t) => tags.includes(t.value));
})

  // ---- MG "Missing from mindGAME" filter (applies to whatever is currently on-screen)
.filter((g) => {
  if (!mgMetaLoaded) return true;

  // if neither toggle is on, pass everything
  if (!mgMissingById && !mgMissingByName) return true;

  // compute matches
  const appIdStr = String(parseInt(g.app_id ?? "", 10)).trim();
  const idMatches = appIdStr ? mgSteamIdSetRef.current.has(appIdStr) : false;

  const nm = String(g.name ?? "").trim().toLowerCase();
  const nameMatches = nm ? mgGameNameSetRef.current.has(nm) : false;

  // AND logic: if a toggle is on, require "missing" for that dimension
  if (mgMissingById && idMatches) return false;
  if (mgMissingByName && nameMatches) return false;

  return true;
})

// Exclude games where:
// - 1st or 2nd primary tag is Sexual Content / NSFW / Nudity
// - OR any of the first 3 primary tags is Utilities
.filter((g) => {
  if (!excludeAdultPrimary) return true;

  // Prefer primary_tags (already first 5), otherwise fall back to tags
  const list =
    Array.isArray(g.primary_tags) && g.primary_tags.length
      ? g.primary_tags
      : Array.isArray(g.tags) && g.tags.length
      ? g.tags
      : [];

  const t0 = String(list[0] ?? "").trim().toLowerCase();
  const t1 = String(list[1] ?? "").trim().toLowerCase();
  const t2 = String(list[2] ?? "").trim().toLowerCase();

  const hasAdultPrimary = ADULT_PRIMARY_TAGS.has(t0) || ADULT_PRIMARY_TAGS.has(t1);
  const hasExcludedUtility = EXCLUDED_PRIMARY_TAGS.has(t0) || EXCLUDED_PRIMARY_TAGS.has(t1) || EXCLUDED_PRIMARY_TAGS.has(t2);

  return !(hasAdultPrimary || hasExcludedUtility);
})

// Primary Tags (first three only)
.filter((g) => {
  if (!selectedPrimaryTag) return true;
  const sel = Array.isArray(selectedPrimaryTag) ? selectedPrimaryTag : [selectedPrimaryTag];
  if (sel.length === 0) return true;
  const p = Array.isArray(g.primary_tags) ? g.primary_tags : (Array.isArray(g.tags) ? g.tags.slice(0, 5) : []);
  return sel.some((t) => p.includes(t.value));
})
      .filter((g) => {
        if (gamesFilter !== "unreleased") return true;

        const rd = safeDate(g.release_date);

        // ✅ Safer rule: Unreleased must have a real future date.
// Missing/invalid date -> EXCLUDE from Unreleased
if (!rd) return false;

// Future date -> Unreleased
return rd > new Date();

      });

    if (selectedPublisher) {
  const sel = Array.isArray(selectedPublisher) ? selectedPublisher : [selectedPublisher];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    const idx = publisherIndexRef.current;
    if (idx) {
      // fast path via index: OR across the chosen publishers
      result = result.filter((g) => {
        const idStr = g.app_id;
        for (const w of wants) {
          const set = idx.get(w);
          if (set && set.has(idStr)) return true;
        }
        return false;
      });
    } else {
      // fallback: per-card normalized list
      result = result.filter((g) => {
        const pubs = getPublishersNorm(g); // lowercased array
        return wants.some((w) => pubs.includes(w));
      });
    }
  }
}

            const activeTierIds = Array.isArray(selectedPriceRange)
      ? selectedPriceRange
      : [];

    if (activeTierIds.length > 0 && activeTierIds.length < PRICE_TIERS.length) {
      result = result.filter((g) => {
        const price = g.price_value;
        if (price == null || !Number.isFinite(price)) return false;
        return activeTierIds.some((id) => {
          const tier = PRICE_TIER_MAP[id];
          if (!tier) return false;
          return price >= tier.min && price <= tier.max;
        });
      });
    }



    if (selectedDateRange[0] && selectedDateRange[1]) {
      const [from, to] = selectedDateRange;
      result = result.filter((g) => {
        if (!g.release_date) return false;
        const rd = new Date(g.release_date);
        return rd >= from && rd <= to;
      });
    }

    if (selectedDevelopers) {
  const sel = Array.isArray(selectedDevelopers) ? selectedDevelopers : [selectedDevelopers];
  if (sel.length > 0) {
    const wants = sel
      .map((s) => (s?.value || "").trim().toLowerCase())
      .filter(Boolean);

    result = result.filter((g) => {
      const devs = getDevelopersNorm(g); // lowercased array
      return wants.some((w) => devs.includes(w)); // OR across selected devs
    });
  }
}

    return result;
  }, [
    followersDisplayed,
    hiddenFollowers,
    searchTerm,
    selectedTag,
    selectedPublisher,
    selectedDevelopers,
    gamesFilter,
    selectedDateRange,
    selectedPriceRange,
    selectedPrimaryTag,
    mgMissingById,
    mgMissingByName,
    excludeAdultPrimary,
    mgMetaLoaded,
    minFollowers, 
  ]);

  /** Sorting (Followers) */
  const sortedFollowers = useMemo(() => {
    const copy = [...filteredFollowers];
    if (followersSort === "growth_7d") {
      copy.sort(
        (a, b) => (b.growth_7d ?? -Infinity) - (a.growth_7d ?? -Infinity)
      );
    } else {
      copy.sort((a, b) => (b.followers || 0) - (a.followers || 0));
    }
    return copy;
  }, [filteredFollowers, followersSort]);

  /** Stats */
  const { totalCcu } = useMemo(() => {
    const total = filteredPlayers.reduce((sum, g) => sum + (g.ccu || 0), 0);
    return { totalCcu: total };
  }, [filteredPlayers]);

  const baselineTotalCcu = useMemo(() => {
    return games.reduce((sum, g) => {
      if (!(g.ccu > 0)) return sum;
      return sum + (g.ccu || 0);
    }, 0);
  }, [games]);

  const playersShareOfTotal = useMemo(() => {
    if (baselineTotalCcu <= 0) return "0.00";
    return ((totalCcu / baselineTotalCcu) * 100).toFixed(2);
  }, [totalCcu, baselineTotalCcu]);

// When games are added to the Player History chart, compute stats for that selection
const chartSelectionStats = useMemo(() => {
  if (!chartGames.length) return null;

  const ids = new Set(chartGames.map((g) => String(g.metadata.app_id)));

  let totalChartCcu = 0;
  for (const g of games) {
    if (ids.has(String(g.app_id))) {
      totalChartCcu += g.ccu || 0;
    }
  }

  const pctOfBaseline =
    baselineTotalCcu > 0
      ? ((totalChartCcu / baselineTotalCcu) * 100).toFixed(2)
      : "0.00";

  return {
    totalChartCcu,
    pctOfBaseline,
  };
}, [chartGames, games, baselineTotalCcu]);

// Are we in the compare chart mode (Players + Cards + at least one chart game)?
const inChartMode =
  mode === "players" && viewMode === "cards" && !!chartSelectionStats;

// Header numbers: total CCU + % of baseline
const headerTotalCcu =
  inChartMode && chartSelectionStats
    ? chartSelectionStats.totalChartCcu
    : totalCcu;

const headerPctRaw =
  inChartMode && chartSelectionStats
    ? chartSelectionStats.pctOfBaseline
    : playersShareOfTotal;

// If it's exactly 100.00, display 100; otherwise keep XX.XX
const headerPlayersShareDisplay =
  headerPctRaw === "100.00" ? "100" : headerPctRaw;

// Alphabetical legend list for the chip row above the chart
const chartGamesLegend = useMemo(() => {
  return [...chartGames].sort((a, b) => {
    const an = (a?.metadata?.name || "").toLowerCase();
    const bn = (b?.metadata?.name || "").toLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    return 0;
  });
}, [chartGames]);


    const followersTotal = useMemo(
    () => sortedFollowers.reduce((sum, g) => sum + (g.followers || 0), 0),
    [sortedFollowers]
  );

  // When games are added to the Followers chart, compute total followers
  const followersChartStats = useMemo(() => {
    if (mode !== "followers" || !chartGames.length) return null;

    const ids = new Set(chartGames.map((g) => String(g.metadata.app_id)));

    let totalFollowers = 0;
    for (const g of followersGames) {
      if (ids.has(String(g.app_id))) {
        totalFollowers += g.followers || 0;
      }
    }

    return { totalFollowers };
  }, [mode, chartGames, followersGames]);

  const inFollowersChartMode =
    mode === "followers" && viewMode === "cards" && !!followersChartStats;

  const headerFollowersTotal =
    inFollowersChartMode && followersChartStats
      ? followersChartStats.totalFollowers
      : followersTotal;


  /** Windowed rendering (Players cards) */
  const INITIAL_BATCH = 120;
  const BATCH_SIZE = 80;
  const [itemsShown, setItemsShown] = useState(INITIAL_BATCH);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setItemsShown(INITIAL_BATCH);
  }, [
    searchTerm,
    selectedGame,
    selectedTag,
    selectedPublisher,
    displayedGames,
    hiddenGames,
    loadingPlayers,
    viewMode,
    mode,
    minPlayers,
  ]);

  // Effervescent sizing (no scroll)
  const mainRef = useRef(null);
  const headerRef = useRef(null);
  const [effHeight, setEffHeight] = useState(600);

  useEffect(() => {
    const calc = () => {
      const mainEl = mainRef.current;
      const headerEl = headerRef.current;
      if (!mainEl || !headerEl) return; // guard inside callback
      const mainRect = mainEl.getBoundingClientRect();
      const headerH = headerEl.getBoundingClientRect().height;
      setEffHeight(Math.max(0, mainRect.height - headerH));
    };
    const ro = new ResizeObserver(calc);
    if (mainRef.current) ro.observe(mainRef.current);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener("resize", calc);
    calc();
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calc);
    };
  }, []);

  useEffect(() => {
    if (mode !== "players" || viewMode !== "cards") return;
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setItemsShown((prev) =>
            prev >= filteredPlayers.length
              ? prev
              : Math.min(filteredPlayers.length, prev + BATCH_SIZE)
          );
        }
      },
      { root: null, rootMargin: "800px 0px 800px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [filteredPlayers.length, viewMode, mode]);

  const visiblePlayers = useMemo(
    () =>
      viewMode === "cards"
        ? filteredPlayers.slice(0, itemsShown)
        : filteredPlayers,
    [filteredPlayers, itemsShown, viewMode]
  );

  const hasMorePlayers =
    mode === "players" &&
    viewMode === "cards" &&
    itemsShown < filteredPlayers.length;

  // ===== Market/Effervescent scaffolding (Players only) =====
  const bubbleContainerRef = useRef(null);
  const bubbleSvgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [zoomT, setZoomT] = useState(d3.zoomIdentity);
  const [bubbleHovered, setBubbleHovered] = useState(null);

  useEffect(() => {
    if (mode !== "players" || viewMode !== "market") return;
    if (!bubbleContainerRef.current) return;
    const el = bubbleContainerRef.current;
    const ro = new ResizeObserver(() => {
      const b = el.getBoundingClientRect();
      setDimensions({ width: b.width, height: b.height });
    });
    ro.observe(el);
    const b = el.getBoundingClientRect();
    setDimensions({ width: b.width, height: b.height });
    return () => ro.disconnect();
  }, [viewMode, mode]);

  const { maxCcu } = useMemo(() => {
    const max = Math.max(1, ...filteredPlayers.map((g) => g.ccu || 0));
    return { maxCcu: max };
  }, [filteredPlayers]);

  const colorScale = useMemo(() => {
    const palette = [
      "#ff5342",
      "#fe6d4e",
      "#ff9463",
      "#ffc28b",
      "#819898",
      "#4c7d8e",
      "#174d7c",
      "#2794c3",
    ];
    const interp = d3.piecewise(d3.interpolateRgb.gamma(2.2), palette);

    const LOW_RED_CUTOFF = 100;
    const max = Math.max(LOW_RED_CUTOFF * 10, maxCcu || 1);

    const logT = d3
      .scaleLog()
      .domain([LOW_RED_CUTOFF, max])
      .range([0.25, 1])
      .clamp(true);

    return (ccu) => {
      const v = Math.max(0, ccu || 0);
      if (v === 0) return palette[0];
      if (v < LOW_RED_CUTOFF) {
        return interp((v / LOW_RED_CUTOFF) * 0.25);
      }
      return interp(logT(v));
    };
  }, [maxCcu]);

  const bubbleNodes = useMemo(() => {
  if (mode !== "players" || viewMode !== "market") return [];

  const w = Math.max(1, dimensions.width || 1200);
  const h = Math.max(120, dimensions.height || Math.max(300, effHeight));
  if (!visiblePlayers.length) return [];
  
// If a full-set layout was precomputed in the background for this exact set/size, use it
const fullIds = filteredPlayers.map((g) => g.app_id).join(",");
const expectedFullKey = `full:${fullIds}|${w}x${h}`;
if (
  marketCacheRef.current.key === expectedFullKey &&
  Array.isArray(marketCacheRef.current.nodes) &&
  marketCacheRef.current.nodes.length
) {
  return marketCacheRef.current.nodes;
}


  // Build children
    const children = visiblePlayers
    .map((g) => ({
      name: g.name,
      value: Math.max(0, g.ccu || 0),
      color: colorScale(Math.max(0, g.ccu || 0)),
      ccu: g.ccu || 0,
      app_id: g.app_id,
      box_art: g.box_art,
      short_description: g.short_description,
      release_date: g.release_date,
      full_price: g.full_price,
      price_value: g.price_value, 
      f2p: g.f2p, 
      developers: g.developers,
      publishers: g.publishers,
      tags: g.tags,
      video: g.video,
    }))

    .filter((c) => c.value > 0);

  const root = d3.hierarchy({ children }).sum((d) => d.value);

  // --- IMPORTANT ---
  // Make the layout a centered square so the circle NEVER clips.
  // We also inset a safe margin so the outer ring + stroke + labels fit.
  const SAFE_INSET = Math.floor(Math.min(w, h) * 0.06); // ~6% inset = no cut-off
  const S = Math.max(1, Math.min(w, h) - SAFE_INSET * 2);
  const x0 = Math.floor((w - S) / 2);
  const y0 = Math.floor((h - S) / 2);

  d3.pack().size([S, S]).padding(1.5)(root);
  const leaves = root.leaves();
  for (const n of leaves) {
    n.x += x0;
    n.y += y0;
  }
  return leaves;
}, [mode, viewMode, visiblePlayers, dimensions, effHeight, colorScale]);

/** Index app_id -> node.data for fast preview lookups */
const nodeIndexRef = useRef(new Map());
useEffect(() => {
  const m = new Map();
  (bubbleNodes || []).forEach((leaf) => {
    const d = leaf?.data;
    if (d?.app_id != null) m.set(String(d.app_id), d);
  });
  nodeIndexRef.current = m;
}, [bubbleNodes]);

  useEffect(() => {
  if (mode !== "players" || viewMode !== "market") return;
  if (!precomputedReady) return;              // ⬅ wait until skeleton is gone
  if (!bubbleSvgRef.current) return;

  const svg = d3.select(bubbleSvgRef.current);

  const zoom = d3
    .zoom()
    .scaleExtent([0.5, 12])
    .on("zoom", ({ transform }) => {
      // keep state + <g id="steam-mm-zoom"> in sync
      requestAnimationFrame(() => setZoomT(transform));
      d3.select("#steam-mm-zoom").attr("transform", transform);
    });

  svg.call(zoom);

  return () => {
    svg.on(".zoom", null);
  };
}, [mode, viewMode, precomputedReady]);

// Warm-up a few images so popovers feel instant
useEffect(() => {
  if (mode !== "players" || viewMode !== "cards") return;
  const list = visiblePlayers.slice(0, 40).map((g) => g.box_art).filter(Boolean);
  list.forEach((src) => { const img = new Image(); img.decoding = "async"; img.src = src; });
}, [mode, viewMode, visiblePlayers]);


// After nodeIndexRef useEffect, before the zoom useEffect
useEffect(() => {
  if (
    mode === "players" &&
    viewMode === "market" &&
    bubbleNodes.length &&
    dimensions.width > 0 &&
    dimensions.height > 0
  ) {
    // At this point we have both a layout and real container size,
    // so it's safe to swap from skeleton to full bubble chart.
    setPrecomputedReady(true);
  }
}, [mode, viewMode, bubbleNodes.length, dimensions.width, dimensions.height]);

  // Should we show the Market Mind skeleton instead of the real bubbles?
  const showMarketSkeleton =
    mode === "players" &&
    viewMode === "market" &&
    (
      !precomputedReady ||
      !bubbleNodes.length ||
      dimensions.width <= 0 ||
      dimensions.height <= 0
    );

// Precompute Market bubble layout while viewing Players -> Cards
useEffect(() => {
  if (mode !== "players" || viewMode !== "cards") return;
  if (!visiblePlayers.length) return;
  if (document.hidden) return;

  const w = mainRef.current?.clientWidth || window.innerWidth;
  const headerH = headerRef.current?.getBoundingClientRect().height || 0;
  const h = Math.max(300, (mainRef.current?.clientHeight || window.innerHeight) - headerH);

  const ids = visiblePlayers.map((g) => g.app_id).join(",");
  const nextKey = `${ids}|${w}x${h}`;

  // If we already have a match, we're done
  if (marketCacheRef.current.key === nextKey && marketCacheRef.current.nodes?.length) {
    setPrecomputedReady(true);
    return;
  }

  setPrecomputedReady(false);

  const handle = rIC((deadline) => {
  // If the browser says we're busy, try again later
  if (deadline && typeof deadline.timeRemaining === "function" && deadline.timeRemaining() < 12) {
    // reschedule lightly
    rIC(() => {}); // noop to yield a tick
    return;
  }
  try {
            const children = visiblePlayers
        .map((g) => ({
          name: g.name,
          value: Math.max(0, g.ccu || 0),
          color: colorScale(Math.max(0, g.ccu || 0)),
          ccu: g.ccu || 0,
          app_id: g.app_id,
          box_art: g.box_art,
          short_description: g.short_description,
          release_date: g.release_date,
          full_price: g.full_price,
          price_value: g.price_value, 
          f2p: g.f2p,   
          developers: g.developers,
          publishers: g.publishers,
          tags: g.tags,
          video: g.video,
        }))

        .filter((c) => c.value > 0);

      const root = d3.hierarchy({ children }).sum((d) => d.value);
      d3.pack().size([w, Math.max(120, h - 60)]).padding(1.5)(root);
      const leaves = root.leaves();

      // Strip off heavy prototype chains; keep only fields we read in Market render
const slim = leaves.map((n) => ({
  x: n.x, y: n.y, r: n.r,
  data: n.data && {
    app_id: n.data.app_id,
    name: n.data.name,
    ccu: n.data.ccu,
    color: n.data.color,
    box_art: n.data.box_art,
    short_description: n.data.short_description,
    release_date: n.data.release_date,
    full_price: n.data.full_price,
    price_value: n.data.price_value, 
    f2p: n.data.f2p,  
    developers: n.data.developers,
    publishers: n.data.publishers,
    tags: n.data.tags,
    video: n.data.video,
  }
}));
marketCacheRef.current = { key: nextKey, nodes: slim };
      setPrecomputedReady(true);
    } catch {
      setPrecomputedReady(false);
    }
  });

  return () => {
    if (window.cancelIdleCallback) {
      try { window.cancelIdleCallback(handle); } catch {}
    }
  };
}, [mode, viewMode, visiblePlayers, colorScale]);

// Background pre-load for FULL Market Mind (all filteredPlayers), not just the on-screen slice.
// Runs only while viewing Players → Cards, only when the tab is visible, and only in idle time.
useEffect(() => {
  if (mode !== "players" || viewMode !== "cards") return;
  if (!filteredPlayers.length) return;
  if (document.hidden) return; // don't do work in a background tab

  // Use the same sizing you use elsewhere so keys match when you switch to Market
  const w = mainRef.current?.clientWidth || window.innerWidth;
  const headerH = headerRef.current?.getBoundingClientRect().height || 0;
  const h = Math.max(300, (mainRef.current?.clientHeight || window.innerHeight) - headerH);

  // Key represents the FULL filtered set at the current layout size
  const ids = filteredPlayers.map((g) => g.app_id).join(",");
  const nextKey = `full:${ids}|${w}x${h}`;

  // If we already have this precompute, bail
  if (marketCacheRef.current.key === nextKey && marketCacheRef.current.nodes?.length) return;

  const handle = rIC((deadline) => {
    // Be polite to the main thread: only run when there's time
    if (deadline && typeof deadline.timeRemaining === "function" && deadline.timeRemaining() < 12) {
      return; // will try again on a later idle tick automatically as effects rerun
    }

    try {
            const children = filteredPlayers
        .map((g) => ({
          name: g.name,
          value: Math.max(0, g.ccu || 0),
          color: colorScale(Math.max(0, g.ccu || 0)),
          ccu: g.ccu || 0,
          app_id: g.app_id,
          box_art: g.box_art,
          short_description: g.short_description,
          release_date: g.release_date,
          full_price: g.full_price,
          price_value: g.price_value, 
          f2p: g.f2p,  
          developers: g.developers,
          publishers: g.publishers,
          tags: g.tags,
          video: g.video,
        }))

        .filter((c) => c.value > 0);

      const root = d3.hierarchy({ children }).sum((d) => d.value);
      d3.pack().size([w, Math.max(120, h - 60)]).padding(1.5)(root);
      const leaves = root.leaves();

      // Store a slim, render-ready copy (no heavy prototype chains)
            const slim = leaves.map((n) => ({
        x: n.x,
        y: n.y,
        r: n.r,
        data: n.data && {
          app_id: n.data.app_id,
          name: n.data.name,
          ccu: n.data.ccu,
          color: n.data.color,
          box_art: n.data.box_art,
          short_description: n.data.short_description,
          release_date: n.data.release_date,
          full_price: n.data.full_price,
          price_value: n.data.price_value, 
          f2p: n.data.f2p, 
          developers: n.data.developers,
          publishers: n.data.publishers,
          tags: n.data.tags,
          video: n.data.video,
        },
      }));


      marketCacheRef.current = { key: nextKey, nodes: slim };
      // no need to setPrecomputedReady here; Market will read from cache when toggled
    } catch {
      // swallow; next idle tick / next dependency change can try again
    }
  });

  return () => {
    if (window.cancelIdleCallback) {
      try { window.cancelIdleCallback(handle); } catch {}
    }
  };
}, [mode, viewMode, filteredPlayers, colorScale]);

  /** Windowed rendering (Followers cards) */
  const FOLLOWERS_INITIAL = 120;
  const FOLLOWERS_BATCH = 80;
  const [followersShown, setFollowersShown] = useState(FOLLOWERS_INITIAL);
  const followersSentinelRef = useRef(null);

  useEffect(() => {
    setFollowersShown(FOLLOWERS_INITIAL);
  }, [
    searchTerm,
    selectedGame,
    selectedTag,
    selectedPublisher,
    followersSort,
    followersDisplayed,
    hiddenFollowers,
    followersLoading,
    mode,
    gamesFilter,
    minFollowers,
  ]);

  useEffect(() => {
    if (mode !== "followers") return;
    if (!followersSentinelRef.current) return;
    const el = followersSentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setFollowersShown((prev) =>
            prev >= sortedFollowers.length
              ? prev
              : Math.min(sortedFollowers.length, prev + FOLLOWERS_BATCH)
          );
        }
      },
      { root: null, rootMargin: "800px 0px 800px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sortedFollowers.length, mode]);

  const visibleFollowers = useMemo(
    () => sortedFollowers.slice(0, followersShown),
    [sortedFollowers, followersShown]
  );
  const hasMoreFollowers =
    mode === "followers" && followersShown < sortedFollowers.length;

  /** Disable page scroll in Market/Effervescent (Players mode only) */
  useEffect(() => {
    const lock = mode === "players" && viewMode !== "cards";
    const { body, documentElement } = document;
    if (lock) {
      body.style.overflow = "hidden";
      documentElement.style.overflow = "hidden";
      body.style.height = "100vh";
      documentElement.style.height = "100vh";
    } else {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    }
    return () => {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    };
  }, [viewMode, mode]);

// Disable "Released This Week" when in Followers + Unreleased
const releasedThisWeekDisabled = mode === "followers" && gamesFilter === "unreleased";

const handleModeChange = (nextMode) => {
  if (nextMode === mode) return;

  // Full reset so nothing from the other tab (search text, dropdown filters,
  // or pinned compare-chart cards) leaks into the tab we're switching to.
  setSelectedGame(null);
  setGameQuery("");
  setSearchTerm("");
  setSelectedTag(null);
  setTagQuery("");
  setSelectedPrimaryTag(null);
  setPrimaryTagQuery("");
  setSelectedPublisher(null);
  setPubQuery("");
  setSelectedDevelopers(null);
  setDevQuery("");
  setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
  setSelectedDateRange([null, null]);
  setHiddenGames([]);
  setHiddenFollowers([]);
  setDisplayedGames(games);
  setFollowersDisplayed(followersGames);
  setIsReleasedThisWeek(false);
  setChartGames([]);
  setHighlightedSeries(null);
  setMode(nextMode);
};

// Show "Online" only when the Players dataset is 100% unfiltered/default
const showOnlineStat = useMemo(() => {
  if (mode !== "players") return false; // Followers doesn't show this stat anyway
  const noTextSearch = (searchTerm || "").trim() === "";
  const noGame = !selectedGame || (Array.isArray(selectedGame) && selectedGame.length === 0);
  const noAllTags = !selectedTag || (Array.isArray(selectedTag) && selectedTag.length === 0);
  const noPrimaryTags = !selectedPrimaryTag || (Array.isArray(selectedPrimaryTag) && selectedPrimaryTag.length === 0);
  const noPublisher = !selectedPublisher || (Array.isArray(selectedPublisher) && selectedPublisher.length === 0);
  const noDevelopers = !selectedDevelopers || (Array.isArray(selectedDevelopers) && selectedDevelopers.length === 0);
  const noDateRange = !selectedDateRange?.[0] && !selectedDateRange?.[1];

  // PRICE: treat "all selected" OR "none selected" as *no price filter*
  const noPriceRange =
    !Array.isArray(selectedPriceRange) ||
    selectedPriceRange.length === PRICE_TIERS.length ||
    selectedPriceRange.length === 0;

  const noHidden = hiddenGames.length === 0;
  const notWeekOnly = !isReleasedThisWeek;

  // AND: the working set is the original "games" array (not a filtered subset)
  const usingFullSet = displayedGames === games;

  return (
    noTextSearch &&
    noGame &&
    noAllTags &&
    noPrimaryTags &&
    noPublisher &&
    noDevelopers &&
    noDateRange &&
    noPriceRange &&
    noHidden &&
    notWeekOnly &&
    usingFullSet
  );
}, [
  mode,
  searchTerm,
  selectedGame,
  selectedTag,
  selectedPrimaryTag,
  selectedPublisher,
  selectedDevelopers,
  selectedDateRange,
  selectedPriceRange,
  hiddenGames.length,
  isReleasedThisWeek,
  displayedGames,
  games,
]);

const [refreshReady, setRefreshReady] = useState(false);

const REFRESH_COOLDOWN_MS = 10 * 60 * 1000;
const refreshTimerRef = useRef(null);

const armRefreshCooldown = () => {
  setRefreshReady(false);

  if (refreshTimerRef.current) {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;
  }

  refreshTimerRef.current = setTimeout(() => {
    setRefreshReady(true);
    refreshTimerRef.current = null;
  }, REFRESH_COOLDOWN_MS);
};

useEffect(() => {
  armRefreshCooldown(); // start hidden, show after 10m
  return () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Auto-refresh: run the same logic as the button every 5 minutes
useEffect(() => {
  const FIVE_MIN = 5 * 60 * 1000;

  const id = setInterval(() => {
    // Don't spam refreshes if the tab isn't visible
    if (document.hidden) return;

    // Only auto-refresh the live Players view; Followers is based on CSV snapshots
    if (mode === "players") {
      refreshSteamData();
    }
  }, FIVE_MIN);

  return () => clearInterval(id);
  // deps: keep closure "fresh" but not crazy noisy
}, [mode, refreshSteamData]);


  const statNumberStyle = {
    color: "#fff",
    fontWeight: 900,
    fontSize: "1.35rem",
    lineHeight: 1,
  };

// Only show backdrop when exactly one game is selected
const chartBgArt =
  chartGames.length === 1 ? chartGames[0]?.metadata?.box_art || "" : "";

  const showChartBgArt = !!chartBgArt;

  // Build per-game chart rows, with optional Followers Daily diff mode
 const buildChartRowsForGame = (g) => {
  const id = g?.metadata?.app_id;
  if (!id) return [];

  // -------- Followers mode (dataMode is "daily" for followers) --------
  if (mode === "followers") {
    const raw = Array.isArray(g.daily) ? g.daily : [];
    if (!raw.length) return [];

    // 1) Total = last data point of each UTC day (one point per day)
if (followersValueMode === "total") {
  const { byDay, days } = bucketFirstLastPerUtcDay(raw);

  return days.map((day) => {
    const d = byDay.get(day);
    return { x: startOfDayUTCISO(day), [id]: d.lastV };
  });
}


  // 2) Daily = day-over-day delta, BUT:
// - if the first-ever dt is on/after 2025-10-01, treat Day 1 as baseline=0 → diff = lastV
// - otherwise (legacy tracking), Day 1 uses intra-day delta → diff = lastV - firstV
const { byDay, days } = bucketFirstLastPerUtcDay(raw);

const firstSeenTs = raw?.[0]?.dt ? new Date(raw[0].dt).getTime() : NaN;
const cutoffTs = Date.parse("2025-10-01T00:00:00.000Z");
const isNewish = Number.isFinite(firstSeenTs) && firstSeenTs >= cutoffTs;

let prevLast = null;

return days.map((day, idx) => {
  const d = byDay.get(day);
  const lastV = Number(d?.lastV);
  const firstV = Number(d?.firstV);

  let diff = 0;

  if (idx === 0) {
    diff = isNewish ? lastV : (lastV - firstV);
  } else {
    diff = prevLast == null ? 0 : (lastV - prevLast);
  }

  prevLast = lastV;

  return { x: startOfDayUTCISO(day), [id]: diff };
});


  }

  // -------- Players mode (unchanged) --------
  return toChartRows(g, dataMode, viewByDaysSinceLaunch);
};



  // merged CCU chart data for Player History compare
  const mergedData = useMemo(() => {
    const bucket = {};
    const src = chartGames.filter(Boolean);
    if (!src.length) return [];

    const _xToNumber = (x) => {
      if (dataMode === "live") return new Date(x).getTime();
      if (viewByDaysSinceLaunch) return Number(x);
      return new Date(x).getTime();
    };

    const lastXPerId = new Map();

    src.forEach((g) => {
      const rows = buildChartRowsForGame(g);
      let maxX = -Infinity;

      rows.forEach((pt) => {
        const id = g.metadata.app_id;
        const xKey = pt.x;
        const v = pt[id];

        if (!bucket[xKey]) bucket[xKey] = { x: xKey };

        const prev = bucket[xKey][id];
        const nv =
          prev == null ? v : Math.max(Number(prev) || 0, Number(v) || 0);
        bucket[xKey][id] = nv;

        const xn = _xToNumber(xKey);
        if (Number.isFinite(xn) && xn > maxX) maxX = xn;
      });

      lastXPerId.set(String(g.metadata.app_id), maxX);
    });

    const arr = Object.values(bucket);
    if (dataMode === "live") {
      arr.sort((a, b) => new Date(a.x) - new Date(b.x));
    } else {
      arr.sort((a, b) =>
        viewByDaysSinceLaunch
          ? parseInt(a.x, 10) - parseInt(b.x, 10)
          : new Date(a.x) - new Date(b.x)
      );
    }

    const ids = src.map((g) => String(g.metadata.app_id));
    const lastVal = Object.create(null);

    for (const row of arr) {
      const rowXn =
        dataMode === "live" || !viewByDaysSinceLaunch
          ? new Date(row.x).getTime()
          : Number(row.x);
      for (const id of ids) {
        const v = row[id];
        if (v == null || !Number.isFinite(Number(v))) {
          const cutoff = lastXPerId.get(id);
          if (lastVal[id] != null && cutoff != null && rowXn <= cutoff) {
            row[id] = lastVal[id];
          }
        } else {
          lastVal[id] = Number(v);
        }
      }
    }

    return arr;
  },  [chartGames,  dataMode,  viewByDaysSinceLaunch,  followersValueMode,  mode]);

  // windowed renderData
  const renderData = useMemo(() => {
    if (!mergedData || mergedData.length === 0) return [];
    const s = Math.max(0, Math.min(winStartIdx, mergedData.length - 1));
    const e = Math.max(s, Math.min(winEndIdx, mergedData.length - 1));
    return mergedData.slice(s, e + 1);
  }, [mergedData, winStartIdx, winEndIdx]);

  const chartTicks = useMemo(() => chartAxisTicks(renderData), [renderData]);

  // Daily Users (Estimated) for single-game Live (7d) view
  const dailyUsersEstimate = useMemo(() => {
  if (dataMode !== "live") return null;
  if (chartGames.length !== 1) return null;

  const g = chartGames[0];
  if (!Array.isArray(g.live) || g.live.length < 2) return null;

  // 🔹 Dynamic hours per user based on CCU shape + days since launch
  const { lowHours, highHours } = getDynamicSessionHoursForGame(g);

  return estimateDailyUsersFromLive(g.live, lowHours, highHours);
}, [dataMode, chartGames]);


  useEffect(() => {
    if (!mergedData || mergedData.length === 0) return;
    if (!hasInitWindow.current) {
      setWinStartIdx(0);
      setWinEndIdx(mergedData.length - 1);
      hasInitWindow.current = true;
    } else {
      setWinStartIdx((prev) =>
        Math.max(0, Math.min(prev, Math.max(0, mergedData.length - 2)))
      );
      setWinEndIdx((prev) =>
        Math.max(1, Math.min(prev, Math.max(1, mergedData.length - 1)))
      );
    }
  }, [mergedData.length]);

  useEffect(() => {
    if (mergedData && mergedData.length > 0) {
      setWinStartIdx(0);
      setWinEndIdx(mergedData.length - 1);
    }
  }, [dataMode, mergedData.length]);

  const labelForIdx = (idx) => {
  if (!mergedData || mergedData.length === 0) return "";
  const x = mergedData[Math.min(Math.max(idx, 0), mergedData.length - 1)].x;

  if (dataMode === "live") return fmtDateTimeUTC(x);
if (viewByDaysSinceLaunch) return String(x);
return fmtDateShortUTC(x);

};


// Match mindSHARE chart palette (blue, beige, orange, magenta, soft blue, green)
const STEAM_CHART_COLORS = [
  "#3fa9f5", // mindGAME blue
 "#F5E6C8", // beige
  "#ff9b70", // orange
 "#33c3eb", // turquoise
  "#f1726a",//red orange
 "#66CCFF", // soft blue
];

const seriesColor = (i) =>
  STEAM_CHART_COLORS[i % STEAM_CHART_COLORS.length];

// Which series (if any) should animate on add
const [animSeriesId, setAnimSeriesId] = useState(null);

// Track whether multi-select modifier (Ctrl/Cmd) is held down
const [multiKeyDown, setMultiKeyDown] = useState(false);

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === "Control" || e.key === "Meta") {
      setMultiKeyDown(true);
    }
  };

  const handleKeyUp = (e) => {
    if (e.key === "Control" || e.key === "Meta") {
      setMultiKeyDown(false);
    }
  };

  const handleBlur = () => {
    // If the window loses focus, reset
    setMultiKeyDown(false);
  };

  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("keyup", handleKeyUp);
  window.addEventListener("blur", handleBlur);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("keyup", handleKeyUp);
    window.removeEventListener("blur", handleBlur);
  };
}, []);


// Add a game from Steam cards into the compare chart
const handleAddToChart = async (game) => {
  if (!game?.app_id) return;
  const appId = String(game.app_id);

  // Already in chart? no-op
  if (chartGames.some((g) => String(g.metadata.app_id) === appId)) return;

  // Shared metadata for both Players and Followers charts
  const metadataForChart = {
    app_id: appId,
    name: game.name,
    box_art: game.box_art,
    release_date: game.release_date,
    full_price: game.full_price,
  };


  // 1) Optimistic placeholder so user sees the chip instantly
  setChartGames((prev) => [
    ...prev,
    { metadata: metadataForChart, live: [], daily: [], loading: true },
  ]);

  try {
    let live = [];
    let daily = [];

    if (mode === "players") {
      // ⚽ existing CCU logic (unchanged)
      [live, daily] = await Promise.all([
        loadLiveSeries(appId),
        loadDailySeries(appId),
      ]);
    } else {
      // 👥 Followers mode: last 90 days Followers, daily series only
      daily = await loadFollowersSeries(appId, 90);
      live = []; // no live followers series
    }

    // 2) Hydrate that entry with real data
    setChartGames((prev) =>
      prev.map((g) =>
        String(g.metadata.app_id) === appId
          ? { ...g, live, daily, loading: false }
          : g
      )
    );

    // 3) Pulse-animate this series line once
    setAnimSeriesId(appId);
    setTimeout(() => {
      setAnimSeriesId((current) => (current === appId ? null : current));
    }, 800);
  } catch (e) {
    console.error("❌ Failed to load history for chart:", e);
    setChartGames((prev) =>
      prev.map((g) =>
        String(g.metadata.app_id) === appId
          ? { ...g, loading: false }
          : g
      )
    );
  }
};

  const handleRemoveFromChart = (appId) => {
    const idStr = String(appId);
    setChartGames((prev) =>
      prev.filter((g) => String(g.metadata.app_id) !== idStr)
    );
  };

  /** Video embed helper (shared) */
  const getVideoEmbed = (raw) => {
    if (!raw) return null;
    const url = raw.trim();
    const lower = url.toLowerCase();

    if (/\.(mp4|webm|ogg)(\?|#|$)/.test(lower)) {
      return { type: "file", src: url };
    }
    const yt = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/
    );
    if (yt) {
      const id = yt[1];
      return {
        type: "youtube",
        src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&showinfo=0&loop=1&playlist=${id}`,
      };
    }
    return null;
  };

  const [hoveredCards, setHoveredCards] = useState({});
  const cardRefs = useRef({});

  const handleMouseMove = (e, appId) => {
    const card = cardRefs.current[appId];
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = (x / rect.width) * 2 - 1;
    const py = (y / rect.height) * 2 - 1;
    const rotateY = px * 4;
    const rotateX = -py * 4;

    card.style.setProperty("--rx", `${rotateX}deg`);
    card.style.setProperty("--ry", `${rotateY}deg`);
    card.style.setProperty("--sx", `${(x / rect.width) * 100}%`);
    card.style.setProperty("--sy", `${(y / rect.height) * 100}%`);
  };

  const handleMouseLeave = (appId) => {
    const card = cardRefs.current[appId];
    if (!card) return;

    card.style.transition = `transform 0.5s ease, box-shadow 0.5s ease`;
    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
    card.style.setProperty("--sx", "50%");
    card.style.setProperty("--sy", "50%");

    setTimeout(() => {
      if (cardRefs.current[appId]) {
        cardRefs.current[appId].style.transition =
          "transform 140ms ease, box-shadow 140ms ease";
      }
    }, 500);
  };

  const getShineStyle = (hovered, card) => {
    if (!card) return {};
    const style = {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      borderRadius: "14px",
      opacity: hovered ? 1 : 0,
      transition: "opacity 150ms ease",
      background: `radial-gradient(600px at var(--sx,50%) var(--sy,50%), rgba(255,255,255,0.18), transparent 40%), 
                 linear-gradient(120deg, rgba(255,255,255,0.12), rgba(63,169,245,0.12) 20%, transparent 60%)`,
      mixBlendMode: "screen",
      zIndex: 3,
    };
    return style;
  };



  // Effervescent hover + reset on filter changes
  const [effHover, setEffHover] = useState(null);
  useEffect(() => {
    if (mode === "players" && viewMode === "effervescent") {
      setEffHover(null);
      setStreamNonce((n) => n + 1);
    }
  }, [viewMode, mode]);

  const effKey = useMemo(() => {
    const ids = visiblePlayers.map((g) => g.app_id).join(",");
    return `eff:${ids}:${streamNonce}`;
  }, [visiblePlayers, streamNonce]);

  const effContainerRef = useRef(null);

  /** If user switches to Followers, force viewMode to cards (Followers are card-only) */
  useEffect(() => {
    if (mode === "followers" && viewMode !== "cards") setViewMode("cards");
  }, [mode, viewMode]);

// When switching between Players and Followers, reset the compare chart
useEffect(() => {
  // 🔒 hard lock: don't wipe while a restore/apply is in progress
  if (restoringRef.current) return;

  setChartGames([]);
  setHighlightedSeries(null);
  setAnimSeriesId(null);
  hasInitWindow.current = false;
  setWinStartIdx(0);
  setWinEndIdx(0);

  if (mode === "followers") {
    setDataMode("daily");
    setViewByDaysSinceLaunch(false);
    setShowRegionBands(false);
  }
}, [mode]);


// Double-safeguard: if Followers pill is Unreleased, ensure the week filter is OFF (and data reset)
useEffect(() => {
  if (mode === "followers" && gamesFilter === "unreleased" && isReleasedThisWeek) {
    setIsReleasedThisWeek(false);
    setFollowersDisplayed(followersGames);
  }
}, [mode, gamesFilter, isReleasedThisWeek, followersGames]);

  /* ============================
     UI
     ============================ */
    return (
    <div
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: "100vh",
      }}
    >
{/* ✅ COPY TOAST: put it right here */}
    {copiedToast && (
      <div
        style={{
          position: "fixed",
          left: "50%",
          bottom: 18,
          transform: "translateX(-50%)",
          zIndex: 99999,
          background: "rgba(0,0,0,0.80)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 700,
          pointerEvents: "none",
          boxShadow: "0 12px 30px rgba(0,0,0,0.65)",
          backdropFilter: "blur(6px)",
          maxWidth: "90vw",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {copiedToast.text}
      </div>
    )}

      {/* Sidebar */}
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "100dvh",
          overflowY: "auto",
          padding: 20,
          background: "#111",
          borderRight: "1px solid #333",
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          zIndex: 2400,
        }}
      >

        {/* NEW: Players | Followers pill toggle */}
        <div
          role="tablist"
          aria-label="Players or Followers"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            background: "transparent",
            border: "1px solid #333",
            borderRadius: 999,
            padding: 4,
            marginBottom: 6,
          }}
        >
          <button
            role="tab"
            aria-selected={mode === "players"}
            onClick={() => handleModeChange("players")}
            style={{
              border: "none",
              borderRadius: 999,
              padding: "8px 10px",
              fontWeight: 800,
              cursor: "pointer",
              background: mode === "players" ? "#3fa9f5" : "transparent",
              color: mode === "players" ? "#000" : "#ddd",
              transition: "background 0.15s ease",
            }}
          >
            Players
          </button>
          <button
            role="tab"
            aria-selected={mode === "followers"}
            onClick={() => handleModeChange("followers")}
            style={{
              border: "none",
              borderRadius: 999,
              padding: "8px 10px",
              fontWeight: 800,
              cursor: "pointer",
              background: mode === "followers" ? "#3fa9f5" : "transparent",
              color: mode === "followers" ? "#000" : "#ddd",
              transition: "background 0.15s ease",
            }}
          >
            Followers
          </button>
        </div>

        <SavedViewsManager
  key={savedViewsKey}
  type="steam"
  currentFilters={{
    searchTerm,
    tag: selectedTag,
    primaryTag: selectedPrimaryTag,
    game: null,
    publisher: selectedPublisher,
    developer: selectedDevelopers,
    priceRange: selectedPriceRange,
    dateRange: selectedDateRange,
    viewMode,
    pageMode: mode,
    gamesFilter,
    releasedThisWeek: isReleasedThisWeek,
    mgMissingById,
    mgMissingByName,
    excludeAdultPrimary,
    chart: {
      ids: (chartGames || []).map((g) => String(g?.metadata?.app_id)).filter(Boolean),
      dataMode,
      viewByDaysSinceLaunch,
      showRegionBands,
      followersValueMode,
      highlightedSeries,
      winStartIdx,
      winEndIdx,
    },
  }}

         onApplyFilters={(filters) => {
  // 🔒 lock immediately (synchronous)
  restoringRef.current = true;
  restoreTokenRef.current += 1;

  setSearchTerm(filters.searchTerm ?? "");
  setSelectedGame(null);
  setSelectedTag(filters.tag ?? null);
  setSelectedPrimaryTag(filters.primaryTag ?? null);
  setSelectedPublisher(filters.publisher ?? null);
  setSelectedDevelopers(filters.developer ?? null);
  setMgMissingById(!!filters.mgMissingById);
  setMgMissingByName(!!filters.mgMissingByName);
  setExcludeAdultPrimary(!!filters.excludeAdultPrimary);


  const incomingPrice = filters.priceRange;
  if (Array.isArray(incomingPrice) && incomingPrice.every((id) => typeof id === "string")) {
    setSelectedPriceRange(incomingPrice);
  } else {
    setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
  }

  const dateRange = filters.dateRange ?? [null, null];
  if (dateRange[0] && typeof dateRange[0] === "string") dateRange[0] = new Date(dateRange[0]);
  if (dateRange[1] && typeof dateRange[1] === "string") dateRange[1] = new Date(dateRange[1]);
  setSelectedDateRange(dateRange);

const hasChartIds =
  Array.isArray(filters?.chart?.ids) && filters.chart.ids.length > 0;

// ✅ If the saved view includes a chart, force Cards so the chart UI exists.
setViewMode(hasChartIds ? "cards" : (filters.viewMode ?? "cards"));
setMode(filters.pageMode ?? "players");

setGamesFilter(filters.gamesFilter ?? "all");

// ✅ Apply Released This Week from saved view
const nextPageMode = filters.pageMode ?? "players";
const nextGamesFilter = filters.gamesFilter ?? "all";

// Disallow week filter when Followers + Unreleased (since button is disabled there)
const wantWeek = !!filters.releasedThisWeek;
const weekAllowed = !(nextPageMode === "followers" && nextGamesFilter === "unreleased");
const appliedWeek = weekAllowed ? wantWeek : false;

setIsReleasedThisWeek(appliedWeek);

if (appliedWeek) {
  if (nextPageMode === "players") {
    setDisplayedGames(games.filter((g) => withinLastNDays(g.release_date, 7)));
    setFollowersDisplayed(followersGames); // keep followers base set
  } else {
    setFollowersDisplayed(followersGames.filter((g) => withinLastNDays(g.release_date, 7)));
    setDisplayedGames(games); // keep players base set
  }
} else {
  setDisplayedGames(games);
  setFollowersDisplayed(followersGames);
}


if ((filters.pageMode ?? "players") === "followers") {
  setFollowersValueMode(filters?.chart?.followersValueMode ?? "total");
}

  // queue chart restore
  setPendingChartRestore(filters.chart ?? null);
}}


onClearFilters={() => {
  // 1) Clear ALL selections (no active filters)
  setSelectedGame(null);
  setSelectedTag(null);
  setSelectedPublisher(null);
  setSelectedDevelopers(null);
  setMgMissingById(false);
  setMgMissingByName(false);
  setExcludeAdultPrimary(false);
  restoreTokenRef.current += 1;
  setChartGames([]);
     setPendingChartRestore(null);
      restoringRef.current = false;
  setHighlightedSeries(null);
  setAnimSeriesId(null);
  hasInitWindow.current = false;
  setWinStartIdx(0);
  setWinEndIdx(0);
     setFollowersValueMode("total");
  setDataMode("live");
  setViewByDaysSinceLaunch(false);
  setShowRegionBands(false);



  // Price/date: set to "no filter" states (so the blocks don't run)
      setSelectedPriceRange(PRICE_TIERS.map((t) => t.id));
  setSelectedDateRange([null, null]);     // <— IMPORTANT (don’t pass releaseDateRange)


  // 2) Restore full datasets & hidden states
  setHiddenGames([]);
  setHiddenFollowers([]);
  setDisplayedGames(games);               // back to full
  setFollowersDisplayed(followersGames);  // back to full

  // 3) Reset misc UI state that can affect counts
  setIsReleasedThisWeek(false);
  setSearchTerm("");
  setViewMode("cards");
  setMode("players");
}}
        />

        {/* Shared filters */}
        <TextInputField
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => {
            setSelectedGame(null);
            setSearchTerm(e.target.value);
          }}
        />
        <VirtualizedDropdown
  placeholder="All Games"
previewType="game"
onPreview={(type, value) => applyPreview({ type, value })}
onMenuClose={() => {
  applyPreview(null);
}}

  value={null}
  onChange={(opts, meta) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedGame(null);
      setSearchTerm("");
    } else {
      const nextOptions = Array.isArray(opts) ? opts : [opts];
      const nextSearch = nextOptions.map(getGameOptionName).filter(Boolean).join(" ");
      setSelectedGame(null);
      setSearchTerm(nextSearch);
    }
  }}
  options={mode === "followers" ? followersOptionsByFollowers : gamesOptionsByCCU}
  isMulti={false}
  closeMenuOnSelect
  blurInputOnSelect={false}
  hideSelectedOptions={false}

  inputValue={searchTerm}
  onInputChange={(val, meta) => {
    // Only update for actual typing
    if (meta?.action === "input-change") {
      setSelectedGame(null);
      setSearchTerm(val ?? "");
    }
    // ignore 'set-value', 'menu-close', 'input-blur', etc.
  }}

  maxMenuWidth={340}
/>

<VirtualizedDropdown
  placeholder="Primary Tags"
  /* highlighter restricted to first-5 tags */
  previewType="primaryTag"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}

  /* MULTI selection (same UX as All Tags) */
  value={
    Array.isArray(selectedPrimaryTag)
      ? selectedPrimaryTag
      : selectedPrimaryTag
      ? [selectedPrimaryTag]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedPrimaryTag(null);
    } else {
      setSelectedPrimaryTag(Array.isArray(opts) ? opts : [opts]);
    }
  }}

  /* Simple alphabetized list from first-5 tags only */
  options={primaryTagsAlphaOptions}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

  /* keep the input text stable while selecting */
  inputValue={primaryTagQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") {
      setPrimaryTagQuery(val ?? "");
    }
  }}

  maxMenuWidth={340}
/>

<VirtualizedDropdown
  placeholder="All Tags"
  /* NEW: live highlight while hovering tag options */
  previewType="tag"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedTag)
      ? selectedTag
      : selectedTag
      ? [selectedTag]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedTag(null);
    } else {
      setSelectedTag(Array.isArray(opts) ? opts : [opts]);
    }
    // keep typed text intact
  }}
  options={tagsOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

  /* keep the input text stable across selections */
  inputValue={tagQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") {
      setTagQuery(val ?? "");
    }
  }}

  maxMenuWidth={340}
/>


<VirtualizedDropdown
  placeholder="All Publishers"

/* NEW: live highlight while hovering publisher options */
  previewType="publisher"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedPublisher)
      ? selectedPublisher
      : selectedPublisher
      ? [selectedPublisher]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedPublisher(null);
    } else {
      setSelectedPublisher(Array.isArray(opts) ? opts : [opts]);
    }
  }}

  options={(pubQuery || "").length >= 2 ? publisherOptions : publisherOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

/* keep the fast-index build and typed text behavior */
  onMenuOpen={onPublishersMenuOpen}
  inputValue={pubQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") setPubQuery(val ?? "");
  }}

  maxMenuWidth={340}
/>


<VirtualizedDropdown
  placeholder="All Developers"

/* NEW: live highlight while hovering developer options */
  previewType="developer"
  onPreview={(type, value) => applyPreview({ type, value })}
  onMenuClose={() => applyPreview(null)}

  /* MULTI value shape */
  value={
    Array.isArray(selectedDevelopers)
      ? selectedDevelopers
      : selectedDevelopers
      ? [selectedDevelopers]
      : []
  }
  onChange={(opts) => {
    if (!opts || (Array.isArray(opts) && opts.length === 0)) {
      setSelectedDevelopers(null);
    } else {
      setSelectedDevelopers(Array.isArray(opts) ? opts : [opts]);
    }
  }}

  options={(devQuery || "").length >= 2 ? developerOptions : developerOptionsByCCU}
  isMulti
  closeMenuOnSelect={false}
  blurInputOnSelect={false}
  hideSelectedOptions={false}

/* keep typed text intact */
  inputValue={devQuery}
  onInputChange={(val, meta) => {
    if (meta?.action === "input-change") setDevQuery(val ?? "");
  }}

  maxMenuWidth={340}
/>


                {/* Price goes directly under Developers, same spacing as dropdowns */}
<PriceSlider
  tiers={PRICE_TIERS}
  selected={selectedPriceRange}
  onChange={setSelectedPriceRange}
/>

{/* Release Date – pull it up slightly to cancel its internal top margin */}
<div style={{ marginTop: -10 }}>
  <DateSlider
    range={releaseDateRange}
    selected={selectedDateRange}
    onChange={setSelectedDateRange}
    onClear={() => setSelectedDateRange([null, null])}
  />
</div>


        {/* Released This Week */}
        <button
          disabled={releasedThisWeekDisabled}
          onClick={() => {
            if (releasedThisWeekDisabled) return;

            if (isReleasedThisWeek) {
              setDisplayedGames(games);
              setFollowersDisplayed(followersGames);
              setIsReleasedThisWeek(false);
            } else {
              if (mode === "players") {
                handleReleasedThisWeekPlayers();
              } else {
                handleReleasedThisWeekFollowers();
              }
              setIsReleasedThisWeek(true);
            }
          }}
          title={
            releasedThisWeekDisabled
              ? "Disabled on Followers → Unreleased (released games are never unreleased)"
              : undefined
          }
          style={{
            width: "100%",
            minHeight: 36,
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid",
            boxSizing: "border-box",
            fontWeight: "normal",

            background: releasedThisWeekDisabled
              ? "#222"
              : isReleasedThisWeek
              ? "#3fa9f5"
              : "#333",
            borderColor: releasedThisWeekDisabled
              ? "#333"
              : isReleasedThisWeek
              ? "#3fa9f5"
              : "#555",
            color: releasedThisWeekDisabled
              ? "#666"
              : isReleasedThisWeek
              ? "#000"
              : "#fff",
            cursor: releasedThisWeekDisabled ? "not-allowed" : "pointer",
            opacity: releasedThisWeekDisabled ? 0.6 : 1,
          }}
        >
          Released This Week
        </button>


      {/* Mode-specific sidebar actions */}
{mode === "players" ? (
  <>
    <button
      onClick={handleResetPlayers}
      style={{
        width: "100%",
        minHeight: 36,
        background: "#333",
        border: "1px solid #555",
        borderRadius: 8,
        padding: "8px 12px",
        color: "#fff",
        cursor: "pointer",
      }}
    >
      ↻ Reset Games
    </button>

    <CsvExportButton
      onClick={handleExportPlayers}
      style={{
        width: "100%",
        minHeight: 36,
        background: "#333",
        border: "1px solid #555",
        boxSizing: "border-box",
        borderRadius: 8,
        padding: "8px 12px",
        color: "#fff",
        cursor: "pointer",
      }}
    >
      ⬇ Export CSV
    </CsvExportButton>

    {/* ✅ ADD THESE BELOW Export CSV (Players too) */}
  </>
) : (
  <>
    <button
      onClick={handleResetFollowers}
      style={{
        width: "100%",
        minHeight: 36,
        background: "#333",
        border: "1px solid #555",
        borderRadius: 8,
        padding: "8px 12px",
        color: "#fff",
        cursor: "pointer",
      }}
    >
      ↻ Reset Games
    </button>

    <CsvExportButton
      onClick={handleExportFollowers}
      style={{
        width: "100%",
        minHeight: 36,
        background: "#333",
        border: "1px solid #555",
        boxSizing: "border-box",
        borderRadius: 8,
        padding: "8px 12px",
        color: "#fff",
        cursor: "pointer",
      }}
    >
      ⬇ Export CSV
    </CsvExportButton>

  </>
)}

    {/* ✅ Keep them below Export CSV (Followers too) */}
    {MgMissingButtons}

{/* Debug thresholds (Players/Follows) */}
<div style={{ marginTop: 6 }}>
  <div style={{ fontSize: 11, color: "#aaa", fontWeight: 700, marginBottom: 4 }}>
    Minimum Players (CCU)
  </div>
  <input
    value={minPlayersRaw}
    onChange={(e) => setMinPlayersRaw(e.target.value)}
    placeholder="e.g. 500"
    inputMode="numeric"
    style={{
      width: "100%",
      height: 36,
      borderRadius: 8,
      border: "1px solid #555",
      background: "#0b0b0b",
      color: "#fff",
      padding: "0 10px",
      boxSizing: "border-box",
      outline: "none",
    }}
  />

  <div
    style={{
      fontSize: 11,
      color: "#aaa",
      fontWeight: 700,
      marginTop: 10,
      marginBottom: 4,
    }}
  >
    Minimum Followers
  </div>
  <input
    value={minFollowersRaw}
    onChange={(e) => setMinFollowersRaw(e.target.value)}
    placeholder="e.g. 10000"
    inputMode="numeric"
    style={{
      width: "100%",
      height: 36,
      borderRadius: 8,
      border: "1px solid #555",
      background: "#0b0b0b",
      color: "#fff",
      padding: "0 10px",
      boxSizing: "border-box",
      outline: "none",
    }}
  />
</div>
      </div>



      {/* Main */}
      <div
  ref={mainRef}
  style={{
    flex: 1,
    padding: mode === "players" && viewMode !== "cards" ? "20px 20px 0 20px" : 20, // bottom=0 when Market/Bubbles
    position: "relative",
    height: mode === "players" && viewMode !== "cards" ? "100svh" : "auto",       // svh avoids OS/UI chrome mismatch
    overflow: mode === "players" && viewMode !== "cards" ? "hidden" : "visible",
  }}
>
        {/* Header stats */}
<div
  ref={headerRef}
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 10,
    width: "100%",
    position: "relative",   // NEW
          zIndex: 5,               // NEW (header sits above bubbles)
  }}
>
  {mode === "players" ? (
    <>
      {/* Left: Players stats */}
    <PlayersHeader
  steamTotalOnline={steamTotalOnline}
  totalCcu={headerTotalCcu}
  playersShareOfTotal={headerPlayersShareDisplay}
  filteredPlayers={filteredPlayers}
  statNumberStyle={statNumberStyle}
  showOnline={showOnlineStat && !inChartMode}
  onRefresh={handleQuickRefresh}
  refreshReady={refreshReady}
  chartMode={inChartMode}
  mgMissingById={mgMissingById}
  mgMissingByName={mgMissingByName}
  mgMetaLoaded={mgMetaLoaded}

/>



  {/* Right: Players view pills (Cards | Market | Effervescent) */}
  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
    <div
      role="tablist"
      aria-label="Players view"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 6,
        background: "transparent",
        border: "1px solid #333",
        borderRadius: 999,
        padding: 4,
        minWidth: 260,
      }}
    >
      <button
        role="tab"
        aria-selected={viewMode === "cards"}
        onClick={() => setViewMode("cards")}
        style={{
          ...pillBase,
          background: viewMode === "cards" ? "#3fa9f5" : "transparent",
          color: viewMode === "cards" ? "#000" : "#ddd",
          whiteSpace: "nowrap",
        }}
      >
        Cards
      </button>
      <button
        role="tab"
        aria-selected={viewMode === "market"}
        onClick={() => setViewMode("market")}
        style={{
          ...pillBase,
          background: viewMode === "market" ? "#3fa9f5" : "transparent",
          color: viewMode === "market" ? "#000" : "#ddd",
          whiteSpace: "nowrap",
        }}
      >
        Market Mind
      </button>
        </div>
      </div>
    </>
   ) : (
    <FollowersHeader
      filteredFollowers={filteredFollowers}
      followersTotal={headerFollowersTotal}   // 👈 chart-aware total
      gamesFilter={gamesFilter}
      setGamesFilter={setGamesFilter}
      followersSort={followersSort}
      setFollowersSort={setFollowersSort}
      statNumberStyle={statNumberStyle}
      chartMode={inFollowersChartMode}        // 👈 tell header to switch layout
    />
  )}
</div>



        {/* Player History compare chart (only Players / Cards) */}
        {viewMode === "cards" && chartGames.length > 0 && (
          <div style={{ marginBottom: 20 }}>
              <>
 

               {/* Chart itself */}
        <div style={{ paddingRight: 0 }}>
          <div
            style={{
              position: "relative",
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid #222",
              background: "#050505",
            }}
          >
            {/* Background box art + heavy dark overlay */}
           {showChartBgArt && (
  <>
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${chartBgArt})`,
        backgroundSize: "cover",
        backgroundPosition: "top center",
        transform: "scale(1.00)",
        filter: "saturate(130%) brightness(1.3) contrast(1.05)",
        opacity: 0.2,
      }}
    />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "radial-gradient(120% 140% at 50% -20%, rgba(63,169,245,0.22), transparent 45%), linear-gradient(180deg, rgba(0,0,0,0.80), rgba(0,0,0,0.96))",
                    mixBlendMode: "multiply",
                  }}
                />
              </>
            )}

               {/* Chart top control strip */}
<div
  style={{
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    padding: "10px 12px",
    background: "transparent",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    minHeight: 42,
  }}
>

  {/* Only Players see Live / Daily & region/days controls */}
  {mode === "players" ? (
    <>
      {/* Live vs Daily toggle (unchanged) */}
      <div
        style={{
          display: "inline-flex",
          border: "1px solid #2a2a2a",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <button
          onClick={() => setDataMode("live")}
          aria-pressed={dataMode === "live"}
          style={{
            height: 32,
            padding: "0 12px",
            background: dataMode === "live" ? "#3fa9f5" : "#111",
            color: dataMode === "live" ? "#000" : "#ddd",
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Live (7D)
        </button>
        <button
          onClick={() => setDataMode("daily")}
          aria-pressed={dataMode === "daily"}
          style={{
            height: 32,
            padding: "0 12px",
            background: dataMode === "daily" ? "#3fa9f5" : "#111",
            color: dataMode === "daily" ? "#000" : "#ddd",
            fontWeight: 700,
            borderLeft: "1px solid #2a2a2a",
            borderRight: "none",
            borderTop: "none",
            borderBottom: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Daily
        </button>
      </div>

      {/* Region bands vs Days Since Launch (Players only) */}
      {dataMode === "live" ? (
  <>
    <label
      style={{
        fontSize: 13,
        color: "#bbb",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
      title="Show/Hide APAC / EU / NA prime-hour bands"
    >
      <input
        type="checkbox"
        checked={showRegionBands}
        onChange={() => setShowRegionBands((v) => !v)}
        style={{
          marginRight: 6,
          verticalAlign: "middle",
        }}
      />
      View Region Prime Hours
    </label>

    {showRegionBands && (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          marginLeft: 10,
        }}
      >
      {/* APAC (yellow) */}
<span
  style={{
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    // darker, translucent chip instead of neon square
    background: "rgba(250, 204, 21, 0.35)",       // was "#facc15"
    border: "1px solid rgba(148, 114, 12, 0.9)",  // dark edge
    boxShadow: "0 0 0 1px rgba(0,0,0,0.7)",       // subtle dark inset
  }}
/>
<span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>
  APAC
</span>

{/* EU (green) */}
<span
  style={{
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    background: "rgba(74, 222, 128, 0.35)",       // was "#4ade80"
    border: "1px solid rgba(22, 120, 72, 0.9)",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.7)",
  }}
/>
<span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>
  EU
</span>

{/* NA (blue) */}
<span
  style={{
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    background: "rgba(56, 189, 248, 0.35)",       // was "#38bdf8"
    border: "1px solid rgba(12, 105, 148, 0.9)",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.7)",
  }}
/>
<span style={{ fontSize: 12, color: "#cfd8e3", marginRight: 12 }}>
  NA
</span>

      </div>
    )}
  </>
) : (
 // Days-since-launch toggle (unchanged)
        <label
          style={{
            fontSize: 13,
            color: "#bbb",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          <input
            type="checkbox"
            checked={viewByDaysSinceLaunch}
            onChange={() => setViewByDaysSinceLaunch((v) => !v)}
            style={{
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          View by Days Since Launch
        </label>
      )}
    </>
  ) : (
    // Followers mode: Total vs Daily toggle
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      {/* Total | Daily pills */}
      <div
        style={{
          display: "inline-flex",
          border: "1px solid #2a2a2a",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setFollowersValueMode("total")}
          aria-pressed={followersValueMode === "total"}
          style={{
            height: 32,
            padding: "0 12px",
            background:
              followersValueMode === "total" ? "#3fa9f5" : "#111",
            color:
              followersValueMode === "total" ? "#000" : "#ddd",
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Total
        </button>
        <button
          type="button"
          onClick={() => setFollowersValueMode("daily")}
          aria-pressed={followersValueMode === "daily"}
          style={{
            height: 32,
            padding: "0 12px",
            background:
              followersValueMode === "daily" ? "#3fa9f5" : "#111",
            color:
              followersValueMode === "daily" ? "#000" : "#ddd",
            fontWeight: 700,
            borderLeft: "1px solid #2a2a2a",
            borderRight: "none",
            borderTop: "none",
            borderBottom: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Daily
        </button>
      </div>

      {/* Label */}
      <span
        style={{
          fontSize: 13,
          color: "#bbb",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        Followers · Last 90 Days
      </span>
    </div>
  )}

{dailyUsersEstimate && (
  <div
    style={{
      marginLeft: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 999,
      border: "1px solid rgba(63,169,245,0.35)",
      background:
        "linear-gradient(135deg, rgba(63,169,245,0.18), rgba(255,255,255,0.05))",
      boxShadow: "0 0 18px rgba(63,169,245,0.12)",
      whiteSpace: "nowrap",
    }}
  >
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#8ccfff",
      }}
    >
      Daily Users Est.
    </span>

    <span
      style={{
        fontSize: 15,
        fontWeight: 900,
        color: "#fff",
      }}
    >
      {dailyUsersEstimate.low.toLocaleString()} –{" "}
      {dailyUsersEstimate.high.toLocaleString()}
    </span>

    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: "#9ca3af",
        padding: "2px 7px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.10)",
      }}
    >
      Last 24H
    </span>
  </div>
)}

  {/* chip row (shared) */}
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
    }}
  >
    {chartGamesLegend.map((g) => (
      <button
        key={g.metadata.app_id}
        onClick={() => handleRemoveFromChart(g.metadata.app_id)}
        style={{
          borderRadius: 999,
          border: "1px solid #2a2a2a",
          background: "#111",
          color: "#ddd",
          padding: "4px 10px",
          fontSize: 12,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            maxWidth: 140,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {g.metadata.name}
        </span>
        <span style={{ opacity: 0.8 }}>✕</span>
      </button>
    ))}
  </div>
</div>

<div
  style={{
    position: "relative",
    zIndex: 1,
    padding: "8px 8px 0 0",
    height: 600,
  }}
>


  {renderData.length === 0 ? (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        color: "#999",
      }}
   >
      {mode === "followers"
        ? "Loading follower history…"
        : "Loading player history…"}
    </div>
  ) : (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={renderData}>
        {/* existing chart contents */}

                    {/* existing CartesianGrid / XAxis / YAxis / Tooltip / ReferenceDot / bands / Area series */}
                    <CartesianGrid stroke="#222" />
                    <XAxis
  dataKey="x"
  ticks={chartTicks}
  stroke="#ddd"
  tick={{ fontSize: 11 }}  // 1px smaller than the default
  tickFormatter={(tick) =>
    dataMode === "live"
      ? fmtDateTimeUTC(tick)
      : viewByDaysSinceLaunch
      ? String(tick)
      : fmtDateShortUTC(tick)
  }
/>

                    <YAxis
  stroke="#ddd"
  width={110}
  domain={[0, (max) => roundYAxisMax(max)]}
  allowDataOverflow
  tickFormatter={(tick) =>
    new Intl.NumberFormat("en-US").format(tick)
  }
/>

                   <Tooltip content={renderTooltip} />


                        {/* Peak dots for each series */}
                 {(() => {
  const peaks = {};
  for (const row of renderData) {
    for (const key of Object.keys(row)) {
      if (key === "x") continue;
      const v = Number(row[key]);
      if (!Number.isFinite(v)) continue;
      if (!peaks[key] || v > peaks[key].y) {
        peaks[key] = { x: row.x, y: v };
      }
    }
  }

  // Normalize highlight into an array
  const selectedIds = Array.isArray(highlightedSeries)
    ? highlightedSeries
    : highlightedSeries
    ? [String(highlightedSeries)]
    : [];

  return Object.entries(peaks).map(([id, { x, y }]) => {
    const seriesIndex = chartGames.findIndex(
      (g) => String(g.metadata.app_id) === String(id)
    );

    const game =
      seriesIndex >= 0 ? chartGames[seriesIndex] : null;
    const gameName = game?.metadata?.name || String(id);

    const color = seriesColor(
      seriesIndex >= 0 ? seriesIndex : 0
    );

    const isDimmed =
      selectedIds.length > 0 && !selectedIds.includes(String(id));

    const labelColor = isDimmed ? "#777" : color;

const labelPosition =
      mode === "followers" ? "left" : "top";

    return (
      <ReferenceDot
        key={`peak-${id}`}
        x={x}
        y={y}
        r={5}
        ifOverflow="extendDomain"
        fill={isDimmed ? "#444" : color}
        stroke="#000"
        label={{
          value: gameName,
          position: labelPosition,
          fill: labelColor,
          fontSize: 12,
          fontWeight: 700,
        }}
      />
    );
  });
})()}





                        {/* Region bands for live mode */}
                        {dataMode === "live" &&
                          showRegionBands &&
                          renderData.length > 0 &&
                          (() => {
                            const first = new Date(
                              renderData[0].x
                            ).getTime();
                            const last = new Date(
                              renderData[renderData.length - 1].x
                            ).getTime();
                            const seenDays = new Set();
                            const out = [];
                          const pushBand = (x1ms, x2ms, fill, key /*, tag */) => {
  const x1 = Math.max(first, x1ms);
  const x2 = Math.min(last, x2ms);
  if (x2 <= x1) return;
  out.push(
    <ReferenceArea
      key={key}
      x1={new Date(x1).toISOString()}
      x2={new Date(x2).toISOString()}
      ifOverflow="hidden"
      fill={fill}
      stroke="none"
    />
  );
};

                            for (const row of renderData) {
                              const d = new Date(row.x);
                              if (Number.isNaN(d)) continue;
                              const y = d.getUTCFullYear();
                              const m = d.getUTCMonth();
                              const day = d.getUTCDate();
                              const dayKey = `${y}-${m}-${day}`;
                              if (seenDays.has(dayKey)) continue;
                              seenDays.add(dayKey);

                              // APAC
                              {
                                const s = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  ASIA_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  ASIA_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s,
                                  e,
                                  ASIA_BAND_FILL,
                                  `apac-${dayKey}`,
                                  "APAC"
                                );
                              }
                              // EU
                              {
                                const s = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  EU_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  EU_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s,
                                  e,
                                  EU_BAND_FILL,
                                  `eu-${dayKey}`,
                                  "EU"
                                );
                              }
                              // NA (may wrap)
                              if (
                                NA_UTC_START_HOUR <=
                                NA_UTC_END_HOUR
                              ) {
                                const s = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  NA_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  NA_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s,
                                  e,
                                  NA_BAND_FILL,
                                  `na-${dayKey}`,
                                  "NA"
                                );
                              } else {
                                const s1 = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  NA_UTC_START_HOUR,
                                  0,
                                  0
                                );
                                const e1 = Date.UTC(
                                  y,
                                  m,
                                  day,
                                  24,
                                  0,
                                  0
                                );
                                pushBand(
                                  s1,
                                  e1,
                                  NA_BAND_FILL,
                                  `na-a-${dayKey}`,
                                  "NA"
                                );
                                const s2 = Date.UTC(
                                  y,
                                  m,
                                  day + 1,
                                  0,
                                  0,
                                  0
                                );
                                const e2 = Date.UTC(
                                  y,
                                  m,
                                  day + 1,
                                  NA_UTC_END_HOUR,
                                  0,
                                  0
                                );
                                pushBand(
                                  s2,
                                  e2,
                                  NA_BAND_FILL,
                                  `na-b-${dayKey}`,
                                  "NA"
                                );
                              }
                            }
                            return out;
                          })()}

                      {/* Area series for each game */}
{chartGames.map((g, idx) => {
  const id = String(g.metadata.app_id);
  const color = seriesColor(idx);

  // Normalize highlightedSeries into an array
  const selectedIds = Array.isArray(highlightedSeries)
    ? highlightedSeries
    : highlightedSeries
    ? [String(highlightedSeries)]
    : [];

  const isDimmed =
    selectedIds.length > 0 && !selectedIds.includes(id);

  return (
    <Area
      key={id}
      type="monotone"
      dataKey={id}
      stroke={color}
      fill={color}
      fillOpacity={isDimmed ? 0.08 : 0.25}
      strokeWidth={isDimmed ? 1.4 : 2}
      strokeOpacity={isDimmed ? 0.4 : 1}
      dot={false}
      name={g.metadata.name}
      isAnimationActive={id === animSeriesId}
      animationDuration={600}
      onClick={() => {
  setHighlightedSeries((prev) => {
    const prevArr = Array.isArray(prev)
      ? prev
      : prev
      ? [String(prev)]
      : [];

    const already = prevArr.includes(id);
    const multi = multiKeyDown; // 👈 comes from the global key tracker

    if (multi) {
      // Ctrl/Cmd + click: toggle this series in the highlighted set
      if (already) {
        const next = prevArr.filter((x) => x !== id);
        return next.length ? next : null;
      }
      return [...prevArr, id];
    } else {
      // Normal click: single-select, or clear if it was the only one
      if (already && prevArr.length === 1) {
        return null;
      }
      return [id];
    }
  });
}}
      style={{ cursor: "pointer" }}
    />
  );
})}

                      </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

                {/* Window slider */}
                {mergedData.length > 1 && (
                  <div style={{ marginTop: 10, paddingRight: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: "#bbb",
                        marginBottom: 6,
                      }}
                    >
                      <span>
                        Window:{" "}
                        <b>{labelForIdx(winStartIdx)}</b>
                      </span>
                      <span>
                        → <b>{labelForIdx(winEndIdx)}</b>
                      </span>
                    </div>
                    <div
                      style={{
                        position: "relative",
                        height: 6,
                        background: "#1a1a1a",
                        borderRadius: 4,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: `${
                            (100 * winStartIdx) /
                            (mergedData.length - 1)
                          }%`,
                          width: `${
                            (100 * (winEndIdx - winStartIdx)) /
                            Math.max(1, mergedData.length - 1)
                          }%`,
                          top: 0,
                          bottom: 0,
                          background: "#3fa9f5",
                          opacity: 0.35,
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div style={{ position: "relative", height: 0 }}>
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, mergedData.length - 1)}
                        value={winStartIdx}
                        onChange={(e) =>
                          setWinStartIdx(
                            Math.min(
                              parseInt(e.target.value, 10),
                              winEndIdx - 1
                            )
                          )
                        }
                        style={{
                          position: "relative",
                          width: "100%",
                          WebkitAppearance: "none",
                          background: "transparent",
                          top: -14,
                        }}
                      />
                      <input
                        type="range"
                        min={0}
                        max={Math.max(0, mergedData.length - 1)}
                        value={winEndIdx}
                        onChange={(e) =>
                          setWinEndIdx(
                            Math.max(
                              parseInt(e.target.value, 10),
                              winStartIdx + 1
                            )
                          )
                        }
                        style={{
                          position: "relative",
                          width: "100%",
                          WebkitAppearance: "none",
                          background: "transparent",
                          top: -26,
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
          </div>
        )}


        {/* Content */}
        {globalLoading || loadingPlayers || followersLoading ? (
          <VirtualizedGameGrid
            games={[]}
            onRemoveGame={handleRemove}
            totalCcu={0}
            maxCcu={1}
            loading={true}
          />
        ) : mode === "players" ? (
          viewMode === "market" ? (
           // ============ Market Mind ============
<div
  ref={bubbleContainerRef}
  style={{
    position: "absolute", // overlay under the header so bubbles can 'bleed'
    inset: 0,
    zIndex: 0,
  }}
>
  <style>{`.mm-bubble-dim{opacity:.25}.mm-bubble-hit{opacity:1}`}</style>

  {/* Skeleton until full-size layout is ready (mirrors mindSHARE) */}
{showMarketSkeleton ? (
  // --- centered packed skeleton, like mindSHARE’s ball ---
  (() => {
    const w = dimensions.width || window.innerWidth || 1200;
    const h = dimensions.height || window.innerHeight || 720;

    const size = Math.max(
      480,                           // minimum size
      Math.min(Math.min(w, h), 900)  // cap max so it doesn’t explode
    );

    return (
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
              <SteamBubbleSkeleton width={size} height={size} count={200} />
      </div>
    );
  })()
) : (
  <>
          <svg
            ref={bubbleSvgRef}
            width={dimensions.width}
            height={dimensions.height}
            style={{ display: "block" }}
          >
            <g id="steam-mm-zoom">
              {bubbleNodes.map((d, i) => {
                const k = zoomT?.k || 1;
                const visibleR = d.r * k;
                const hit = matchesPreviewSteam(d.data, preview);

                // label visibility (more labels as you zoom)
                const worldCutoff = 10 / Math.pow(k, 1.05);
                const screenCutoff = 6;
                const showLabel =
                  d.r > worldCutoff || visibleR >= screenCutoff;

                const maxChars =
                  visibleR > 80 ? 28 : visibleR > 44 ? 22 : 12;
                const label =
                  d.data.name.length > maxChars
                    ? d.data.name.slice(0, maxChars) + "…"
                    : d.data.name;

                const fontSize = 10 / Math.pow(k, 0.8);
                const strokeW = Math.max(0.25, 1 / Math.pow(k, 0.9));
                const strokeA = Math.max(0.1, 0.5 / Math.pow(k, 0.85));

                return (
                  <g
                    key={i}
                    className="mm-node"
                    data-app={d.data.app_id}
                    transform={`translate(${d.x},${d.y})`}
                    onMouseEnter={() => setBubbleHovered(d)}
                    onMouseLeave={() => setBubbleHovered(null)}
                    style={{ cursor: "grab" }}
                  >
                    <circle
                      r={bubbleHovered === d ? d.r * 1.08 : d.r}
                      fill={d.data.color}
                      opacity={0.9}
                      style={{ transition: "all .25s ease" }}
                    />
                    {preview && hit && (
                      <circle
                        r={d.r}
                        fill="none"
                        stroke="rgba(255,255,255,0.55)"
                        strokeWidth={Math.max(
                          1,
                          1.5 / Math.pow(k, 0.85)
                        )}
                        style={{
                          filter:
                            "drop-shadow(0 0 10px rgba(255,255,255,0.35))",
                        }}
                      />
                    )}

                    {showLabel && (
                      <text
                        textAnchor="middle"
                        dy="0.35em"
                        fontSize={fontSize}
                        fill="#fff"
                        stroke={`rgba(0,0,0,${strokeA})`}
                        strokeWidth={strokeW}
                        style={{
                          pointerEvents: "none",
                          fontWeight: 500,
                          letterSpacing: "-0.2px",
                          paintOrder: "stroke",
                          fontFamily:
                            '"Segoe UI", system-ui, -apple-system, sans-serif',
                        }}
                      >
                        {label}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {bubbleHovered &&
            bubbleHovered.x != null &&
            bubbleHovered.y != null &&
            (() => {
              const screenX =
                zoomT?.applyX?.(bubbleHovered.x) ?? bubbleHovered.x;
              const screenY =
                zoomT?.applyY?.(bubbleHovered.y) ?? bubbleHovered.y;

              const pos = computePopoverPos({
                x: screenX,
                y: screenY,
                r: bubbleHovered.r || 40,
                containerWidth: dimensions.width,
                containerHeight: dimensions.height,
              });

              return (
                <div
                  style={{
                    position: "absolute",
                    left: pos.left,
                    top: pos.top,
                    background: "#222",
                    color: "#fff",
                    padding: 0,
                    borderRadius: 12,
                    boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
                    zIndex: 1000,
                    width: 320,
                    pointerEvents: "none",
                    border: "1px solid #333",
                    overflow: "hidden",
                  }}
                >
              {bubbleHovered.data.box_art ? (
                <img
                  src={bubbleHovered.data.box_art}
                  alt={bubbleHovered.data.name}
                  style={{ width: "100%", display: "block" }}
                />
              ) : null}
              <div style={{ height: 8, background: "#222" }} />
              {(() => {
                const embed = getVideoEmbed(bubbleHovered.data.video);
                if (!embed) return null;
                return (
                  <div style={{ width: "100%", background: "#000" }}>
                    <SteamVideoPreview
                      tags={bubbleHovered.data.tags}
                      title={bubbleHovered.data.name}
                    >
                      {embed.type === "file" ? (
                        <video
                          src={embed.src}
                          autoPlay
                          muted
                          loop
                          playsInline
                          style={{ width: "100%", height: "auto", display: "block" }}
                        />
                      ) : (
                        <iframe
                          src={embed.src}
                          title={`${bubbleHovered.data.name} trailer`}
                          frameBorder="0"
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          style={{ width: "100%", aspectRatio: "16/9", display: "block" }}
                        />
                      )}
                    </SteamVideoPreview>
                  </div>
                );
              })()}
              <div
                style={{
                  padding: 12,
                  borderTop: "1px solid #333",
                  background: "#222",
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
                  {bubbleHovered.data.name}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
                  {bubbleHovered.data.ccu.toLocaleString()} CCU
                </div>
                {bubbleHovered.data.short_description && (
                  <div style={{ fontSize: 13, color: "#ddd", marginBottom: 8 }}>
                    {bubbleHovered.data.short_description}
                  </div>
                )}
                {(bubbleHovered.data.release_date || formatPrice(bubbleHovered.data)) && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 6 }}>
    {bubbleHovered.data.release_date && (
      <div style={{ marginBottom: formatPrice(bubbleHovered.data) ? 6 : 0 }}>
        <strong>Release:</strong>{" "}
        {new Date(bubbleHovered.data.release_date).toLocaleDateString()}
      </div>
    )}
    {formatPrice(bubbleHovered.data) && (
      <div>
        <strong>Price:</strong> {formatPrice(bubbleHovered.data)}
      </div>
    )}
  </div>
)}

{bubbleHovered.data.publishers && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 4 }}>
    <strong>Publisher:</strong> {bubbleHovered.data.publishers}
  </div>
)}
{bubbleHovered.data.developers && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 8 }}>
    <strong>Developer:</strong> {bubbleHovered.data.developers}
  </div>
)}

                {bubbleHovered.data.tags?.length ? (
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    {bubbleHovered.data.tags.join(", ")}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })()}
    </>
  )}
</div>

          ) : viewMode === "effervescent" ? (
            // ============ Effervescent ============
            <div
              ref={effContainerRef}
              style={{
                position: "relative",
                height: effHeight,
                overflow: "hidden",
              }}
            >
              <EffervescentBubbles
                key={effKey}
                games={visiblePlayers}
                onHoverChange={setEffHover}
              />

              {/* Smart-positioned hover popout */}
              {effHover &&
                effHover.x != null &&
                effHover.y != null &&
                (() => {
                  const rect = effContainerRef.current?.getBoundingClientRect();
                  const containerWidth =
                    rect?.width ||
                    document.querySelector("[data-eff-dim]")?.clientWidth ||
                    1600;
                  const containerHeight = rect?.height || effHeight;
                  const pos = computePopoverPos({
                    x: effHover.x,
                    y: effHover.y,
                    r: effHover.r || 40,
                    containerWidth,
                    containerHeight,
                  });
                  return (
                    <div
                      style={{
                        position: "absolute",
                        left: pos.left,
                        top: pos.top,
                        background: "#222",
                        color: "#fff",
                        padding: 0,
                        borderRadius: 12,
                        boxShadow: "0 10px 28px rgba(0,0,0,0.55)",
                        zIndex: 1000,
                        width: 320,
                        pointerEvents: "none",
                        border: "1px solid #333",
                        overflow: "hidden",
                      }}
                    >
                      {effHover.data.box_art ? (
                        <img
                          src={effHover.data.box_art}
                          alt={effHover.data.name}
                          style={{ width: "100%", display: "block" }}
                        />
                      ) : null}
                      <div style={{ height: 8, background: "#222" }} />
                      {(() => {
                        const embed = getVideoEmbed(effHover.data.video);
                        if (!embed) return null;
                        return (
                          <div style={{ width: "100%", background: "#000" }}>
                            <SteamVideoPreview
                              tags={effHover.data.tags}
                              title={effHover.data.name}
                            >
                              {embed.type === "file" ? (
                                <video
                                  src={embed.src}
                                  autoPlay
                                  muted
                                  loop
                                  playsInline
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                  }}
                                />
                              ) : (
                                <iframe
                                  src={embed.src}
                                  title={`${effHover.data.name} trailer`}
                                  frameBorder="0"
                                  allow="autoplay; encrypted-media; picture-in-picture"
                                  allowFullScreen
                                  style={{
                                    width: "100%",
                                    aspectRatio: "16/9",
                                    display: "block",
                                  }}
                                />
                              )}
                            </SteamVideoPreview>
                            </div>
                        );
                      })()}
                      <div
                        style={{
                          padding: 12,
                          borderTop: "1px solid #333",
                          background: "#222",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 16,
                            marginBottom: 6,
                          }}
                        >
                          {effHover.data.name}
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 800,
                            marginBottom: 8,
                          }}
                        >
                          {(effHover.data.ccu || 0).toLocaleString()} CCU
                        </div>
                        {effHover.data.short_description && (
                          <div
                            style={{
                              fontSize: 13,
                              color: "#ddd",
                              marginBottom: 8,
                            }}
                          >
                            {effHover.data.short_description}
                          </div>
                        )}
                      {(effHover.data.release_date || formatPrice(effHover.data)) && (
  <div style={{ fontSize: 13, color: "#ccc", marginBottom: 6 }}>
    {effHover.data.release_date && (
      <div style={{ marginBottom: formatPrice(effHover.data) ? 6 : 0 }}>
        <strong>Release:</strong>{" "}
        {new Date(effHover.data.release_date).toLocaleDateString()}
      </div>
    )}
    {formatPrice(effHover.data) && (
      <div>
        <strong>Price:</strong> {formatPrice(effHover.data)}
      </div>
    )}
  </div>
)}


                        {effHover.data.publishers && (
                          <div
                            style={{
                              fontSize: 13,
                              color: "#ccc",
                              marginBottom: 4,
                            }}
                          >
                            <strong>Publisher:</strong>{" "}
                            {effHover.data.publishers}
                          </div>
                        )}
                        {effHover.data.developers && (
                          <div
                            style={{
                              fontSize: 13,
                              color: "#ccc",
                              marginBottom: 8,
                            }}
                          >
                            <strong>Developer:</strong>{" "}
                            {effHover.data.developers}
                          </div>
                        )}
                        {effHover.data.tags?.length ? (
                          <div style={{ fontSize: 11, color: "#aaa" }}>
                            {effHover.data.tags.join(", ")}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              {/* invisible size anchor */}
              <div
                data-eff-dim
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
              />
            </div>
                   ) : (
            // ============ Players Cards ============
            <Fragment key="players-cards">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))",
                  gap: "24px",
                  justifyContent: "center",
                  overflow: "visible",
                }}
              >
                {visiblePlayers.map((g, i) => {
                  const share =
                    totalCcu > 0 ? ((g.ccu / totalCcu) * 100).toFixed(2) : "0.00";
                  const hovered = hoveredCards[g.app_id] || false;
                  const isRecent = releasedWithin7Days(g.release_date);
                  const mgAppId = String(parseInt(g.app_id ?? "", 10)).trim();
const mgIdMatches = mgMetaLoaded && mgAppId ? mgSteamIdSetRef.current.has(mgAppId) : false;

const mgName = String(g.name ?? "").trim().toLowerCase();
const mgNameMatches = mgMetaLoaded && mgName ? mgGameNameSetRef.current.has(mgName) : false;

// "missing" meaning it does NOT match in MG
const isMissingId = mgMetaLoaded ? !mgIdMatches : false;
const isMissingName = mgMetaLoaded ? !mgNameMatches : false;

// ✅ ONLY glow if missing BOTH ID and Name
const showMissingGlow = mgMetaLoaded && isMissingId && isMissingName;

                  const isInChart = chartGames.some(
                    (cg) => String(cg.metadata.app_id) === String(g.app_id)
                  );
                  // parsed metadata for clickable filters (kept for future use)
                  const publisherNames = splitPublishersSafe(g.publishers || "");
                  const developerNames = splitDevelopersSafe(g.developers || "");
                  const tagList = Array.isArray(g.tags) ? g.tags : [];

                  return (
                    <div
                      key={g.app_id}
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "360px",
                        margin: "0 auto",
                      }}
                      onMouseLeave={() => {
                        // leaving both the card and its popout → clear hover
                        setHoveredCards({});
                      }}
                    >
                      {/* Gold glow */}
                      <GoldGlow active={isRecent} subtle={isReleasedThisWeek} />

                      <RedMissingGlow active={showMissingGlow} />

                      {/* Card wrapper */}
                      <div
                        ref={(el) => (cardRefs.current[g.app_id] = el)}
                        style={{
                          borderRadius: "12px",
                          overflow: "visible",
                          background: "transparent",
                          width: "100%",
                          position: "relative",
                          cursor: "default",
                          perspective: "900px",
                          zIndex: hovered ? 1000 : 0,
                        }}
                        onMouseEnter={() => {
                          setHoveredCards({ [g.app_id]: true });
                          const card = cardRefs.current[g.app_id];
                          if (card) {
                            card.style.setProperty("--sx", "50%");
                            card.style.setProperty("--sy", "50%");
                          }
                        }}
                        onMouseLeave={() => {
                          handleMouseLeave(g.app_id);
                          setTimeout(() => {
                            setHoveredCards((prev) => {
                              if (prev[g.app_id]) return prev;
                              return { ...prev, [g.app_id]: false };
                            });
                          }, 80);
                        }}
                        onMouseMove={(e) => handleMouseMove(e, g.app_id)}
                      >
                        {/* Tilted card */}
                      <div
  style={{
    borderRadius: "14px",
    overflow: "hidden",
    background: "#000",
    transformStyle: "preserve-3d",
    transform: `rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg))`,
    transition: "transform 140ms ease, box-shadow 140ms ease",
    willChange: "transform, box-shadow",
    boxShadow: hovered
      ? "0 18px 44px rgba(0,0,0,0.65)"
      : "0 10px 28px rgba(0,0,0,0.55)",
    position: "relative",
    zIndex: 1,
  }}
>

                          {/* Base gradient skin */}
                          <div
                            style={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: "14px",
                              background:
                                "linear-gradient(135deg, rgba(0,153,255,0.18), rgba(0,153,255,0.02))",
                              boxShadow:
                                "0 18px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(63,169,245,0.12)",
                              filter: "saturate(110%)",
                              pointerEvents: "none",
                              zIndex: 0,
                            }}
                          />

                          {/* Subtle inner gleam when recent */}
                          {isRecent && (
                            <div
                              aria-hidden
                              style={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: "14px",
                                pointerEvents: "none",
                                zIndex: 4,
                                boxShadow:
                                  "inset 0 0 0 1px rgba(255,215,0,0.15)",
                                background:
                                  "radial-gradient(600px 340px at var(--sx,50%) var(--sy,50%), rgba(255,215,0,0.08), transparent 45%)",
                                mixBlendMode: "screen",
                              }}
                            />
                          )}

                          {/* Shine overlay */}
                          <div
                            style={getShineStyle(
                              hovered,
                              cardRefs.current[g.app_id]
                            )}
                          />

                          {/* Box art */}
                          <img
                            src={g.box_art}
                            alt={g.name}
                            style={{
                              display: "block",
                              width: "100%",
                              aspectRatio: "230/107",
                              objectFit: "cover",
                              position: "relative",
                              zIndex: 2,
                            }}
                          />

                          {/* Top-left row: rank + copy buttons */}
<div
  style={{
    position: "absolute",
    top: 6,
    left: 6,
    zIndex: 3,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  }}
>
  {/* Rank badge */}
  <div
    style={{
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      borderRadius: "14px",
      padding: "0px 6px",
      height: "20px",
      lineHeight: "20px",
      fontSize: "12px",
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    #{i + 1}
  </div>

  {/* Copy Name */}
  <button
    type="button"
    title="Copy game name"
    style={copyBtnBase}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      copyTextToClipboard(g.name);
    }}
  >
    Copy Name
  </button>

  {/* Copy ID */}
  <button
    type="button"
    title="Copy Steam app_id"
    style={copyBtnBase}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      copyTextToClipboard(g.app_id);
    }}
  >
    Copy ID
  </button>
</div>


                          {/* Close button */}
                          <button
                            style={{
                              position: "absolute",
                              top: "6px",
                              right: "6px",
                              background: "rgba(0,0,0,0.55)",
                              border: "none",
                              fontSize: "12px",
                              color: "#fff",
                              cursor: "pointer",
                              borderRadius: "14px",
                              padding: "0 6px",
                              height: "20px",
                              lineHeight: "20px",
                              fontWeight: 700,
                              zIndex: 3,
                            }}
                            type="button"
                            aria-label="Hide card"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemove(g.app_id);
                            }}
                          >
                            ×
                          </button>

                          {/* Footer – entire bar adds to chart */}
                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: 0,
                              padding: "12px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "baseline",
                              background:
                                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
                              zIndex: 2,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInChart) {
      handleRemoveFromChart(g.app_id);   // 🔹 remove if already in chart
    } else {
      handleAddToChart(g);               // 🔹 add if not in chart
    }
  }}
>
                            {/* Left: + / ✓ and name */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                maxWidth: "72%",
                                minWidth: 0,
                              }}
                            >
                             <button
  type="button"
  title={
    isInChart
      ? "Remove from Player History chart"
      : "Add to Player History chart"
  }
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();        // don’t double-fire the footer handler
    if (isInChart) {
      handleRemoveFromChart(g.app_id);
    } else {
      handleAddToChart(g);
    }
  }}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "999px",
                                  border: isInChart
                                    ? "1px solid #fff"
                                    : "1px solid rgba(255,255,255,0.7)",
                                  background: isInChart
                                    ? "#fff"
                                    : "rgba(0,0,0,0.55)",
                                  color: isInChart ? "#000" : "#fff",
                                  fontSize: 12,
                                  fontWeight: 800,
                                  lineHeight: "18px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                {isInChart ? "✓" : "+"}
                              </button>

                              <div
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "15px",
                                  color: "#fff",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {g.name}
                              </div>
                            </div>

                            {/* Right: CCU + share */}
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#cfd8e3",
                                whiteSpace: "nowrap",
                                fontWeight: 600,
                              }}
                              aria-label="Current CCU and share of visible total"
                            >
                              {g.ccu?.toLocaleString()} · {share}%
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hover popout with hoverable gap wrapper */}
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          width: "100%",
                          transform: "none",
                          boxSizing: "border-box",
                          paddingTop: 10, // this is the visual gap, but still hover area
                          zIndex: 1000,
                          opacity: hovered ? 1 : 0,
                          pointerEvents: hovered ? "auto" : "none",
                          transition: "opacity 160ms ease",
                        }}
                        onMouseEnter={() => {
                          setHoveredCards({ [g.app_id]: true });
                        }}
                        onMouseLeave={() => {
                          setHoveredCards((prev) => ({
                            ...prev,
                            [g.app_id]: false,
                          }));
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            background: "rgba(34,34,34,0.96)",
                            borderRadius: "12px",
                            padding: "10px 12px",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.6)",
                            fontSize: "14px",
                            lineHeight: 1.4,
                            border: "1px solid #333",
                            backdropFilter: "blur(10px)",
                          }}
                        >
                          {/* Video embed */}
                          {hovered &&
                            g.video &&
                            (() => {
                              const embed = getVideoEmbed(g.video);
                              if (!embed) return null;
                              return (
                                <div
                                  style={{
                                    width: "100%",
                                    aspectRatio: "16/9",
                                    background: "#000",
                                    borderRadius:                                     "10px",
                                    border: "1px solid #333",
                                    overflow: "hidden",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <SteamVideoPreview tags={g.tags} title={g.name}>
                                    {embed.type === "file" ? (
                                      <video
                                        src={embed.src}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          display: "block",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    ) : (
                                      <iframe
                                        src={embed.src}
                                        title={`${g.name} trailer`}
                                        frameBorder="0"
                                        allow="encrypted-media; picture-in-picture"
                                        allowFullScreen
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          display: "block",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    )}
                                  </SteamVideoPreview>
                                </div>
                              );
                            })()}

                          {/* Game info */}
                          {g.short_description && (
                            <div style={{ marginBottom: 10, color: "#fff" }}>
                              {g.short_description}
                            </div>
                          )}

                          {(g.release_date || formatPrice(g)) && (
                            <div style={{ marginBottom: 6, color: "#cfd8e3" }}>
                              {g.release_date && (
                                <div
                                  style={{
                                    marginBottom: formatPrice(g) ? 6 : 0,
                                  }}
                                >
                                  <strong style={{ color: "#fff" }}>
                                    Release:
                                  </strong>{" "}
                                  {new Date(
                                    g.release_date
                                  ).toLocaleDateString()}
                                </div>
                              )}
                              {formatPrice(g) && (
                                <div>
                                  <strong style={{ color: "#fff" }}>
                                    Price:
                                  </strong>{" "}
                                  {formatPrice(g)}
                                </div>
                              )}
                            </div>
                          )}

                          {g.publishers && (
                            <div
                              style={{ marginBottom: 6, color: "#cfd8e3" }}
                            >
                              <strong style={{ color: "#fff" }}>
                                Publisher:
                              </strong>{" "}
                              {g.publishers}
                            </div>
                          )}

                          {g.developers && (
                            <div
                              style={{ marginBottom: 10, color: "#cfd8e3" }}
                            >
                              <strong style={{ color: "#fff" }}>
                                Developer:
                              </strong>{" "}
                              {g.developers}
                            </div>
                          )}

                          {Array.isArray(g.tags) && g.tags.length > 0 && (
                            <div style={{ color: "#cfd8e3" }}>
                              <strong style={{ color: "#fff" }}>Tags:</strong>{" "}
                              {g.tags.join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasMorePlayers && (
                <div
                  ref={sentinelRef}
                  style={{ height: "40px", background: "transparent" }}
                />
              )}
            </Fragment>
          )

        ) : (
          // ============ Followers Cards ============
          <Fragment key="followers-cards">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))",
                gap: "24px",
                justifyContent: "center",
                overflow: "visible",
              }}
            >
              {visibleFollowers.map((g, i) => {
                const growth7 = g.growth_7d ?? 0;
                const share = `${g.followers?.toLocaleString()} · ${
                  growth7 >= 0 ? "+" : ""
                }${Math.abs(growth7).toLocaleString()} (7D)`;
                const hovered = hoveredCards[g.app_id] || false;
                const isRecent = releasedWithin7Days(g.release_date);
                const mgAppId = String(parseInt(g.app_id ?? "", 10)).trim();
const mgIdMatches =
  mgMetaLoaded && mgAppId ? mgSteamIdSetRef.current.has(mgAppId) : false;

const mgName = String(g.name ?? "").trim().toLowerCase();
const mgNameMatches =
  mgMetaLoaded && mgName ? mgGameNameSetRef.current.has(mgName) : false;

const isMissingId = mgMetaLoaded ? !mgIdMatches : false;
const isMissingName = mgMetaLoaded ? !mgNameMatches : false;

// glow if missing either (your current behavior)
const showMissingGlow = mgMetaLoaded && isMissingId && isMissingName;

// 🔹 ADD THIS:
  const isInChart = chartGames.some(
    (cg) => String(cg.metadata.app_id) === String(g.app_id)
  );

                return (
                  <div
                    key={g.app_id}
                    style={{
                      position: "relative",
                      width: "100%",
                      maxWidth: "360px",
                      margin: "0 auto",
                    }}
                  >
                    {/* NEW: gold outer glow */}
                    <GoldGlow active={isRecent} subtle={isReleasedThisWeek} />
                    <RedMissingGlow active={showMissingGlow} />

                    {/* Card wrapper */}
                    <div
  ref={(el) => (cardRefs.current[g.app_id] = el)}
  style={{
    borderRadius: "12px",
    overflow: "visible",
    background: "transparent",
    width: "100%",
    position: "relative",
    cursor: "default",   // match Players cards
    perspective: "900px",
    zIndex: hovered ? 1000 : 0,
  }}
  onMouseEnter={() => {
                        setHoveredCards((prev) => ({
                          ...prev,
                          [g.app_id]: true,
                        }));
                        const card = cardRefs.current[g.app_id];
                        if (card) {
                          card.style.setProperty("--sx", "50%");
                          card.style.setProperty("--sy", "50%");
                        }
                      }}
                      onMouseLeave={() => {
                        setHoveredCards((prev) => ({
                          ...prev,
                          [g.app_id]: false,
                        }));
                        handleMouseLeave(g.app_id);
                      }}
                      onMouseMove={(e) => handleMouseMove(e, g.app_id)}
                    >
                      {/* Tilt div */}
                      <div
                        style={{
                          borderRadius: "14px",
                          overflow: "hidden",
                          background: "#000",
                          transformStyle: "preserve-3d",
                          transform: `rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg))`,
                          transition:
                            "transform 140ms ease, box-shadow 140ms ease",
                          willChange: "transform, box-shadow",
                          boxShadow: hovered
                            ? "0 18px 44px rgba(0,0,0,0.65)"
                            : "0 10px 28px rgba(0,0,0,0.55)",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {/* Base layer */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "14px",
                            background:
                              "linear-gradient(135deg, rgba(0,153,255,0.18), rgba(0,153,255,0.02))",
                            boxShadow:
                              "0 18px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(63,169,245,0.12)",
                            filter: "saturate(110%)",
                            pointerEvents: "none",
                            zIndex: 0,
                          }}
                        />

                        {/* NEW: very subtle inner gleam when recent */}
                        {isRecent && (
                          <div
                            aria-hidden
                            style={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: "14px",
                              pointerEvents: "none",
                              zIndex: 4,
                              boxShadow: "inset 0 0 0 1px rgba(255,215,0,0.15)",
                              background:
                                "radial-gradient(600px 340px at var(--sx,50%) var(--sy,50%), rgba(255,215,0,0.08), transparent 45%)",
                              mixBlendMode: "screen",
                            }}
                          />
                        )}

                        {/* Shine */}
                        <div
                          style={getShineStyle(
                            hovered,
                            cardRefs.current[g.app_id]
                          )}
                        />

                        {/* Image */}
                        <img
                          src={g.box_art}
                          alt={g.name}
                          style={{
                            display: "block",
                            width: "100%",
                            aspectRatio: "230/107",
                            objectFit: "cover",
                            position: "relative",
                            zIndex: 2,
                          }}
                        />

                       {/* Top-left row: rank + copy buttons */}
<div
  style={{
    position: "absolute",
    top: 6,
    left: 6,
    zIndex: 3,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  }}
>
  <div
    style={{
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      borderRadius: "14px",
      padding: "0px 6px",
      height: "20px",
      lineHeight: "20px",
      fontSize: "12px",
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    #{i + 1}
  </div>

  <button
    type="button"
    title="Copy game name"
    style={copyBtnBase}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      copyTextToClipboard(g.name);
    }}
  >
    Copy Name
  </button>

  <button
    type="button"
    title="Copy Steam app_id"
    style={copyBtnBase}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      copyTextToClipboard(g.app_id);
    }}
  >
    Copy ID
  </button>
</div>


                        {/* Close button */}
                        <button
                          style={{
                            position: "absolute",
                            top: "6px",
                            right: "6px",
                            background: "rgba(0,0,0,0.55)",
                            border: "none",
                            fontSize: "12px",
                            color: "#fff",
                            cursor: "pointer",
                            borderRadius: "14px",
                            padding: "0 6px",
                            height: "20px",
                            lineHeight: "20px",
                            fontWeight: 700,
                            zIndex: 3,
                          }}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveFollower(g.app_id);
                          }}
                        >
                          ×
                        </button>
                       {/* Footer */}
<div
  style={{
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    background:
      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
    zIndex: 2,
    cursor: "pointer",
  }}
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation(); // don't bubble to the card
    if (isInChart) {
      handleRemoveFromChart(g.app_id);
    } else {
      handleAddToChart(g); // Followers mode: loads followers series
    }
  }}
>

  {/* Left: + / ✓ and name */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      maxWidth: "72%",
      minWidth: 0,
    }}
  >
    <button
      type="button"
      title={
        isInChart
          ? "Remove from Followers chart"
          : "Add to Followers chart"
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation(); // don't navigate to /game
        if (isInChart) {
          handleRemoveFromChart(g.app_id);
        } else {
          handleAddToChart(g); // mode-aware: will load Followers series
        }
      }}
      style={{
        width: 20,
        height: 20,
        borderRadius: "999px",
        border: isInChart
          ? "1px solid #fff"
          : "1px solid rgba(255,255,255,0.7)",
        background: isInChart
          ? "#fff"
          : "rgba(0,0,0,0.55)",
        color: isInChart ? "#000" : "#fff",
        fontSize: 12,
        fontWeight: 800,
        lineHeight: "18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {isInChart ? "✓" : "+"}
    </button>

    <div
      style={{
        fontWeight: "bold",
        fontSize: "15px",
        color: "#fff",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {g.name}
    </div>
  </div>

  {/* Right: Followers + 7D growth line (unchanged) */}
  <div
    style={{
      fontSize: "13px",
      color: "#cfd8e3",
      whiteSpace: "nowrap",
      fontWeight: 600,
    }}
  >
    {share}
  </div>
</div>

                      </div>
                    </div>

                  {/* Info box */}
<div
  style={{
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    width: "100%",
    transform: "none",
    boxSizing: "border-box",
    // hoverable zone including the visual gap
    paddingTop: 12,
    zIndex: 2000,
    opacity: hovered ? 1 : 0,
    pointerEvents: hovered ? "auto" : "none",
    transition: "opacity 160ms ease",
  }}
  onMouseEnter={() => {
    setHoveredCards((prev) => ({ ...prev, [g.app_id]: true }));
  }}
  onMouseLeave={() => {
    setHoveredCards((prev) => ({ ...prev, [g.app_id]: false }));
  }}
>
  <div
    style={{
      width: "100%",
      boxSizing: "border-box",
      background: "rgba(34,34,34,0.96)",
      borderRadius: "12px",
      padding: "10px 12px",
      boxShadow: "0 12px 28px rgba(0,0,0,0.6)",
      fontSize: "14px",
      lineHeight: 1.4,
      border: "1px solid #333",
      backdropFilter: "blur(10px)",
      pointerEvents: "none", // keep the panel itself non-interactive
    }}
  >
    {/* Video embed (Followers: prefer microtrailer, then fallback to video) */}
    {hovered &&
      (g.microtrailer ||
        g.micro_trailer ||
        g.micro_trailer_url ||
        g.video) &&
      (() => {
        const rawUrl =
          g.microtrailer ||
          g.micro_trailer ||
          g.micro_trailer_url ||
          g.video;

        const embed = getVideoEmbed(rawUrl);
        if (!embed) return null;

        return (
          <div
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#000",
              borderRadius: "10px",
              border: "1px solid #333",
              overflow: "hidden",
              marginBottom: "10px",
            }}
          >
            <SteamVideoPreview tags={g.tags} title={g.name}>
              {embed.type === "file" ? (
                <video
                  src={embed.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              ) : (
                <iframe
                  src={embed.src}
                  title={`${g.name} trailer`}
                  frameBorder="0"
                  allow="encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              )}
            </SteamVideoPreview>
          </div>
        );
      })()}

    {/* Game info */}
    {g.short_description && (
      <div style={{ marginBottom: 10, color: "#fff" }}>
        {g.short_description}
      </div>
    )}

    {(g.release_date || formatPrice(g)) && (
      <div style={{ marginBottom: 6, color: "#cfd8e3" }}>
        {g.release_date && (
          <div style={{ marginBottom: formatPrice(g) ? 6 : 0 }}>
            <strong style={{ color: "#fff" }}>Release:</strong>{" "}
            {new Date(g.release_date).toLocaleDateString()}
          </div>
        )}
        {formatPrice(g) && (
          <div>
            <strong style={{ color: "#fff" }}>Price:</strong>{" "}
            {formatPrice(g)}
          </div>
        )}
      </div>
    )}

    {g.publishers && (
      <div style={{ marginBottom: 6, color: "#cfd8e3" }}>
        <strong style={{ color: "#fff" }}>Publisher:</strong>{" "}
        {g.publishers}
      </div>
    )}

    {g.developers && (
      <div style={{ marginBottom: 10, color: "#cfd8e3" }}>
        <strong style={{ color: "#fff" }}>Developer:</strong>{" "}
        {g.developers}
      </div>
    )}

    {Array.isArray(g.tags) && g.tags.length > 0 && (
      <div style={{ color: "#cfd8e3" }}>
        <strong style={{ color: "#fff" }}>Tags:</strong>{" "}
        {g.tags.join(", ")}
      </div>
    )}
  </div>
</div>


                  </div>
                );
              })}
            </div>

            {hasMoreFollowers && (
              <div
                ref={followersSentinelRef}
                style={{ height: "40px", background: "transparent" }}
              />
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
}

export default Steam_debug;
